import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getStoredToken } from '../utils/authApi';

import QRCode from 'qrcode';
import { AuthUser, postLog } from '../utils/authApi';
import { getPermissions } from '../utils/permissions';
import { loadMessageTemplates, applyTemplate } from '../utils/messageTemplates';
import { getCurrencyDisplay } from '../utils/currency';
import { getCountryCurrency } from '../utils/countryCurrency';
// ── Types ──────────────────────────────────────────────────────────────────
interface Tab {
  id: number;
  customer_name: string;
  phone: string;
  email?: string;
  address?: string;
  status: 'open' | 'partially_paid' | 'settled' | 'overdue';
  verified_email: boolean;
  total_charged: number;
  total_paid: number;
  balance: number;
  created_at: string;
  updated_at: string;
  entries?: Entry[];
  payments?: Payment[];
  already_exists?: boolean;
}
interface Entry {
  id: number; date: string; time: string; description: string;
  category: string; qty: number; rate: number; amount: number;
  added_by?: string; note?: string; created_at: string;
}
interface Payment {
  id: number; amount: number; date: string; note?: string; created_at: string;
}

// ── API ────────────────────────────────────────────────────────────────────
const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

function getToken() { return getStoredToken() || ''; }

// Strict continuous cycle math: stacks new duration directly onto previous expiry date
function calculateContinuousEndDate(currentEndDate: string, durationDays: number): { newStartDate: string; newEndDate: string } {
  const base = currentEndDate ? new Date(currentEndDate) : new Date();
  const validBase = isNaN(base.getTime()) ? new Date() : base;

  const newEnd = new Date(validBase);
  newEnd.setDate(newEnd.getDate() + Number(durationDays || 30));

  return {
    newStartDate: validBase.toISOString().split('T')[0],
    newEndDate: newEnd.toISOString().split('T')[0],
  };
}

function cleanErrorText(raw: any): string {
  if (!raw) return 'Something went wrong. Please try again.';

  let s = '';
  if (Array.isArray(raw)) {
    s = raw.map((e: any) => e?.msg || e?.message || (typeof e === 'string' ? e : '')).filter(Boolean).join('. ');
  } else if (typeof raw === 'object') {
    s = raw?.detail || raw?.message || raw?.error || JSON.stringify(raw);
  } else {
    s = String(raw);
  }

  s = s.trim();
  s = s.replace(/^(HTTPException|ValidationError|IntegrityError|ValueError|Exception|Error)[:\s]*\d*[:\s]*/i, '');
  s = s.replace(/^\d{3}[:\s]*/, '');
  s = s.replace(/^(detail|message|error)[:\s]*/i, '');
  s = s.replace(/\bat\s+\S+\.(py|ts|js):\d+/g, '');
  s = s.replace(/\([^)]*(sqlite|postgres|database)[^)]*\)/gi, '');

  if (/already registered/i.test(s)) return 'This face or credential is already registered to an active member.';
  if (/duplicate|already exists|unique constraint/i.test(s)) return 'A member with this phone number or email already exists.';
  if (/invalid otp|otp expired|incorrect otp/i.test(s)) return 'Invalid or expired verification code. Please request a new code.';
  if (/network|offline|failed to fetch|timeout/i.test(s)) return 'Network connection issue. Please check your internet and try again.';
  if (/field required|value_error\.missing/i.test(s)) return 'Please fill in all required fields before continuing.';

  s = s.replace(/\b[a-z]+(?:_[a-z]+)+\b/g, w => w.replace(/_/g, ' '));
  s = s.trim();

  if (!s || /traceback|\{.*"\w+":|<\w+ object|0x[0-9a-f]+/i.test(s)) {
    return 'Something went wrong. Please try again.';
  }

  s = s.charAt(0).toUpperCase() + s.slice(1);
  if (!/[.!?]$/.test(s)) s += '.';
  return s;
}

function friendlyError(status: number, detail?: any): string {
  if (detail) return cleanErrorText(detail);
  switch (status) {
    case 400: return 'That request looks invalid. Please check the details and try again.';
    case 401: return 'Your session has expired. Please log in again.';
    case 403: return "You don't have permission to do that.";
    case 404: return 'Not found — it may have already been deleted.';
    case 409: return 'This already exists or conflicts with existing data.';
    case 422: return 'Some fields are missing or invalid. Please check and try again.';
    case 429: return 'Too many requests — please wait a moment and try again.';
    case 500: case 502: case 503: return 'Something went wrong on our end. Please try again shortly.';
    default: return 'Something went wrong. Please try again.';
  }
}

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers },
    });
  } catch {
    throw new Error('Network error — check your internet connection and try again.');
  }
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(friendlyError(res.status, err.detail)); }
  return res.json();
}

async function sendWhatsApp(phone: string, message: string, outletId?: number | null) {
  const digits = phone.replace(/\D/g, '');
  const fullPhone = digits.length === 10 ? `91${digits}` : digits;
  
  let res: Response;
  try {
    res = await fetch(`${BASE}/whatsapp/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ phone: fullPhone, message, outlet_id: outletId }),
    });
  } catch {
    window.dispatchEvent(new CustomEvent('service-disconnected', {
      detail: { service: 'whatsapp', reason: 'Network error or WhatsApp server unreachable.' }
    }));
    throw new Error('WhatsApp service unreachable.');
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.connected === false || data?.status === 'disconnected' || data?.error === 'not_connected' || data?.success === false) {
    const reason = data?.detail || data?.message || data?.error || 'WhatsApp is not connected to any device.';
    window.dispatchEvent(new CustomEvent('service-disconnected', {
      detail: { service: 'whatsapp', reason }
    }));
    throw new Error(reason);
  }
  return data;
}

async function sendWhatsAppImage(phone: string, base64: string, caption: string, outletId?: number | null) {
  const digits = phone.replace(/\D/g, '');
  const fullPhone = digits.length === 10 ? `91${digits}` : digits;
  const pureBase64 = base64.replace(/^data:image\/\w+;base64,/, '');

  let res: Response;
  try {
    res = await fetch(`${BASE}/whatsapp/send-image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ phone: fullPhone, base64: pureBase64, caption, outlet_id: outletId }),
    });
  } catch {
    window.dispatchEvent(new CustomEvent('service-disconnected', {
      detail: { service: 'whatsapp', reason: 'Network error or WhatsApp server unreachable.' }
    }));
    throw new Error('WhatsApp service unreachable.');
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.connected === false || data?.error === 'not_connected' || data?.success === false) {
    const reason = data?.detail || data?.message || data?.error || 'WhatsApp is not connected to any device.';
    window.dispatchEvent(new CustomEvent('service-disconnected', {
      detail: { service: 'whatsapp', reason }
    }));
    throw new Error(reason);
  }
  return data;
}

async function sendEmail(to: string, subject: string, html: string, outletId?: number | null) {
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  let res: Response;
  try {
    res = await fetch(`${BASE}/email/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ to, subject, html, outlet_id: outletId }),
    });
  } catch {
    window.dispatchEvent(new CustomEvent('service-disconnected', {
      detail: { service: 'email', reason: 'Network error or Email server unreachable.' }
    }));
    throw new Error('Email service unreachable.');
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const reason = data?.detail || data?.message || 'Email service is not configured.';
    window.dispatchEvent(new CustomEvent('service-disconnected', {
      detail: { service: 'email', reason }
    }));
    throw new Error(reason);
  }
}
const COUNTRY_CODES: Record<string, string> = {
  AF:'93',AL:'355',DZ:'213',AS:'1684',AD:'376',AO:'244',AI:'1264',AQ:'672',AG:'1268',AR:'54',
  AM:'374',AW:'297',AU:'61',AT:'43',AZ:'994',BS:'1242',BH:'973',BD:'880',BB:'1246',BY:'375',
  BE:'32',BZ:'501',BJ:'229',BM:'1441',BT:'975',BO:'591',BA:'387',BW:'267',BR:'55',IO:'246',
  VG:'1284',BN:'673',BG:'359',BF:'226',BI:'257',KH:'855',CM:'237',CA:'1',CV:'238',KY:'1345',
  CF:'236',TD:'235',CL:'56',CN:'86',CX:'61',CC:'61',CO:'57',KM:'269',CK:'682',CR:'506',
  HR:'385',CU:'53',CW:'599',CY:'357',CZ:'420',CD:'243',DK:'45',DJ:'253',DM:'1767',DO:'1809',
  EC:'593',EG:'20',SV:'503',GQ:'240',ER:'291',EE:'372',ET:'251',FK:'500',FO:'298',FJ:'679',
  FI:'358',FR:'33',GF:'594',PF:'689',GA:'241',GM:'220',GE:'995',DE:'49',GH:'233',GI:'350',
  GR:'30',GL:'299',GD:'1473',GP:'590',GU:'1671',GT:'502',GG:'44',GN:'224',GW:'245',GY:'592',
  HT:'509',HN:'504',HK:'852',HU:'36',IS:'354',IN:'91',ID:'62',IR:'98',IQ:'964',IE:'353',
  IM:'44',IL:'972',IT:'39',CI:'225',JM:'1876',JP:'81',JE:'44',JO:'962',KZ:'7',KE:'254',
  KI:'686',XK:'383',KW:'965',KG:'996',LA:'856',LV:'371',LB:'961',LS:'266',LR:'231',LY:'218',
  LI:'423',LT:'370',LU:'352',MO:'853',MK:'389',MG:'261',MW:'265',MY:'60',MV:'960',ML:'223',
  MT:'356',MH:'692',MQ:'596',MR:'222',MU:'230',YT:'262',MX:'52',FM:'691',MD:'373',MC:'377',
  MN:'976',ME:'382',MS:'1664',MA:'212',MZ:'258',MM:'95',NA:'264',NR:'674',NP:'977',NL:'31',
  AN:'599',NC:'687',NZ:'64',NI:'505',NE:'227',NG:'234',NU:'683',NF:'672',KP:'850',MP:'1670',
  NO:'47',OM:'968',PK:'92',PW:'680',PS:'970',PA:'507',PG:'675',PY:'595',PE:'51',PH:'63',
  PN:'64',PL:'48',PT:'351',PR:'1787',QA:'974',CG:'242',RE:'262',RO:'40',RU:'7',RW:'250',
  BL:'590',SH:'290',KN:'1869',LC:'1758',MF:'590',PM:'508',VC:'1784',WS:'685',SM:'378',
  ST:'239',SA:'966',SN:'221',RS:'381',SC:'248',SL:'232',SG:'65',SX:'1721',SK:'421',SI:'386',
  SB:'677',SO:'252',ZA:'27',KR:'82',SS:'211',ES:'34',LK:'94',SD:'249',SR:'597',SJ:'47',
  SZ:'268',SE:'46',CH:'41',SY:'963',TW:'886',TJ:'992',TZ:'255',TH:'66',TL:'670',TG:'228',
  TK:'690',TO:'676',TT:'1868',TN:'216',TR:'90',TM:'993',TC:'1649',TV:'688',VI:'1340',
  UG:'256',UA:'380',AE:'971',GB:'44',US:'1',UY:'598',UZ:'998',VU:'678',VA:'39',VE:'58',
  VN:'84',WF:'681',EH:'212',YE:'967',ZM:'260',ZW:'263',
};
// ── Country Code Picker ────────────────────────────────────────────────────
const POPULAR_COUNTRIES = ['IN','AE','US','GB','SA','SG','AU','CA','PK','BD','MY','QA','KW','OM','BH'];

function CountryPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allCountries = Object.keys(COUNTRY_CODES);
  const filtered = search
    ? allCountries.filter(c =>
        c.toLowerCase().includes(search.toLowerCase()) ||
        COUNTRY_CODES[c]?.includes(search.replace('+', ''))
      )
    : [...POPULAR_COUNTRIES, ...allCountries.filter(c => !POPULAR_COUNTRIES.includes(c))];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(!open)} style={{
        display: 'flex', alignItems: 'center', gap: 4, background: '#f8fafc',
        border: '1.5px solid #e2e8f0', borderRadius: '10px 0 0 10px',
        padding: '10px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 700,
        color: '#1e293b', minWidth: 80, whiteSpace: 'nowrap',
      }}>
        +{COUNTRY_CODES[value] || '91'} <span style={{ fontSize: 10, opacity: 0.6 }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 999,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)', width: 220, maxHeight: 280,
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: 8 }}>
            <input autoFocus placeholder="Search country..." value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.map(c => (
              <button key={c} type="button" onClick={() => { onChange(c); setOpen(false); setSearch(''); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  background: c === value ? '#f0f9ff' : 'transparent',
                  border: 'none', padding: '8px 12px', cursor: 'pointer',
                  fontSize: 12, color: '#374151', textAlign: 'left',
                }}>
                <span style={{ fontWeight: 700, color: '#6366f1', minWidth: 36 }}>+{COUNTRY_CODES[c]}</span>
                <span>{c}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Robust Phone Parsing & Clean E.164 ─────────────────────────────────────
function detectCountryCode(adminDefault?: string | null): string {
  if (adminDefault && COUNTRY_CODES[adminDefault]) return adminDefault;
  const userDefault = localStorage.getItem('default_phone_country');
  if (userDefault && COUNTRY_CODES[userDefault]) return userDefault;

  const cached = sessionStorage.getItem('detected_country');
  if (cached && COUNTRY_CODES[cached]) return cached;

  try {
    const langRegion = navigator.language?.split('-')[1]?.toUpperCase();
    if (langRegion && COUNTRY_CODES[langRegion]) {
      sessionStorage.setItem('detected_country', langRegion);
      return langRegion;
    }
  } catch {}

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzToCountry: Record<string, string> = {
    'Asia/Kolkata':'IN','Asia/Calcutta':'IN','Asia/Dubai':'AE','Asia/Muscat':'OM',
    'Asia/Riyadh':'SA','Asia/Kuwait':'KW','Asia/Qatar':'QA','Asia/Bahrain':'BH',
    'Asia/Karachi':'PK','Asia/Dhaka':'BD','Asia/Kathmandu':'NP','Asia/Colombo':'LK',
    'Asia/Singapore':'SG','Asia/Kuala_Lumpur':'MY','Asia/Jakarta':'ID','Asia/Manila':'PH',
    'Asia/Bangkok':'TH','Asia/Ho_Chi_Minh':'VN','Asia/Yangon':'MM','Asia/Phnom_Penh':'KH',
    'Asia/Tokyo':'JP','Asia/Seoul':'KR','Asia/Shanghai':'CN','Asia/Hong_Kong':'HK',
    'Asia/Taipei':'TW','Asia/Macau':'MO','Asia/Ulaanbaatar':'MN',
    'Asia/Kabul':'AF','Asia/Tehran':'IR','Asia/Baghdad':'IQ','Asia/Beirut':'LB',
    'Asia/Amman':'JO','Asia/Jerusalem':'IL','Asia/Damascus':'SY','Asia/Aden':'YE',
    'Asia/Tashkent':'UZ','Asia/Almaty':'KZ','Asia/Baku':'AZ','Asia/Tbilisi':'GE','Asia/Yerevan':'AM',
    'Europe/London':'GB','Europe/Dublin':'IE','Europe/Lisbon':'PT','Europe/Paris':'FR',
    'Europe/Berlin':'DE','Europe/Amsterdam':'NL','Europe/Brussels':'BE','Europe/Madrid':'ES',
    'Europe/Rome':'IT','Europe/Vienna':'AT','Europe/Zurich':'CH','Europe/Stockholm':'SE',
    'Europe/Oslo':'NO','Europe/Copenhagen':'DK','Europe/Helsinki':'FI','Europe/Warsaw':'PL',
    'Europe/Prague':'CZ','Europe/Budapest':'HU','Europe/Bucharest':'RO','Europe/Sofia':'BG',
    'Europe/Athens':'GR','Europe/Istanbul':'TR','Europe/Moscow':'RU','Europe/Kiev':'UA','Europe/Minsk':'BY',
    'America/New_York':'US','America/Chicago':'US','America/Denver':'US','America/Los_Angeles':'US',
    'America/Anchorage':'US','Pacific/Honolulu':'US','America/Toronto':'CA','America/Vancouver':'CA',
    'America/Montreal':'CA','America/Mexico_City':'MX','America/Sao_Paulo':'BR',
    'America/Buenos_Aires':'AR','America/Bogota':'CO','America/Lima':'PE','America/Santiago':'CL',
    'America/Caracas':'VE','America/La_Paz':'BO',
    'Australia/Sydney':'AU','Australia/Melbourne':'AU','Australia/Brisbane':'AU',
    'Australia/Perth':'AU','Pacific/Auckland':'NZ',
    'Africa/Cairo':'EG','Africa/Lagos':'NG','Africa/Nairobi':'KE','Africa/Johannesburg':'ZA',
    'Africa/Accra':'GH','Africa/Casablanca':'MA','Africa/Addis_Ababa':'ET',
    'Africa/Dar_es_Salaam':'TZ','Africa/Kampala':'UG',
  };
  const code = tzToCountry[tz] || 'IN';
  sessionStorage.setItem('detected_country', code);
  return code;
}

function parsePhoneValue(value: string, defaultCountry = 'IN'): { country: string; dialCode: string; nationalNumber: string } {
  const fallbackCountry = defaultCountry && COUNTRY_CODES[defaultCountry] ? defaultCountry : 'IN';
  const fallbackDialCode = (COUNTRY_CODES[fallbackCountry] || '91').replace(/\D/g, '');

  if (!value) return { country: fallbackCountry, dialCode: fallbackDialCode, nationalNumber: '' };

  const raw = String(value).trim();
  const hasLeadingPlus = raw.startsWith('+');
  let cleanDigits = raw.replace(/\D/g, '');

  if (hasLeadingPlus) {
    let bestCountry = fallbackCountry;
    let bestDialCode = fallbackDialCode;
    let maxCodeLen = 0;

    const sortedEntries = Object.entries(COUNTRY_CODES).sort(
      (a, b) => b[1].replace(/\D/g, '').length - a[1].replace(/\D/g, '').length
    );

    for (const [c, code] of sortedEntries) {
      const pureCode = code.replace(/\D/g, '');
      if (cleanDigits.startsWith(pureCode) && pureCode.length > maxCodeLen) {
        bestCountry = c;
        bestDialCode = pureCode;
        maxCodeLen = pureCode.length;
      }
    }

    return {
      country: bestCountry,
      dialCode: bestDialCode,
      nationalNumber: cleanDigits.slice(bestDialCode.length),
    };
  }

  if (cleanDigits.startsWith('0') && fallbackCountry !== 'IT' && cleanDigits.length >= 7) {
    cleanDigits = cleanDigits.replace(/^0/, '');
  }

  if (cleanDigits.startsWith(fallbackDialCode) && cleanDigits.length >= fallbackDialCode.length + 5) {
    return {
      country: fallbackCountry,
      dialCode: fallbackDialCode,
      nationalNumber: cleanDigits.slice(fallbackDialCode.length),
    };
  }

  return {
    country: fallbackCountry,
    dialCode: fallbackDialCode,
    nationalNumber: cleanDigits,
  };
}

export function getCleanE164Phone(phone: string, defaultCountry = 'IN'): string {
  if (!phone) return '';
  const parsed = parsePhoneValue(phone, defaultCountry);
  const national = parsed.nationalNumber.replace(/\D/g, '');
  const dial = parsed.dialCode.replace(/\D/g, '');
  const total = `${dial}${national}`;

  if (national.length < 4 || total.length < 7 || total.length > 15) {
    return '';
  }
  return `+${dial}${national}`;
}

function PhoneInput({ value, onChange, defaultCountry }: { value: string; onChange: (full: string) => void; defaultCountry?: string }) {
  const defaultC = defaultCountry || detectCountryCode();
  const parsed = parsePhoneValue(value, defaultC);

  const [country, setCountry] = useState(() => {
    if (value && value.startsWith('+')) return parsed.country;
    return defaultC;
  });
  const [number, setNumber] = useState(() => parsed.nationalNumber);
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    if (!value) {
      setNumber('');
    } else {
      const p = parsePhoneValue(value, defaultCountry || detectCountryCode());
      setCountry(p.country);
      setNumber(p.nationalNumber);
    }
  }, [value, defaultCountry]);

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const dialCode = COUNTRY_CODES[newCountry] || '91';
    const cleanDigits = number.trim();
    const formatted = cleanDigits ? `+${dialCode}${cleanDigits}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  const handleNumberChange = (raw: string) => {
    let cleanNum = raw.replace(/\D/g, '');
    const dialCode = (COUNTRY_CODES[country] || '91').replace(/\D/g, '');

    // Strip dial code if pasted with '+' or duplicate country prefix
    if (raw.includes('+') && cleanNum.startsWith(dialCode)) {
      cleanNum = cleanNum.slice(dialCode.length);
    } else if (cleanNum.startsWith(dialCode) && cleanNum.length > 10) {
      cleanNum = cleanNum.slice(dialCode.length);
    }

    setNumber(cleanNum);
    const formatted = cleanNum ? `+${dialCode}${cleanNum}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  return (
    <div style={{ display: 'flex' }}>
      <CountryPicker value={country} onChange={handleCountryChange} />
      <input
        type="tel"
        value={number}
        onChange={e => handleNumberChange(e.target.value)}
        placeholder="Phone number"
        style={{
          flex: 1, border: '1.5px solid #e2e8f0', borderLeft: 'none',
          borderRadius: '0 10px 10px 0', padding: '10px 14px',
          fontSize: 13, outline: 'none', background: '#f8fafc',
          fontWeight: 600, color: '#1e293b',
        }}
      />
    </div>
  );
}

// ── Excel Export (Fast on-demand loader) ──────────────────────────────────
async function exportCustomerExcel(tab: Tab) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  const entries = (tab.entries || []).map(e => ({
    Date: e.date, Time: e.time, Description: e.description,
    Category: e.category, Qty: e.qty, Rate: e.rate, Amount: e.amount,
    'Added By': e.added_by || '', Note: e.note || '',
  }));
  const payments = (tab.payments || []).map(p => ({
    Date: p.date, Amount: p.amount, Note: p.note || '',
  }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(entries), 'Charges');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(payments), 'Payments');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{
    Customer: tab.customer_name, Phone: tab.phone, Email: tab.email || '',
    'Total Charged': tab.total_charged, 'Total Paid': tab.total_paid,
    Balance: tab.balance, Status: tab.status,
  }]), 'Summary');
  XLSX.writeFile(wb, `${tab.customer_name.replace(/\s+/g, '_')}_ledger.xlsx`);
}

async function exportAllExcel(tabs: Tab[]) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  const summary = tabs.map(t => ({
    Customer: t.customer_name, Phone: t.phone, Email: t.email || '',
    Address: t.address || '', Status: t.status,
    'Total Charged': t.total_charged, 'Total Paid': t.total_paid,
    Balance: t.balance, 'Last Updated': t.updated_at,
  }));
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), 'All Customers');
  const allEntries = tabs.flatMap(t =>
    (t.entries || []).map(e => ({ Customer: t.customer_name, ...e }))
  );
  if (allEntries.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(allEntries), 'All Charges');
  XLSX.writeFile(wb, `ledger_report_${new Date().toISOString().split('T')[0]}.xlsx`);
}

// ── QR Generator ───────────────────────────────────────────────────────────
async function generateTabQR(tabId: number): Promise<string> {
  const url = `${window.location.origin}/ledger?tab=${tabId}`;
  return QRCode.toDataURL(url, { width: 300, margin: 2, color: { dark: '#1e1b4b', light: '#ffffff' } });
}

// ── Helpers ────────────────────────────────────────────────────────────────
const STATUS = {
  open: { bg: '#fef9ec', text: '#b45309', border: '#fcd34d', label: 'Open', dot: '#f59e0b' },
  partially_paid: { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe', label: 'Partial', dot: '#3b82f6' },
  settled: { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0', label: 'Settled', dot: '#22c55e' },
  overdue: { bg: '#fff1f2', text: '#be123c', border: '#fecdd3', label: 'Overdue', dot: '#f43f5e' },
};

const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const now = () => {
  const d = new Date();
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  return { date, time };
};

// ── OTP Input Component ────────────────────────────────────────────────────
function OTPInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref0 = useRef<HTMLInputElement>(null);
  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);
  const ref3 = useRef<HTMLInputElement>(null);
  const ref4 = useRef<HTMLInputElement>(null);
  const ref5 = useRef<HTMLInputElement>(null);
  const refs = [ref0, ref1, ref2, ref3, ref4, ref5];
  const digits = value.padEnd(6, '').split('').slice(0, 6);

  const handleKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      const next = digits.slice(); next[i] = '';
      onChange(next.join('').trimEnd());
      if (i > 0) refs[i - 1].current?.focus();
    }
  };
  const handleChange = (i: number, v: string) => {
    const char = v.replace(/\D/g, '').slice(-1);
    const next = digits.slice(); next[i] = char;
    onChange(next.join(''));
    if (char && i < 5) refs[i + 1].current?.focus();
  };

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '20px 0', flexShrink: 0, flexWrap: 'nowrap' }}>
      {digits.map((d, i) => (
        <input key={i} ref={refs[i]} value={d} maxLength={1}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKey(i, e)}
          style={{
            width: 40, height: 50, textAlign: 'center', fontSize: 20, fontWeight: 900,
            border: d ? '2px solid #6366f1' : '2px solid #94a3b8',
            borderRadius: 12, outline: 'none', background: d ? '#f5f3ff' : '#fff',
            color: '#1e1b4b', fontFamily: 'monospace', boxSizing: 'border-box',
            boxShadow: d ? '0 0 0 3px #e0e7ff' : 'none', flexShrink: 0,
          }} />
      ))}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────


interface LedgerViewProps {
  authUser: AuthUser | null;
  defaultTab?: 'ledger' | 'gym' | 'canteen';
  moduleStatuses?: Partial<Record<string, string>> | null;
  activeCurrency?: string;
  accounts?: { id: string; name: string; email: string; phone: string; company?: string; address?: string }[];
  onAddAccount?: (account: any) => void;
  onUpdateAccount?: (account: any) => void;
  outletId?: number | null;
}

const LedgerView: React.FC<LedgerViewProps> = ({ authUser, defaultTab = 'ledger', moduleStatuses = null, activeCurrency: activeCurrencyProp, accounts = [], onAddAccount, onUpdateAccount, outletId = null }) => {

// ── Counter Display Integration ──────────────────────────────────────────
  const [registerId] = useState(() => {
    let id = localStorage.getItem('cloudairambo_register_id');
    if (!id) {
      id = 'reg_' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem('cloudairambo_register_id', id);
    }
    return id;
  });
  const [outletDisplayToken, setOutletDisplayToken] = useState('');
  const [companyName, setCompanyName] = useState('');
  useEffect(() => {
    api<any>('/auth/company-profile')
      .then((p: any) => { 
        if (p?.name) setCompanyName(p.name); 
        else if (authUser?.name) setCompanyName(authUser.name);
      })
      .catch(() => {
        if (authUser?.name) setCompanyName(authUser.name);
      });
  }, [authUser]);
  const applyT = (tpl: string, vars: any) => {
    const activeCompany = companyName || authUser?.name || 'Our Company';
    if (!tpl) return '';
    let formatted = applyTemplate(tpl, { company: activeCompany, ...vars });
    return formatted.replace(/CloudAIRambo/gi, activeCompany);
  };

  const pushBillToDisplay = (title: string, amount: number, qrData?: string, payUrl?: string, customSubtitle?: string) => {
    if (!outletDisplayToken) return;
    const moduleLabel = mainTab === 'gym' ? 'Fitness' : mainTab === 'canteen' ? 'Food & Dining' : 'Credit Book';
    const itemDesc = customSubtitle || (mainTab === 'gym' ? 'Gym Membership' : mainTab === 'canteen' ? 'Meal Plan' : 'Credit Balance');

    // On counter display screens, any scannable QR code must be 'upi' mode so the display renders the QR image instead of the card reader warning
    const mode = qrData ? 'upi' : (payUrl ? 'online' : 'cash');

    const payload = {
      register_id: registerId,
      bill_number: `${moduleLabel} — ${title}`,
      customer_name: title,
      grand_total: Number(amount || 0),
      payment_mode: mode,
      bill_id: 0,
      upi_id: orgUpiId || undefined,
      qr_data_url: qrData || undefined,
      qr_code: qrData || undefined,
      qr_image: qrData || undefined,
      qr_image_url: qrData || undefined,
      payment_link: payUrl || undefined,
      upi_url: payUrl || undefined,
      gateway_active: true,
      items: [
        {
          name: `${title} - ${itemDesc}`,
          qty: 1,
          price: Number(amount || 0),
          amount: Number(amount || 0),
        },
      ],
      org_id: (authUser as any)?.org_id || 0,
      currency: billingCurrency,
    };
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BASE}/display/push/${outletDisplayToken}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify(payload),
    }).catch(() => {});
  };

  const clearDisplayBill = () => {
    if (!outletDisplayToken) return;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BASE}/display/push/${outletDisplayToken}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({
        register_id: registerId,
        bill_number: '', customer_name: '', grand_total: 0,
        payment_mode: 'cash', bill_id: 0,
        org_id: (authUser as any)?.org_id || 0, currency: billingCurrency,
      }),
    }).catch(() => {});
  };

  useEffect(() => {
    if (!authUser?.org_id) return;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    fetch(`${BASE}/outlets`, { headers: { Authorization: `Bearer ${jwt}` } })
      .then(r => r.json())
      .then((outlets: any[]) => {
        if (!Array.isArray(outlets)) return;
        const outlet = outlets.find(o => o.id === outletId) || outlets.find(o => o.id === authUser?.outlet_id) || outlets[0];
        setOutletDisplayToken(outlet?.display_token || '');
        if (outlet?.upi_id) setOrgUpiId(outlet.upi_id);
      })
      .catch(() => {});
  }, [authUser?.org_id, authUser?.outlet_id, outletId]);

  const openCounterDisplay = () => {
    if (!outletDisplayToken) {
      toast$('No counter display token configured for this outlet', 'error');
      return;
    }
    window.open(`/display?token=${outletDisplayToken}&register_id=${registerId}`, '_blank');
  };

  const [modulePrice, setModulePrice] = useState<number | null>(null);
const [billingCurrency, setBillingCurrency] = useState(activeCurrencyProp || localStorage.getItem('cloudairambo_active_currency') || 'INR');
const currSym = getCurrencyDisplay(
  billingCurrency,
  (localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol'
);
useEffect(() => {
  if (activeCurrencyProp) setBillingCurrency(activeCurrencyProp);
}, [activeCurrencyProp]);
useEffect(() => {
  fetch(`${BASE}/billing/my-country`)
    .then(r => r.json())
    .then(geo => {
      const country = geo.country || '';
      return fetch(`${BASE}/billing/prices?country=${country}`)
        .then(r => r.json())
        .then((rows: any[]) => {
          const countryRows = rows.filter((r: any) => r.country === country);
const activeCurr = countryRows.length ? countryRows[0].currency : getCountryCurrency(country);

          const row = rows.find(r => r.plan === defaultTab && r.billing_cycle === 'monthly');
          if (row) setModulePrice(row.price);
        });
    })
    .catch(() => {});
}, [defaultTab]);
  const perms = getPermissions(authUser?.role, authUser?.permissions, (authUser as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses as any);

const tabGate = defaultTab === 'gym' ? perms.canAccessFitness
  : defaultTab === 'canteen' ? perms.canAccessFood
  : perms.canAccessLedger;

const isLocked = (
  (defaultTab === 'ledger' && moduleStatuses?.['ledger'] === 'locked')
  || (defaultTab === 'canteen' && moduleStatuses?.['canteen'] === 'locked')
  || (defaultTab === 'gym' && moduleStatuses?.['gym'] === 'locked')
);

if (!tabGate) {
  return (
    <div style={{ 
      textAlign: 'center', 
      padding: '80px', 
      color: '#9ca3af', 
      fontSize: 14, 
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' 
    }}>
      You don't have permission to access this section.
    </div>
  );
}

if (isLocked) {
  return (
    <div style={{ 
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', 
      minHeight: '100vh', 
      background: '#f1f5f9', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center' 
    }}>
      <div style={{ textAlign: 'center', padding: 40, maxWidth: 400 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🔒</div>
        {(() => {
          const n = defaultTab === 'canteen' ? 'Food & Dining' : defaultTab === 'gym' ? 'Fitness' : 'Ledger';
          const desc = defaultTab === 'canteen' ? 'member meals and dining slots' : defaultTab === 'gym' ? 'fitness members and attendance' : 'customer tabs, charges, and payment history';
          return <>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', marginBottom: 8 }}>{n} Access Expired</div>
            <div style={{ fontSize: 14, color: '#64748b', marginBottom: 32, lineHeight: 1.6 }}>Your {n} subscription has ended. Renew to access your {desc}.</div>
            <button onClick={() => window.dispatchEvent(new CustomEvent('show-upgrade-plans'))}
  style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 14, padding: '14px 32px', fontSize: 15, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 14px rgba(99,102,241,0.4)' }}>
  Choose a Plan →
</button>
          </>;
        })()}
      </div>
    </div>
  );
}
    
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<Tab | null>(null);
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState('');
  const [newTabError, setNewTabError] = useState('');
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'info' }>({ msg: '', type: 'success' });

  // Disconnected alert modal state
  const [notConnectedModal, setNotConnectedModal] = useState<{ service: 'whatsapp' | 'email'; reason?: string } | null>(null);

  useEffect(() => {
    const handleDisconnected = (e: any) => {
      setNotConnectedModal(e.detail || { service: 'whatsapp' });
    };
    window.addEventListener('service-disconnected', handleDisconnected);
    return () => window.removeEventListener('service-disconnected', handleDisconnected);
  }, []);
  const [qrData, setQrData] = useState<string | null>(null);
  const [showQR, setShowQR] = useState(false);
  const [sendingPayLink, setSendingPayLink] = useState(false);
  const [sendingMemberPayLink, setSendingMemberPayLink] = useState<number | null>(null);
  const [sendingEntryAction, setSendingEntryAction] = useState<string | null>(null);

  // Exact "Scan to Pay" On-Screen QR Modal (Screenshot Parity)
  const [showPayQrModal, setShowPayQrModal] = useState(false);
  const [payQrMember, setPayQrMember] = useState<any>(null);
  const [payQrTitle, setPayQrTitle] = useState('');
  const [payQrSubtitle, setPayQrSubtitle] = useState('');
  const [payQrAmount, setPayQrAmount] = useState<number>(0);
  const [payQrPhone, setPayQrPhone] = useState('');
  const [payQrDataUrl, setPayQrDataUrl] = useState('');
  const [loadingPayQr, setLoadingPayQr] = useState(false);
  const [payShareUrl, setPayShareUrl] = useState('');
  const [payQrIsUpi, setPayQrIsUpi] = useState(false);
  const [payQrTabId, setPayQrTabId] = useState<number | null>(null);
  const [payQrEmail, setPayQrEmail] = useState('');
  const [payReceived, setPayReceived] = useState(false);
  const [confirmingPaid, setConfirmingPaid] = useState(false);
  const payBaselinePaidRef = useRef(0);
  const payPollRef = useRef<any>(null);
  const renewInFlightRef = useRef(false); // guards against the payment poll and a manual Confirm/Cash tap both calling handleConfirmRenew at once

  // Unified 2-Tab Switcher State (UPI vs Card Gateway)
  const [payQrTab, setPayQrTab] = useState<'upi' | 'card'>('upi');
  const [payQrUpiUrl, setPayQrUpiUrl] = useState('');
  const [payQrCardUrl, setPayQrCardUrl] = useState('');
  const [payQrUpiDataUrl, setPayQrUpiDataUrl] = useState('');
  const [payQrCardDataUrl, setPayQrCardDataUrl] = useState('');

  // Walk-In Renewal State (Gym & Canteen)
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewMember, setRenewMember] = useState<any>(null);
  const [selectedRenewPlan, setSelectedRenewPlan] = useState<any>(null);
  const [renewPaymentMethod, setRenewPaymentMethod] = useState<'cash' | 'upi' | 'card'>('cash');
  const [renewing, setRenewing] = useState(false);

  // Share Catalog to Lead
  const [showShareCatalogModal, setShowShareCatalogModal] = useState(false);
  const [leadPhone, setLeadPhone] = useState('');
  const [leadName, setLeadName] = useState('');
  const [sendingCatalog, setSendingCatalog] = useState(false);
  const [orgUpiId, setOrgUpiId] = useState('');

  useEffect(() => {
    api<any>('/auth/company-profile')
      .then((p: any) => { if (p?.upi_id) setOrgUpiId(p.upi_id); })
      .catch(() => {});
  }, []);

  // New tab form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [creatingTab, setCreatingTab] = useState(false);
  const [pendingOTP, setPendingOTP] = useState<{ tabId: number } | null>(null);
  const [otpInput, setOtpInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);
const [emailExists, setEmailExists] = useState(false);
const [showQuickMsg, setShowQuickMsg] = useState(false);
const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastSelected, setBroadcastSelected] = useState<number[]>([]);
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastDone, setBroadcastDone] = useState(0);
  const [customMsg, setCustomMsg] = useState('');
  // Entry form
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [entryDesc, setEntryDesc] = useState('');
  const [entryQty, setEntryQty] = useState('1');
  const [entryRate, setEntryRate] = useState('');
  const [entryCategory, setEntryCategory] = useState('general');
  const [entryNote, setEntryNote] = useState('');
  const [entryAddedBy, setEntryAddedBy] = useState('');
  const [addingEntry, setAddingEntry] = useState(false);

  // Payment form
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payNote, setPayNote] = useState('');
  const [addingPayment, setAddingPayment] = useState(false);
  // ── Subscription State ─────────────────────────────────────
const [mainTab, setMainTab] = useState<'ledger' | 'gym' | 'canteen'>(defaultTab);
const [subMembers, setSubMembers] = useState<any[]>([]);
const [subLoading, setSubLoading] = useState(false);
const [todayBoard, setTodayBoard] = useState<any[]>([]);
const [showNewMember, setShowNewMember] = useState(false);
const [memberName, setMemberName] = useState('');
const [memberPhone, setMemberPhone] = useState('');
const [memberEmail, setMemberEmail] = useState('');
const [memberPlan, setMemberPlan] = useState('Monthly');
const [memberSlots, setMemberSlots] = useState<string[]>([]);
const [memberStart, setMemberStart] = useState(() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
});
const [memberEnd, setMemberEnd] = useState('');
const [creatingMember, setCreatingMember] = useState(false);
const [memberQR, setMemberQR] = useState<{url: string; name: string; member?: any} | null>(null);
const [showScanner, setShowScanner] = useState(false);
const [scanSlot, setScanSlot] = useState('');
const [scanResult, setScanResult] = useState<any>(null);
const videoRef = useRef<HTMLVideoElement>(null);
const streamRef = useRef<MediaStream | null>(null);
const scanInterval = useRef<any>(null);
const [autoReportMembers, setAutoReportMembers] = useState<Set<number>>(new Set());
const GYM_SLOTS = ['Gym'];
const CANTEEN_SLOTS = ['Breakfast', 'Lunch', 'Dinner'];
const [editMember, setEditMember] = useState<any | null>(null);
const [editName, setEditName] = useState('');
const [editPhone, setEditPhone] = useState('');
const [editEmail, setEditEmail] = useState('');
const [editPlan, setEditPlan] = useState('');
const [editSlots, setEditSlots] = useState<string[]>([]);
const [editStart, setEditStart] = useState('');
const [editEnd, setEditEnd] = useState('');
const [savingEdit, setSavingEdit] = useState(false);
const [editPhoneOtp, setEditPhoneOtp] = useState('');
const [showEditPhoneOtp, setShowEditPhoneOtp] = useState(false);
const [pendingEditData, setPendingEditData] = useState<any>(null);
const [sendingEditOtp, setSendingEditOtp] = useState(false);
const [verifyingEditOtp, setVerifyingEditOtp] = useState(false);
const [otpMember, setOtpMember] = useState<any | null>(null);
const [otpValue, setOtpValue] = useState('');
const [sendingOtp, setSendingOtp] = useState(false);
const [showFaceRegister, setShowFaceRegister] = useState(false);
const [faceRegisterMember, setFaceRegisterMember] = useState<any>(null);
const [showFaceScan, setShowFaceScan] = useState(false);
const [faceScanSlot, setFaceScanSlot] = useState('');
const [faceRegisterStatus, setFaceRegisterStatus] = useState<'idle'|'loading'|'capturing'|'done'|'error'>('idle');
const [faceScanStatus, setFaceScanStatus] = useState<'scanning'|'match'|'nomatch'|'error'|'method_disabled'|'already_scanned'|'expired'>('scanning');
const [faceScanResult, setFaceScanResult] = useState<any>(null);
const faceVideoRef = useRef<HTMLVideoElement>(null);
const faceStreamRef = useRef<MediaStream | null>(null);
const faceIntervalRef = useRef<any>(null);
const faceScanCancelledRef = useRef(false);
const [preOtpPhone, setPreOtpPhone] = useState('');
const [preOtpValue, setPreOtpValue] = useState('');
const [showPreOtp, setShowPreOtp] = useState(false);
const [sendingPreOtp, setSendingPreOtp] = useState(false);
const [memberPhoneExists, setMemberPhoneExists] = useState(false);
const [memberEmailExists, setMemberEmailExists] = useState(false);
const [showMemberHistory, setShowMemberHistory] = useState(false);
const [historyMember, setHistoryMember] = useState<any>(null);
const [historyLogs, setHistoryLogs] = useState<any[]>([]);
const [historyLoading, setHistoryLoading] = useState(false);
const [showBiometricSetup, setShowBiometricSetup] = useState(false);
const [biometricMember, setBiometricMember] = useState<any>(null);
const [fingerprintStatus, setFingerprintStatus] = useState<'idle'|'registering'|'done'|'error'|'unsupported'>('idle');
const [phoneEnrollQR, setPhoneEnrollQR] = useState('');
const [waitingPhoneBio, setWaitingPhoneBio] = useState(false);
const phoneBioPollRef = useRef<any>(null);

// ── 📱 Remote Dedicated Phone Punch States (Gym & Food) ────────────────────
const [showRemotePunchModal, setShowRemotePunchModal] = useState(false);
const [remotePunchQR, setRemotePunchQR] = useState('');
const remotePunchPollRef = useRef<any>(null);
const [faceOtpMember, setFaceOtpMember] = useState<any>(null);
const [faceOtpInput, setFaceOtpInput] = useState('');
const [faceOtpLoading, setFaceOtpLoading] = useState(false);
const [fingerprintScanStatus, setFingerprintScanStatus] = useState<'idle'|'scanning'>('idle');
const [faceSetupStatus, setFaceSetupStatus] = useState<'idle'|'loading'|'capturing'|'done'|'error'>('idle');
const faceSetupVideoRef = useRef<HTMLVideoElement>(null);
const faceSetupStreamRef = useRef<MediaStream | null>(null);
const [verifyingPreOtp, setVerifyingPreOtp] = useState(false);
const [pendingMemberData, setPendingMemberData] = useState<any>(null);
const [verifyingOtp, setVerifyingOtp] = useState(false);
const [membershipPlans, setMembershipPlans] = useState<any[]>([]);
const [orgSlots, setOrgSlots] = useState<any[]>([]);
const [newSlotName, setNewSlotName] = useState('');
const [addingSlot, setAddingSlot] = useState(false);
const [showStatusDash, setShowStatusDash] = useState(false);
const [showTodayAttendance, setShowTodayAttendance] = useState(false);
const [sendingReminders, setSendingReminders] = useState(false);
const [remindersDone, setRemindersDone] = useState(0);
const [showPlansManager, setShowPlansManager] = useState(false);
const [planName, setPlanName] = useState('');
const [planPrice, setPlanPrice] = useState('');
const [planDays, setPlanDays] = useState('30');
const [planSlots, setPlanSlots] = useState<string[]>([]);
const [savingPlan, setSavingPlan] = useState(false);
const [planDescription, setPlanDescription] = useState('');
const [deviceApiKey, setDeviceApiKey] = useState('');
const [showKioskSetup, setShowKioskSetup] = useState(false);
const [editTab, setEditTab] = useState<Tab | null>(null);
const [editTabName, setEditTabName] = useState('');
const [editTabPhone, setEditTabPhone] = useState('');
const [editTabEmail, setEditTabEmail] = useState('');
const [editTabAddress, setEditTabAddress] = useState('');
const [savingTabEdit, setSavingTabEdit] = useState(false);
const [showTabPhoneOtp, setShowTabPhoneOtp] = useState(false);
const [tabPhoneOtp, setTabPhoneOtp] = useState('');
const [sendingTabPhoneOtp, setSendingTabPhoneOtp] = useState(false);
const [verifyingTabPhoneOtp, setVerifyingTabPhoneOtp] = useState(false);
const [pendingTabEditData, setPendingTabEditData] = useState<any>(null);
const currentModule = mainTab === 'gym' ? 'gym' : 'canteen';
const currentSlots = orgSlots.length > 0 ? orgSlots.map(s => s.name) : (mainTab === 'gym' ? GYM_SLOTS : CANTEEN_SLOTS);
const isOwnerOrAdmin = authUser?.role === 'owner' || authUser?.role === 'admin';
const [showModuleSettings, setShowModuleSettings] = useState(false);
const [moduleSettings, setModuleSettings] = useState<any>(null);
const [moduleSettingsLoading, setModuleSettingsLoading] = useState(false);

const fetchModuleSettings = async (module: string) => {
  setModuleSettingsLoading(true);
  try {
    const data = await api<any>(`/subscriptions/module-settings?module=${module}`);
    setModuleSettings(data);
  } catch (e: any) {
    toast$(e.message || 'Failed to load settings', 'error');
  } finally {
    setModuleSettingsLoading(false);
  }
};

const saveModuleSettings = async (module: string, updated: any) => {
  setModuleSettingsLoading(true);
  try {
    const data = await api<any>('/subscriptions/module-settings', {
      method: 'PATCH',
      body: JSON.stringify({ module, ...updated }),
    });
    setModuleSettings(data);
    postLog(module === 'gym' ? 'GYM_MODULE_SETTINGS_UPDATED' : 'CANTEEN_MODULE_SETTINGS_UPDATED', `Scan settings updated for ${module}`, outletId);
    toast$('Scan settings saved!');
  } catch (e: any) {
    toast$(e.message || 'Failed to save settings', 'error');
  } finally {
    setModuleSettingsLoading(false);
  }
};
  const toast$ = (msg: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3500);
  };

  // ⚠️ Not Connected Alert Modal Renderer
  const renderNotConnectedModal = () => {
    if (!notConnectedModal) return null;
    const isWA = notConnectedModal.service === 'whatsapp';
    return (
      <div
        style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)',
          backdropFilter: 'blur(5px)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
        onClick={() => setNotConnectedModal(null)}
      >
        <div
          style={{
            background: '#ffffff', borderRadius: 24, padding: '28px 24px',
            width: '100%', maxWidth: 400, textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <div
            style={{
              width: 60, height: 60, borderRadius: '50%',
              background: isWA ? '#fef2f2' : '#eff6ff',
              border: `2px solid ${isWA ? '#fecaca' : '#bfdbfe'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, margin: '0 auto 16px',
            }}
          >
            {isWA ? '📱' : '✉️'}
          </div>

          <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', marginBottom: 6 }}>
            {isWA ? 'WhatsApp is Not Connected' : 'Email Not Configured'}
          </div>

          <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, marginBottom: 20 }}>
            {isWA
              ? 'Your WhatsApp instance is disconnected or logged out. Please link your phone via QR code in Settings before sending messages.'
              : 'Outgoing email delivery has not been configured for this organization.'}
            {notConnectedModal.reason && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#dc2626', fontWeight: 600 }}>
                Details: {notConnectedModal.reason}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {isWA && (
              <button
                type="button"
                onClick={() => {
                  setNotConnectedModal(null);
                  window.dispatchEvent(new CustomEvent('open-whatsapp-settings'));
                }}
                style={{
                  width: '100%', background: '#25D366', color: '#fff', border: 'none',
                  borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 800,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  boxShadow: '0 4px 14px rgba(37,211,102,0.3)',
                }}
              >
                📲 Connect WhatsApp Now
              </button>
            )}

            <button
              type="button"
              onClick={() => setNotConnectedModal(null)}
              style={{
                width: '100%', background: '#f1f5f9', color: '#475569', border: 'none',
                borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  };

  const [ledgerPlanInfo, setLedgerPlanInfo] = useState<{ limit: number; count: number; plan: string } | null>(null);
  const [ledgerCategories, setLedgerCategories] = useState<any[]>([]);
  const [showCatManager, setShowCatManager] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatIcon, setNewCatIcon] = useState('📦');
  const [addingCat, setAddingCat] = useState(false);
  const [showStatusDashLedger, setShowStatusDashLedger] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [editAlertThreshold, setEditAlertThreshold] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [recurringList, setRecurringList] = useState<any[]>([]);
  const [recDesc, setRecDesc] = useState('');
  const [recAmount, setRecAmount] = useState('');
  const [recCategory, setRecCategory] = useState('general');
  const [recDay, setRecDay] = useState('1');
  const [addingRec, setAddingRec] = useState(false);
  const [payMethod, setPayMethod] = useState('cash');
  const [sendingBulkReminders, setSendingBulkReminders] = useState(false);
  const [bulkRemindersDone, setBulkRemindersDone] = useState(0);
  const CATEGORIES = ledgerCategories.length > 0 ? ledgerCategories.map(c => c.name.toLowerCase()) : ['general', 'food', 'room', 'shop', 'service', 'transport', 'other'];
  const CAT_ICON: Record<string, string> = ledgerCategories.length > 0
    ? Object.fromEntries(ledgerCategories.map(c => [c.name.toLowerCase(), c.icon]))
    : { general: '📦', food: '🍽️', room: '🛏️', shop: '🛒', service: '⚙️', transport: '🚗', other: '📌' };
const loadCategories = async () => {
    try {
      const data = await api<any[]>('/ledger/categories');
      setLedgerCategories(data);
    } catch { }
  };

  const loadRecurring = async (tabId: number) => {
  try {
    const data = await api<any[]>(`/ledger/tabs/${tabId}/recurring?outlet_id=${outletId || ''}`);
    setRecurringList(data);
  } catch { }
};
  const [listLoadError, setListLoadError] = useState('');
  const [validLedgerOutletIds, setValidLedgerOutletIds] = useState<number[] | null>(null);
  const tabsRef = useRef<Tab[]>([]);
const dismissedListErrorRef = useRef<string>('');
  useEffect(() => {
    if (mainTab !== 'ledger') return;
    api<{ id: number }[]>('/outlets?module=ledger')
      .then(list => setValidLedgerOutletIds(list.map(o => o.id)))
      .catch(() => setValidLedgerOutletIds(null));
  }, [mainTab]);

  const loadTabs = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const qs = outletId ? `?outlet_id=${outletId}` : '';
      const data = await api<Tab[]>(`/ledger/tabs${qs}`);
      // Skip the state update entirely if nothing actually changed —
      // prevents the whole list re-rendering/flashing on every 8s poll.
      const changed = data.length !== tabsRef.current.length ||
        data.some((t, i) => t.id !== tabsRef.current[i]?.id || t.updated_at !== tabsRef.current[i]?.updated_at || t.balance !== tabsRef.current[i]?.balance);
      if (changed) {
        tabsRef.current = data;
        setTabs(data);
      }
      setListLoadError('');
      dismissedListErrorRef.current = '';
      const activeCount = data.filter(t => t.status !== 'settled').length;
      const ledgerModuleStatus = moduleStatuses?.['ledger'];
      const ledgerLimit = ledgerModuleStatus === 'active' || ledgerModuleStatus === 'trial' ? 999999 : 20;
      setLedgerPlanInfo({ plan: 'module', limit: ledgerLimit, count: activeCount });
    }
    catch (e: any) { if (!silent && e.message !== dismissedListErrorRef.current) setListLoadError(e.message); }
    finally { if (!silent) setLoading(false); }
  }, [authUser, outletId]);

  useEffect(() => {
  // Skip firing the tabs fetch while outletId is a leftover from another
  // module's outlet (e.g. switching tabs from POS) — avoids 403 spam
  // during the brief window before Navbar re-announces the right outlet.
  const outletInvalid = outletId != null && validLedgerOutletIds != null && !validLedgerOutletIds.includes(outletId);
  if (outletInvalid) return;

  loadTabs(true);
  loadCategories();

  // Silent background refresh every 8s — no loading spinner, no flicker
  const interval = setInterval(() => {
    if (document.visibilityState === 'visible') loadTabs(true);
  }, 8000);

  // Refresh instantly when the tab/window regains focus (also silent)
  const onFocus = () => loadTabs(true);
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onFocus);

  return () => {
    clearInterval(interval);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onFocus);
  };
}, [loadTabs, validLedgerOutletIds, outletId]);

  const loadTabDetail = async (tabId: number) => {
  try { setActiveTab(await api<Tab>(`/ledger/tabs/${tabId}?outlet_id=${outletId || ''}`)); setView('detail'); }
  catch (e: any) { setError(e.message); }
};





  const handleCreateTab = async () => {
    if (!newName.trim() || !newPhone.trim()) return;
    const cleanPhone = getCleanE164Phone(newPhone);
    if (!cleanPhone) {
      setNewTabError('Please enter a valid phone number (7 to 15 digits).');
      return;
    }
    setCreatingTab(true); setNewTabError('');
    try {
      // Check duplicate from local state before anything
      const duplicate = tabs.find(t =>
        t.status !== 'settled' && (
          t.phone.replace(/\D/g, '') === cleanPhone.replace(/\D/g, '') ||
          (newEmail.trim() && t.email?.toLowerCase() === newEmail.trim().toLowerCase())
        )
      );
      if (duplicate) {
        const reason = duplicate.phone.replace(/\D/g, '') === cleanPhone.replace(/\D/g, '') ? 'phone number' : 'email address';
        toast$(`⚠️ Tab already exists for this ${reason} (${duplicate.customer_name})`, 'info');
        setShowNewForm(false); resetNewForm(); loadTabs(true); loadTabDetail(duplicate.id);
        return;
      }
      if (!newEmail.trim()) {
        const data = await api<Tab & { already_exists?: boolean }>('/ledger/tabs', {
          method: 'POST',
          body: JSON.stringify({ customer_name: newName, phone: cleanPhone, address: newAddress || undefined, outlet_id: outletId || undefined }),
        });
        if (data.already_exists) {
          toast$(`⚠️ Tab already exists for ${data.customer_name}`, 'info');
          setShowNewForm(false); resetNewForm(); loadTabs(true); loadTabDetail(data.id); return;
        }
        toast$('Tab created successfully!');
        postLog('LEDGER_TAB_CREATED', `New ledger tab created: ${newName} | ${cleanPhone}`, outletId);
        syncTabToAccounts(newName, cleanPhone, newEmail, newAddress);
        setShowNewForm(false); resetNewForm(); loadTabs(true); loadTabDetail(data.id);
      } else {
        const res = await api<any>('/subscriptions/members/send-precreate-otp', {
          method: 'POST',
          body: JSON.stringify({ phone: cleanPhone, name: newName }),
        });
        await sendWhatsApp(cleanPhone, `🔐 Your verification OTP for ${newName}'s account is: *${res.otp}*\nValid for 10 minutes.\n\n— ${companyName} Ledger`, outletId);
        await sendEmail(newEmail, 'Your Verification OTP', `<div style="font-family:sans-serif;max-width:400px;margin:auto;padding:32px;background:#f8f9fb;border-radius:16px"><h2 style="color:#1e1b4b">Verification OTP</h2><div style="font-size:36px;font-weight:900;letter-spacing:12px;color:#6366f1;background:#fff;padding:20px;border-radius:12px;text-align:center">${res.otp}</div><p style="color:#6b7280;margin-top:16px">Valid for 10 minutes. Do not share this with anyone.</p></div>`, outletId);
        setPendingOTP({ tabId: -1 });
        setOtpInput('');
      }
    } catch (e: any) { setError(e.message); }
    finally { setCreatingTab(false); }
  };



useEffect(() => {
  const digits = memberPhone.replace(/\D/g, '');
  if (digits.length < 7) { setMemberPhoneExists(false); return; }
  setMemberPhoneExists(!!subMembers.find(m =>
    m.phone.replace(/\D/g, '').slice(-10) === digits.slice(-10)
  ));
}, [memberPhone, subMembers]);

// Autofill member name/email from a saved client account when the phone matches
useEffect(() => {
  const digits = memberPhone.replace(/\D/g, '');
  if (digits.length < 7) return;
  const match = accounts.find(a => a.phone?.replace(/\D/g, '').slice(-10) === digits.slice(-10));
  if (match) {
    if (!memberName.trim()) setMemberName(match.name || '');
    if (!memberEmail.trim() && match.email) setMemberEmail(match.email);
  }
}, [memberPhone, accounts]);

useEffect(() => {
  if (!memberEmail.trim() || !memberEmail.includes('@')) { setMemberEmailExists(false); return; }
  setMemberEmailExists(!!subMembers.find(m =>
    m.email?.toLowerCase() === memberEmail.trim().toLowerCase()
  ));
}, [memberEmail, subMembers]);

  const resetNewForm = () => { setNewName(''); setNewPhone(''); setNewEmail(''); setNewAddress(''); setPendingOTP(null); setOtpInput(''); };

// Save/update customer in both local state and backend /clients/ directory (App Parity)
const syncTabToAccounts = async (name: string, phone: string, email?: string, address?: string) => {
  const digits = phone.replace(/\D/g, '').slice(-10);
  const existing = accounts.find(a => a.phone?.replace(/\D/g, '').slice(-10) === digits);

  // 1. Immediate UI state update via parent props
  if (existing) {
    if (onUpdateAccount) {
      onUpdateAccount({
        ...existing,
        name: existing.name || name,
        email: existing.email || email || '',
        address: existing.address || address || '',
      });
    }
  } else if (onAddAccount) {
    onAddAccount({
      name, email: email || '', phone, address: address || '',
      visitCount: 0, totalSpend: 0,
    });
  }

  // 2. Direct backend REST persistence
  try {
    if (existing?.id) {
      await api(`/clients/${existing.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...existing,
          name: existing.name || name,
          email: existing.email || email || '',
          address: existing.address || address || '',
        }),
      });
    } else {
      await api('/clients/', {
        method: 'POST',
        body: JSON.stringify({
          name,
          phone,
          email: email || '',
          address: address || '',
        }),
      });
    }
  } catch (e) {
    console.warn('[syncTabToAccounts REST sync failed]', e);
  }
};
useEffect(() => {
  const digits = newPhone.replace(/\D/g, '');
  if (digits.length < 7) { setPhoneExists(false); return; }
  setPhoneExists(!!tabs.find(t =>
    t.status !== 'settled' &&
    t.phone.replace(/\D/g, '').slice(-10) === digits.slice(-10)
  ));
}, [newPhone, tabs]);

// Autofill name/email/address from a saved client account when the phone matches
useEffect(() => {
  const digits = newPhone.replace(/\D/g, '');
  if (digits.length < 7) return;
  const match = accounts.find(a => a.phone?.replace(/\D/g, '').slice(-10) === digits.slice(-10));
  if (match) {
    if (!newName.trim()) setNewName(match.name || '');
    if (!newEmail.trim() && match.email) setNewEmail(match.email);
    if (!newAddress.trim() && match.address) setNewAddress(match.address);
  }
}, [newPhone, accounts]);

useEffect(() => {
  if (!newEmail.trim() || !newEmail.includes('@')) { setEmailExists(false); return; }
  setEmailExists(!!tabs.find(t =>
    t.status !== 'settled' &&
    t.email?.toLowerCase() === newEmail.trim().toLowerCase()
  ));
}, [newEmail, tabs]);
  const handleVerifyOTP = async () => {
    if (!pendingOTP) return;
    const cleanPhone = getCleanE164Phone(newPhone) || newPhone;
    setVerifying(true); setError('');
    try {
      await api('/subscriptions/members/verify-precreate-otp', {
        method: 'POST',
        body: JSON.stringify({ phone: cleanPhone, otp: otpInput }),
      });
      setCreatingTab(true);
      const data = await api<Tab & { already_exists?: boolean }>('/ledger/tabs', {
        method: 'POST',
        body: JSON.stringify({ customer_name: newName, phone: cleanPhone, email: newEmail || undefined, address: newAddress || undefined, outlet_id: outletId || undefined }),
      });
      if (data.already_exists) {
        const reason = (data as any).dupe_reason === 'email' ? 'email address' : 'phone number';
        toast$(`⚠️ Tab already exists for this ${reason} (${data.customer_name})`, 'info');
        setPendingOTP(null); setShowNewForm(false); resetNewForm(); loadTabs(true); loadTabDetail(data.id); return;
      }
      toast$('Tab created & verified! ✓');
      postLog('LEDGER_TAB_CREATED', `New ledger tab created: ${newName} | ${cleanPhone} | ${newEmail}`, outletId);
      syncTabToAccounts(newName, cleanPhone, newEmail, newAddress);
      setPendingOTP(null); setShowNewForm(false); resetNewForm();
      loadTabs(true); loadTabDetail(data.id);
    } catch (e: any) { setError(e.message); }
    finally { setVerifying(false); setCreatingTab(false); }
  };

const handleShareExcelWhatsApp = async (tab: Tab) => {
  try {
    exportCustomerExcel(tab);
    toast$('Excel downloaded! 📊');
  } catch (e: any) {
    toast$(e.message, 'error');
  }
};

  const handleSendPaymentWA = async (pmt: Payment) => {
    if (!activeTab?.phone) return;
    const msg = `💚 *Payment Receipt*\n\nHi *${activeTab.customer_name}*,\nThank you! We received your payment:\n\n• *Amount Paid:* ${currSym}${fmt(pmt.amount)}\n• *Date:* ${pmt.date}${pmt.note ? `\n• *Note:* ${pmt.note}` : ''}${(pmt as any).method ? `\n• *Method:* ${(pmt as any).method.toUpperCase()}` : ''}\n\n📊 *Remaining Balance:* *${currSym}${fmt(activeTab.balance)}*\n\n— ${companyName || authUser?.name || 'Credit Book'}`;
    try {
      await sendWhatsApp(activeTab.phone, msg, outletId);
      toast$('Payment receipt sent via WhatsApp! 📲');
    } catch (err: any) {
      toast$(err.message || 'WhatsApp send failed', 'error');
    }
  };
  const handleAddEntry = async () => {
    if (!activeTab || !entryDesc.trim() || !entryRate) return;
    setAddingEntry(true);
    try {
      const { date, time } = now();
      const result = await api<{ tab_balance: number } & Entry>(`/ledger/tabs/${activeTab.id}/entries?outlet_id=${outletId || ''}`, {
  method: 'POST',
  body: JSON.stringify({
    description: entryDesc, qty: parseFloat(entryQty) || 1,
    rate: parseFloat(entryRate) || 0, category: entryCategory,
    note: entryNote || undefined, added_by: entryAddedBy || undefined,
    date, time,
  }),
});
      toast$(`Charge added — Balance: ${currSym}${fmt(result.tab_balance)}`);
      postLog('LEDGER_CHARGE_ADDED', `Charge added: ${entryDesc} | ${currSym}${fmt(parseFloat(entryRate) * parseFloat(entryQty))} | ${activeTab.customer_name}`, outletId);
      setShowEntryForm(false);
      setEntryDesc(''); setEntryQty('1'); setEntryRate(''); setEntryNote(''); setEntryAddedBy('');
      const amount = fmt(parseFloat(entryRate) * parseFloat(entryQty));
      const templates = await loadMessageTemplates(outletId);
      const msg = applyT(templates.whatsapp_message, {
        name: activeTab.customer_name,
        invoice: entryDesc,
        amount: result.tab_balance,
        currency: currSym,
        date: `${date} ${time}`,
      });
      try {
        await sendWhatsApp(activeTab.phone, msg, outletId);
      } catch (e: any) {
        alert(`Charge saved, but WhatsApp notification failed: ${e.message}`);
      }
      if (activeTab.email) {
        try {
          const subject = applyT(templates.email_subject, { name: activeTab.customer_name, invoice: entryDesc, amount: result.tab_balance, currency: currSym, date: date });
          const bodyText = applyT(templates.email_body, { name: activeTab.customer_name, invoice: entryDesc, amount: result.tab_balance, currency: currSym, date: date });
          const html = `<div style="font-family:sans-serif;max-width:500px;margin:auto;padding:32px">${bodyText.split('\n').map(l => `<p style="margin:0 0 8px">${l}</p>`).join('')}<table style="width:100%;border-collapse:collapse;margin-top:16px"><tr><td style="padding:8px;color:#6b7280">Description</td><td style="padding:8px;font-weight:700">${entryDesc}</td></tr><tr style="background:#f8f9fb"><td style="padding:8px;color:#6b7280">Amount</td><td style="padding:8px;font-weight:700;color:#dc2626">${currSym}${amount}</td></tr><tr><td style="padding:8px;color:#6b7280">Balance</td><td style="padding:8px;font-weight:900;color:#1e1b4b">${currSym}${fmt(result.tab_balance)}</td></tr></table></div>`;
          await sendEmail(activeTab.email, subject, html, outletId);
        } catch (e: any) {
          alert(`Charge saved, but email notification failed: ${e.message}`);
        }
      }
      loadTabDetail(activeTab.id); loadTabs(true);
      // Balance alert check
      const threshold = (activeTab as any).balance_alert_threshold;
      if (threshold && threshold > 0 && result.tab_balance >= threshold) {
        try {
          await sendWhatsApp(authUser?.phone || '', `⚠️ *Balance Alert*\n\nCustomer *${activeTab.customer_name}* has exceeded the alert threshold!\n\nCurrent Balance: ${currSym}${fmt(result.tab_balance)}\nThreshold: ${currSym}${fmt(threshold)}\nPhone: ${activeTab.phone}\n\n— ${companyName} Ledger`);
        } catch (e: any) {
          alert(`Balance alert WhatsApp failed: ${e.message}`);
        }
      }
    } catch (e: any) { setError(e.message); }
    finally { setAddingEntry(false); }
  };

  const handleAddPayment = async () => {
    if (!activeTab || !payAmount) return;
    setAddingPayment(true);
    try {
      const { date, time } = now();
      const result = await api<{ tab_balance: number; tab_status: string } & Payment>(`/ledger/tabs/${activeTab.id}/payments?outlet_id=${outletId || ''}`, {
  method: 'POST',
  body: JSON.stringify({ amount: parseFloat(payAmount), note: payNote || undefined, date, method: payMethod }),
});
      toast$(`Payment of ${currSym}${fmt(result.amount)} recorded!`);
      postLog('LEDGER_PAYMENT_ADDED', `Payment recorded: ${currSym}${fmt(result.amount)} | ${activeTab.customer_name}${payNote ? ` | ${payNote}` : ''}`, outletId);
      setShowPaymentForm(false); setPayAmount(''); setPayNote(''); setPayMethod('cash');
      const templates = await loadMessageTemplates(outletId);
      const msg = applyT(templates.whatsapp_message, {
        name: activeTab.customer_name,
        invoice: '',
        amount: result.amount,
        currency: currSym,
        date: `${date} ${time}`,
      });
      try {
        await sendWhatsApp(activeTab.phone, msg, outletId);
      } catch (e: any) {
        alert(`Payment saved, but WhatsApp notification failed: ${e.message}`);
      }
      if (activeTab.email) {
        try {
          const subject = applyT(templates.email_subject, { name: activeTab.customer_name, invoice: '', amount: result.amount, currency: currSym, date: date });
          const bodyText = applyT(templates.email_body, { name: activeTab.customer_name, invoice: '', amount: result.amount, currency: currSym, date: date });
          const html = `<div style="font-family:sans-serif;max-width:500px;margin:auto;padding:32px">${bodyText.split('\n').map(l => `<p style="margin:0 0 8px">${l}</p>`).join('')}<p style="margin-top:12px;color:#6b7280;font-size:12px">Remaining balance: <strong style="color:#1e1b4b">${currSym}${fmt(result.tab_balance)}</strong></p></div>`;
          await sendEmail(activeTab.email, subject, html, outletId);
        } catch (e: any) {
          alert(`Payment saved, but email notification failed: ${e.message}`);
        }
      }
      loadTabDetail(activeTab.id); loadTabs(true);
    } catch (e: any) { setError(e.message); }
    finally { setAddingPayment(false); }
  };

  const handleSettle = async () => {
  if (!activeTab || !confirm('Mark this tab as fully settled?')) return;
  try {
    await api(`/ledger/tabs/${activeTab.id}/settle?outlet_id=${outletId || ''}`, { method: 'PATCH' });
      toast$('Tab settled! 🎉');
      postLog('LEDGER_TAB_SETTLED', `Tab settled: ${activeTab.customer_name} | Total charged: ${currSym}${fmt(activeTab.total_charged)}`, outletId);
      try {
        await sendWhatsApp(activeTab.phone, `🎉 *Tab Settled!*\n\nHi ${activeTab.customer_name}, your account has been fully settled. Thank you for your payment!\n\n— ${companyName} Ledger`, outletId);
      } catch (e: any) {
        alert(`Tab settled, but WhatsApp notification failed: ${e.message}`);
      }
      loadTabDetail(activeTab.id); loadTabs(true);
    } catch (e: any) { setError(e.message); }
  };

  // Component-level FIFO calculation so all reminder & entry handlers have access
  const settledEntryIds = React.useMemo(() => {
    if (!activeTab?.entries?.length) return new Set<number>();
    let remainingPaid = activeTab.total_paid || 0;
    const settled = new Set<number>();
    const chronological = [...activeTab.entries].sort((a, b) => {
      const d = (a.date || '').localeCompare(b.date || '');
      if (d !== 0) return d;
      return (a.time || '').localeCompare(b.time || '');
    });
    for (const e of chronological) {
      const amt = Number(e.amount) || (Number(e.rate) * Number(e.qty)) || 0;
      if (remainingPaid >= amt && amt > 0) {
        settled.add(e.id);
        remainingPaid -= amt;
      }
    }
    return settled;
  }, [activeTab?.entries, activeTab?.total_paid]);

  const handleDeleteEntry = async (entryId: number) => {
    if (!activeTab) return;
    if (settledEntryIds.has(entryId)) {
      toast$('This charge is already paid and cannot be deleted.', 'error');
      return;
    }
    if (!confirm('Delete this entry?')) return;
    try {
      await api(`/ledger/tabs/${activeTab.id}/entries/${entryId}?outlet_id=${outletId || ''}`, { method: 'DELETE' });
      toast$('Entry deleted', 'info');
      postLog('LEDGER_ENTRY_DELETED', `Entry deleted from tab: ${activeTab.customer_name}`, outletId);
      loadTabDetail(activeTab.id); loadTabs(true);
    } catch (e: any) { setError(e.message); }
  };

  const handleSendEntryWA = async (entry: Entry) => {
    if (!activeTab) return;
    if (settledEntryIds.has(entry.id)) {
      toast$('This charge is already paid.', 'info');
      return;
    }
    if (!activeTab.phone || activeTab.phone.replace(/\D/g, '').length < 7) {
      toast$('Cannot send: customer has no valid phone number.', 'error');
      return;
    }
    const actionKey = `wa_${entry.id}`;
    setSendingEntryAction(actionKey);
    try {
      // Create payment link specifically for THIS item's amount
      const payLink = await getLedgerPaymentUrl(activeTab, entry.amount);
      const qtyStr = entry.qty !== 1 ? ` (${entry.qty} × ${currSym}${fmt(entry.rate)})` : '';
      const payLine = payLink ? `\n\n💳 *Pay for this item online:*\n${payLink}` : '';
      const msg = `🧾 *Bill for ${entry.description}*\n\nHi ${activeTab.customer_name}!\n\nHere are the bill details for this item:\n• *Item:* ${entry.description}${qtyStr}\n• *Amount to Pay:* *${currSym}${fmt(entry.amount)}*\n• *Date & Time:* ${entry.date} ${entry.time || ''}${entry.note ? `\n• *Note:* ${entry.note}` : ''}${entry.added_by ? `\n• *Billed by:* ${entry.added_by}` : ''}${payLine}\n\n— ${companyName || authUser?.name || 'Credit Book'}`;
      
      await sendWhatsApp(activeTab.phone, msg, outletId);
      toast$(`Bill for ${entry.description} (${currSym}${fmt(entry.amount)}) sent via WhatsApp! 📲`);
      postLog('LEDGER_ENTRY_WA_SENT', `Sent WhatsApp bill for "${entry.description}" (${currSym}${fmt(entry.amount)}) to ${activeTab.customer_name}`, outletId);
    } catch (e: any) {
      toast$(e.message || 'Failed to send WhatsApp receipt', 'error');
    } finally {
      setSendingEntryAction(null);
    }
  };

  const handleSendEntryEmail = async (entry: Entry) => {
    if (!activeTab) return;
    if (settledEntryIds.has(entry.id)) {
      toast$('This charge is already paid.', 'info');
      return;
    }
    if (!activeTab.email || !activeTab.email.includes('@')) {
      toast$('No email on file. Tap "Edit" to add one.', 'info');
      return;
    }
    const actionKey = `email_${entry.id}`;
    setSendingEntryAction(actionKey);
    try {
      // Create payment link specifically for THIS item's amount
      const payLink = await getLedgerPaymentUrl(activeTab, entry.amount);
      const qtyStr = entry.qty !== 1 ? ` (${entry.qty} × ${currSym}${fmt(entry.rate)})` : '';
      const payButtonHtml = `
        <div style="text-align:center;margin:24px 0">
          <a href="${payLink}" style="background:#16a34a;color:#ffffff;padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:bold;display:inline-block;font-size:14px">💳 Pay ${currSym}${fmt(entry.amount)} for ${entry.description} Online →</a>
        </div>
      `;

      const html = `
        <div style="font-family:sans-serif;max-width:480px;margin:auto;padding:24px;border:1.5px solid #e2e8f0;border-radius:12px;background:#ffffff">
          <h2 style="color:#1e1b4b;margin-bottom:6px">🧾 Bill for ${entry.description}</h2>
          <p style="color:#64748b;font-size:13px;margin-top:0">${authUser?.name || 'CloudAIRambo Credit Book'}</p>
          <p style="font-size:14px;color:#1e293b">Hi <strong>${activeTab.customer_name}</strong>,</p>
          <p style="font-size:13px;color:#475569">Here are the details for your bill:</p>
          <table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:13px">
            <tr style="border-bottom:1px solid #f1f5f9">
              <td style="padding:8px 0;color:#64748b">Item</td>
              <td style="padding:8px 0;font-weight:700;color:#0f172a;text-align:right">${entry.description}${qtyStr}</td>
            </tr>
            <tr style="border-bottom:1px solid #f1f5f9">
              <td style="padding:8px 0;color:#64748b">Date & Time</td>
              <td style="padding:8px 0;font-weight:600;color:#475569;text-align:right">${entry.date} ${entry.time || ''}</td>
            </tr>
            ${entry.note ? `<tr style="border-bottom:1px solid #f1f5f9"><td style="padding:8px 0;color:#64748b">Note</td><td style="padding:8px 0;font-weight:600;color:#475569;text-align:right">${entry.note}</td></tr>` : ''}
            <tr style="border-top:2px solid #e2e8f0;background:#f0fdf4">
              <td style="padding:10px 8px;font-weight:800;color:#15803d;font-size:14px">Amount to Pay</td>
              <td style="padding:10px 8px;font-weight:900;color:#15803d;text-align:right;font-size:18px">${currSym}${fmt(entry.amount)}</td>
            </tr>
          </table>
          ${payButtonHtml}
          <p style="color:#94a3b8;font-size:12px;text-align:center;margin-top:16px">— ${companyName}</p>
        </div>
      `;
      await sendEmail(activeTab.email, `Bill for ${entry.description} — ${currSym}${fmt(entry.amount)}`, html, outletId);
      toast$(`Bill for ${entry.description} sent via Email! ✉️`);
      postLog('LEDGER_ENTRY_EMAIL_SENT', `Sent Email bill for "${entry.description}" (${currSym}${fmt(entry.amount)}) to ${activeTab.customer_name}`, outletId);
    } catch (e: any) {
      toast$(e.message || 'Failed to send Email receipt', 'error');
    } finally {
      setSendingEntryAction(null);
    }
  };

  // WhatsApp Reminder: Only includes UNSETTLED items & outstanding balance (like mobile app)
  const handleWAReminder = async () => {
    if (!activeTab) return;
    if (activeTab.balance <= 0) {
      toast$('Account balance is 0. No payment pending.', 'info');
      return;
    }
    if (!activeTab.phone || activeTab.phone.replace(/\D/g, '').length < 7) {
      toast$('Cannot send reminder: customer has no valid phone number.', 'error');
      return;
    }
    try {
      const payLink = await getLedgerPaymentUrl(activeTab);
      const payLine = payLink ? `\n\n💳 *Click to pay online:*\n${payLink}` : '';

      // Exclude settled items from the list
      const pendingItems = (activeTab.entries || []).filter(e => !settledEntryIds.has(e.id));
      const pendingItemsList = pendingItems.length > 0
        ? `\n\n*Pending Items to Pay:*\n` + pendingItems.map(e => `• ${e.description}: ${currSym}${fmt(e.amount)}`).join('\n')
        : '';

      const msg = `⏰ *Payment Reminder*\n\nHi ${activeTab.customer_name}!\n\nHere is your account summary:\n• *Total Outstanding to Pay:* *${currSym}${fmt(activeTab.balance)}*${pendingItemsList}${payLine}\n\nPlease clear when convenient. 🙏\n\n— ${companyName || authUser?.name || 'Credit Book'}`;

      await sendWhatsApp(activeTab.phone, msg, outletId);
      toast$('Reminder sent via WhatsApp! 📲');
      postLog('LEDGER_WA_REMINDER_SENT', `Sent WhatsApp reminder for balance ${currSym}${fmt(activeTab.balance)} to ${activeTab.customer_name}`, outletId);
    } catch (e: any) {
      toast$(e.message || 'Failed to send reminder', 'error');
    }
  };

  // Email Reminder: Only includes UNSETTLED items & outstanding balance
  const handleEmailReminder = async () => {
    if (!activeTab) return;
    if (activeTab.balance <= 0) {
      toast$('Account balance is 0. No payment pending.', 'info');
      return;
    }
    if (!activeTab.email || !activeTab.email.includes('@')) {
      toast$('No email on file. Tap "Edit" to add one.', 'info');
      return;
    }
    try {
      const payLink = await getLedgerPaymentUrl(activeTab);
      const pendingItems = (activeTab.entries || []).filter(e => !settledEntryIds.has(e.id));
      const rowsHtml = pendingItems.map(e => `
        <tr style="border-bottom:1px solid #f1f5f9">
          <td style="padding:8px;color:#0f172a;font-weight:600">${e.description}</td>
          <td style="padding:8px;color:#64748b">${e.date} ${e.time || ''}</td>
          <td style="padding:8px;text-align:right;color:#dc2626;font-weight:700">${currSym}${fmt(e.amount)}</td>
        </tr>
      `).join('');

      const html = `
        <div style="font-family:sans-serif;max-width:500px;margin:auto;padding:24px;border:1.5px solid #e2e8f0;border-radius:16px;background:#ffffff">
          <h2 style="color:#1e1b4b;margin-bottom:6px">⏰ Payment Reminder</h2>
          <p style="color:#64748b;font-size:13px;margin-top:0">${authUser?.name || 'CloudAIRambo Credit Book'}</p>
          <p style="font-size:14px;color:#1e293b">Hi <strong>${activeTab.customer_name}</strong>,</p>
          <p style="font-size:13px;color:#475569">Here is your outstanding payment summary:</p>
          <div style="background:#fef2f2;border:1.5px solid #fecdd3;border-radius:12px;padding:16px;text-align:center;margin:16px 0">
            <div style="font-size:11px;font-weight:800;color:#991b1b;text-transform:uppercase;letter-spacing:0.06em">Remaining Amount to Pay</div>
            <div style="font-size:32px;font-weight:900;color:#dc2626;margin-top:4px">${currSym}${fmt(activeTab.balance)}</div>
          </div>
          ${pendingItems.length > 0 ? `
            <div style="font-size:12px;font-weight:700;color:#475569;margin-bottom:8px;text-transform:uppercase">Pending Items to Pay:</div>
            <table style="width:100%;border-collapse:collapse;margin-bottom:20px;font-size:13px">
              <thead><tr style="background:#f8fafc"><th style="padding:8px;text-align:left;color:#64748b">Item</th><th style="padding:8px;text-align:left;color:#64748b">Date</th><th style="padding:8px;text-align:right;color:#64748b">Amount</th></tr></thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          ` : ''}
          <div style="text-align:center;margin:24px 0">
            <a href="${payLink}" style="background:#16a34a;color:#ffffff;padding:13px 28px;border-radius:10px;text-decoration:none;font-weight:800;display:inline-block;font-size:14px">💳 Pay ${currSym}${fmt(activeTab.balance)} Online Now →</a>
          </div>
          <p style="color:#94a3b8;font-size:11px;text-align:center;margin:0">Thank you for your prompt payment!</p>
        </div>
      `;

      await sendEmail(activeTab.email, `Payment Reminder: ${currSym}${fmt(activeTab.balance)} pending`, html, outletId);
      toast$('Payment reminder sent via Email! ✉️');
      postLog('LEDGER_EMAIL_REMINDER_SENT', `Sent email reminder for balance ${currSym}${fmt(activeTab.balance)} to ${activeTab.customer_name}`, outletId);
    } catch (e: any) {
      toast$(e.message || 'Email reminder failed', 'error');
    }
  };

  const handleShowQR = async (tab: Tab) => {
    const qr = await generateTabQR(tab.id);
    setQrData(qr); setShowQR(true);
  };
const handleSendRenewalReminder = async (member: any, type: 'expiring' | 'expired') => {
  const icon = mainTab === 'gym' ? '🏋️' : '🍽️';
  const module = mainTab === 'gym' ? 'Fitness' : 'Food & Dining';
  const templates = await loadMessageTemplates(outletId);
  const templateKey = type === 'expired' ? 'membership_expired' : 'membership_expiring';
  const msg = applyT((templates as any)[templateKey], { name: member.name, icon, module, plan: member.plan_name, end: member.end_date });
  await sendWhatsApp(member.phone, msg, outletId);
  postLog(mainTab === 'gym' ? 'GYM_RENEWAL_REMINDER_SENT' : 'CANTEEN_RENEWAL_REMINDER_SENT', `Renewal reminder sent (${type}): ${member.name} | ${member.plan_name}`, outletId);
};

const handleBulkReminders = async (members: any[], type: 'expiring' | 'expired') => {
  setSendingReminders(true);
  setRemindersDone(0);
  const failed: string[] = [];
  for (const m of members) {
    try {
      await handleSendRenewalReminder(m, type);
    } catch (e: any) {
      failed.push(m.name);
    }
    setRemindersDone(d => d + 1);
    await new Promise(r => setTimeout(r, 800));
  }
  setSendingReminders(false);
  if (failed.length) {
    alert(`Reminders sent to ${members.length - failed.length}/${members.length}. Failed for: ${failed.join(', ')}`);
  } else {
    toast$(`Reminders sent to ${members.length} members! 📲`);
  }
};
  const handleSavePlan = async () => {
  if (!planName || !planPrice || !planDays) return;
  setSavingPlan(true);
  try {
    await api('/subscriptions/plans', {
      method: 'POST',
      body: JSON.stringify({
        module: currentModule,
        name: planName,
        price: parseFloat(planPrice),
        duration_days: parseInt(planDays),
        slots: planSlots,
        description: planDescription,
        outlet_id: outletId || undefined,
      }),
    });
    toast$('Plan saved!');
       postLog(mainTab === 'gym' ? 'GYM_PLAN_CREATED' : 'CANTEEN_PLAN_CREATED', `Plan created: ${planName} | ${currSym}${planPrice} | ${planDays}d | ${planSlots.join(', ')}`, outletId);
    setPlanName(''); setPlanPrice(''); setPlanDays('30'); setPlanSlots([]); setPlanDescription('');
    loadPlans(currentModule);
  } catch (e: any) { toast$(e.message, 'error'); }
  finally { setSavingPlan(false); }
};

const handleDeletePlan = async (id: number) => {
  const plan = membershipPlans.find(p => p.id === id);
  if (!confirm('Delete this plan?')) return;
  try {
    await api(`/subscriptions/plans/${id}?outlet_id=${outletId || ''}`, { method: 'DELETE' });
    loadPlans(currentModule);
       postLog(mainTab === 'gym' ? 'GYM_PLAN_DELETED' : 'CANTEEN_PLAN_DELETED', `Plan deleted: ${plan?.name || '#' + id}`, outletId);
    toast$('Plan deleted', 'info');
  } catch (e: any) { toast$(e.message, 'error'); }
};
const handleSendMemberPayLink = async (member: any, specificPlan?: any, reminderType: '3days' | 'today' | 'overdue2d' | 'manual' = 'manual') => {
  setSendingMemberPayLink(member.id);
  try {
    const planQs = specificPlan ? `plan_id=${specificPlan.id}` : '';
    const outletQs = outletId ? `outlet_id=${outletId}` : '';
    const combinedQs = [planQs, outletQs].filter(Boolean).join('&');
    const data = await api<{
      payment_link: string | null; amount: number;
      qr_image_url?: string | null; qr_image_content?: string | null;
    }>(`/payments/member/${member.id}${combinedQs ? `?${combinedQs}` : ''}`);

    const planName = specificPlan?.name || member.plan_name;
    const planPrice = specificPlan?.price ?? member.plan_price ?? 0;

    let realPaymentLink = data?.payment_link || (data as any)?.url || (data as any)?.short_url || '';

    // Direct UPI fallback if no gateway configured
    let isUpiFallback = false;
    if (!realPaymentLink && !data?.qr_image_content && !data?.qr_image_url && orgUpiId && Number(planPrice) > 0) {
      realPaymentLink = `upi://pay?pa=${orgUpiId}&pn=${encodeURIComponent(authUser?.name || 'Merchant')}&am=${Number(planPrice).toFixed(2)}&cu=INR&tn=${encodeURIComponent('Membership ' + member.name)}`;
      isUpiFallback = true;
    }

    if (!realPaymentLink && !data?.qr_image_content && !data?.qr_image_url) {
      toast$('Payment gateway or UPI ID not configured', 'error');
      return;
    }

    let headline = `💳 *${mainTab === 'gym' ? 'Gym' : 'Dining'} Membership Renewal*`;
    if (reminderType === '3days') headline = `⏰ *${mainTab === 'gym' ? 'Gym' : 'Dining'} Membership Expiring in 3 Days*`;
    else if (reminderType === 'today') headline = `⚠️ *${mainTab === 'gym' ? 'Gym' : 'Dining'} Membership Expires Today*`;
    else if (reminderType === 'overdue2d') headline = `🚨 *${mainTab === 'gym' ? 'Gym' : 'Dining'} Membership Expired (Overdue)*`;

    const payLine = realPaymentLink ? `\n\n👉 *Click here to pay securely:*\n${realPaymentLink}` : '';
    const msg = `${headline}\n\nHi ${member.name}!\n\n• *Plan:* ${planName}\n• *Amount:* ${currSym}${planPrice}\n• *Current Expiry:* ${member.end_date}${payLine}\n\nOnce payment is confirmed, your continuous access will be extended and your updated QR badge will be delivered to you right here. 🏋️🍽️\n\n— ${companyName || authUser?.name || 'Our Company'}`;

    if (data.qr_image_url || data.qr_image_content) {
      const qrSrc = data.qr_image_url
        ? await fetch(`${BASE}/payments/qr-image?url=${encodeURIComponent(data.qr_image_url)}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          }).then(r => { if (!r.ok) throw new Error(`QR image fetch failed: ${r.status}`); return r.blob(); }).then(blob => new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          }))
        : await QRCode.toDataURL(data.qr_image_content!, { width: 300, margin: 2 });
      await sendWhatsAppImage(member.phone, qrSrc, msg, outletId);
    } else {
      await sendWhatsApp(member.phone, msg, outletId);
    }

    if (member.email && realPaymentLink && !isUpiFallback) {
      try {
        const emailHtml = `
          <div style="font-family:sans-serif;max-width:480px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px;border:1px solid #e2e8f0">
            <h2 style="color:${mainTab === 'gym' ? '#7c3aed' : '#065f46'};margin-bottom:6px">${headline.replace(/\*/g, '')}</h2>
            <p>Hi <strong>${member.name}</strong>!</p>
            <table style="width:100%;border-collapse:collapse;font-size:13px;margin:16px 0">
              <tr style="border-bottom:1px solid #f1f5f9"><td style="padding:8px;color:#6b7280">Plan</td><td style="padding:8px;font-weight:700">${planName}</td></tr>
              <tr style="border-bottom:1px solid #f1f5f9"><td style="padding:8px;color:#6b7280">Amount</td><td style="padding:8px;font-weight:700">${currSym}${planPrice}</td></tr>
              <tr><td style="padding:8px;color:#6b7280">Current Expiry</td><td style="padding:8px;font-weight:700">${member.end_date}</td></tr>
            </table>
            <div style="text-align:center;margin:24px 0">
              <a href="${realPaymentLink}" style="background:${mainTab === 'gym' ? '#7c3aed' : '#065f46'};color:#fff;padding:14px 28px;border-radius:10px;text-decoration:none;font-weight:bold;display:inline-block;font-size:14px">
                💳 Pay Now →
              </a>
            </div>
          </div>
        `;
        await sendEmail(member.email, headline.replace(/\*/g, ''), emailHtml, outletId);
      } catch (err) {}
    }

    toast$('Payment request sent via WhatsApp! 📲');
    postLog(mainTab === 'gym' ? 'GYM_RENEWAL_PAYLINK_SENT' : 'CANTEEN_RENEWAL_PAYLINK_SENT', `Payment link sent: ${member.name} | ${planName} (${currSym}${planPrice})`, outletId);
  } catch (e: any) {
    toast$(e.message || 'Failed to generate payment link', 'error');
  } finally {
    setSendingMemberPayLink(null);
  }
};
  // ── Scan to Pay (mirrors the mobile app) ───────────────────────────────
  const buildLedgerUpiIntent = (tab: Tab) => {
    if (!orgUpiId) return '';
    if (currSym !== '₹') return ''; // UPI is India-only
    const params = new URLSearchParams({
      pa: orgUpiId,
      pn: authUser?.name || 'Merchant',
      am: Number(tab.balance || 0).toFixed(2),
      cu: 'INR',
      tn: `Ledger balance — ${tab.customer_name}`.slice(0, 50),
      tr: `LEDGER${tab.id}${Date.now().toString(36).toUpperCase()}`,
    });
    return `upi://pay?${params.toString()}`;
  };

  const getLedgerPaymentUrl = async (tab: Tab, customAmount?: number): Promise<string> => {
    const amt = customAmount && customAmount > 0 ? customAmount : tab.balance;
    const fallback = `${window.location.origin}/pay/ledger/${tab.id}?amount=${amt}`;
    try {
      const qs = `?amount=${amt}${outletId ? `&outlet_id=${outletId}` : ''}`;
      const data = await api<any>(`/payments/ledger-tab/${tab.id}${qs}`);
      const link = data?.payment_link || data?.url || data?.short_url || data?.qr_image_url || '';
      if (link && String(link).startsWith('http')) return link;
    } catch (e) { console.error('[getLedgerPaymentUrl]', e); }
    return fallback;
  };

  // Display builds its own QR from payment_mode/upi_id/grand_total — see
  // pushBillToDisplay above. Nothing else needed here.
  const clearDisplay = () => clearDisplayBill();

  const closePayQr = () => {
    setShowPayQrModal(false);
    setPayReceived(false);
    setPayQrMember(null);
    setPayQrDataUrl('');
    setPayShareUrl('');
    setPayQrTabId(null);
    clearDisplay();
  };

  const openTabPayQr = async (tab: Tab) => {
    if (tab.balance <= 0) {
      toast$('Balance is already cleared.', 'info');
      return;
    }
    payBaselinePaidRef.current = tab.total_paid || 0;
    setPayQrTabId(tab.id);
    setPayQrTitle(tab.customer_name);
    setPayQrSubtitle('Outstanding Balance');
    setPayQrAmount(tab.balance);
    setPayQrPhone(tab.phone);
    setPayQrEmail(tab.email || '');
    setPayQrDataUrl('');
    setPayShareUrl('');
    setPayQrIsUpi(false);
    setPayReceived(false);
    setShowPayQrModal(true);
    setLoadingPayQr(true);

    try {
      // 1. ALWAYS GATEWAY FIRST (Razorpay, Cashfree, Stripe, etc.)
      let gwData: any = null;
      try {
        gwData = await api<any>(`/payments/ledger-tab/${tab.id}${outletId ? `?outlet_id=${outletId}` : ''}`);
      } catch (gwErr) {
        console.warn('[gateway lookup failed]', gwErr);
      }

      const payLink = gwData?.payment_link || gwData?.url || gwData?.short_url || '';

      // 1a. Gateway native UPI QR (e.g. Razorpay dynamic UPI QR)
      if (gwData?.qr_image_content) {
        setPayQrIsUpi(true);
        const qr = await QRCode.toDataURL(gwData.qr_image_content, { width: 220, margin: 1 });
        setPayQrDataUrl(qr);
        setPayShareUrl(payLink || gwData.qr_image_content);
        pushBillToDisplay(tab.customer_name, tab.balance, qr, payLink || gwData.qr_image_content);
        return;
      }

      // 1b. Gateway payment link QR (e.g. Razorpay rzp.io checkout)
      if (payLink && String(payLink).startsWith('http')) {
        setPayQrIsUpi(false);
        setPayShareUrl(payLink);
        const qr = await QRCode.toDataURL(payLink, { width: 220, margin: 1 });
        setPayQrDataUrl(qr);
        pushBillToDisplay(tab.customer_name, tab.balance, qr, payLink);
        return;
      }

      // 2. FALLBACK TO DIRECT UPI VPA (only if no gateway configured)
      const upiIntent = buildLedgerUpiIntent(tab);
      if (upiIntent) {
        setPayQrIsUpi(true);
        setPayShareUrl(upiIntent);
        const qr = await QRCode.toDataURL(upiIntent, { width: 220, margin: 1 });
        setPayQrDataUrl(qr);
        pushBillToDisplay(tab.customer_name, tab.balance, qr, upiIntent);
        return;
      }

      // 3. Fallback to hosted credit book pay link
      const fallbackUrl = `${window.location.origin}/pay/ledger/${tab.id}`;
      setPayShareUrl(fallbackUrl);
      const qr = await QRCode.toDataURL(fallbackUrl, { width: 220, margin: 1 });
      setPayQrDataUrl(qr);
      pushBillToDisplay(tab.customer_name, tab.balance, qr, fallbackUrl);
    } catch (e: any) {
      toast$(e.message || 'Could not generate QR', 'error');
      setShowPayQrModal(false);
    } finally {
      setLoadingPayQr(false);
    }
  };

  const handleConfirmPaid = async () => {
    if (payQrAmount <= 0) return;
    setConfirmingPaid(true);
    try {
      // 1. If this was a member walk-in renewal QR
      if (!payQrTabId && renewMember && selectedRenewPlan) {
        await handleConfirmRenew();
        setPayReceived(true);
        return;
      }

      // 2. If this was a ledger tab balance QR
      if (payQrTabId) {
        const { date } = now();
        await api(`/ledger/tabs/${payQrTabId}/payments?outlet_id=${outletId || ''}`, {
          method: 'POST',
          body: JSON.stringify({ amount: payQrAmount, note: 'Paid via UPI QR', date, method: 'upi' }),
        });
        postLog('LEDGER_PAYMENT_ADDED', `UPI QR payment confirmed: ${currSym}${fmt(payQrAmount)} | ${payQrTitle}`, outletId);
        payBaselinePaidRef.current = Number.MAX_SAFE_INTEGER;
        setPayReceived(true);
      }
    } catch (e: any) {
      toast$(e.message || 'Could not record the payment', 'error');
    } finally {
      setConfirmingPaid(false);
    }
  };

  const handleSharePayLinkWA = async () => {
    if (!payQrPhone) return;
    const msg = `💳 *Payment Request*\n\nHi ${payQrTitle}!\nOutstanding Balance: ${currSym}${fmt(payQrAmount)}\n\nClick or scan to pay online:\n${payShareUrl}\n\n— ${companyName}`;
    try {
      if (payQrDataUrl) await sendWhatsAppImage(payQrPhone, payQrDataUrl, msg, outletId);
      else await sendWhatsApp(payQrPhone, msg, outletId);
      toast$('Payment link sent via WhatsApp! 📲');
    } catch (e: any) { toast$(e.message || 'WhatsApp send failed', 'error'); }
  };

  const handleSharePayLinkEmail = async () => {
    if (!payQrEmail) return;
    try {
      await sendEmail(payQrEmail, 'Payment Request',
        `<div style="font-family:sans-serif;max-width:480px;margin:auto;padding:24px;border:1px solid #e2e8f0;border-radius:12px"><h2 style="color:#1e1b4b">💳 Payment Request</h2><p>Hi <strong>${payQrTitle}</strong>,</p><p style="color:#64748b;font-size:13px">Your outstanding balance is:</p><p style="font-size:28px;font-weight:900;color:#16a34a;margin:8px 0">${currSym}${fmt(payQrAmount)}</p><div style="text-align:center;margin:24px 0"><a href="${payShareUrl}" style="background:#16a34a;color:#ffffff;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:bold;display:inline-block;font-size:14px">💳 Pay Now →</a></div><p style="color:#94a3b8;font-size:12px;text-align:center">— ${companyName}</p></div>`,
        outletId);
      toast$('Payment link sent via Email! ✉️');
    } catch (e: any) { toast$(e.message || 'Email send failed', 'error'); }
  };

  // Re-push periodically with the QR included so it doesn't get cleared
  useEffect(() => {
    if (!showPayQrModal || payReceived || !payQrTitle || !payQrDataUrl) return;
    const t = setInterval(() => pushBillToDisplay(payQrTitle, payQrAmount, payQrDataUrl, payShareUrl, payQrSubtitle), 4000);
    return () => clearInterval(t);
  }, [showPayQrModal, payReceived, payQrTitle, payQrAmount, payQrDataUrl, payShareUrl, payQrSubtitle, outletDisplayToken]);

  // Watch for the gateway crediting the tab or member while the QR is on screen
  useEffect(() => {
    if (!showPayQrModal || payReceived) return;

    // 1. Polling for Ledger Credit Book Tab payments
    if (payQrTabId) {
      payPollRef.current = setInterval(async () => {
        try {
          const fresh = await api<Tab>(`/ledger/tabs/${payQrTabId}?outlet_id=${outletId || ''}`);
          if ((fresh.total_paid || 0) > payBaselinePaidRef.current) {
            setActiveTab(prev => (prev && prev.id === fresh.id ? fresh : prev));
            setPayReceived(true);
          }
        } catch (e) { console.error('[pay QR poll]', e); }
      }, 5000);
      return () => { if (payPollRef.current) clearInterval(payPollRef.current); payPollRef.current = null; };
    }

    // 2. Polling for Member Membership / Renewal payments (App Parity)
    if (payQrMember?.id) {
      payPollRef.current = setInterval(async () => {
        try {
          const outletQs = outletId ? `&outlet_id=${outletId}` : '';
          const res: any = await api(`/payments/status/by-member?member_id=${payQrMember.id}${outletQs}`);
          const paid = Array.isArray(res)
            ? res.find((p: any) => p.status === 'success')
            : (res?.status === 'success' ? res : null);

          if (paid) {
            clearInterval(payPollRef.current);
            setPayReceived(true);
            if (renewMember && selectedRenewPlan && renewMember.id === payQrMember.id) {
              await handleConfirmRenew();
            } else {
              loadSubMembers(currentModule, true);
              toast$(`Payment received for ${payQrMember.name}! ✅`);
            }
          }
        } catch (e) { console.error('[member pay poll]', e); }
      }, 4000);
      return () => { if (payPollRef.current) clearInterval(payPollRef.current); payPollRef.current = null; };
    }
  }, [showPayQrModal, payReceived, payQrTabId, payQrMember?.id, renewMember, selectedRenewPlan, outletId, currentModule]);

  // Auto-close once payment lands
  useEffect(() => {
    if (!payReceived) return;
    const t = setTimeout(() => {
      setShowPayQrModal(false);
      setPayReceived(false);
      loadTabs(true);
      if (payQrTabId) loadTabDetail(payQrTabId);
    }, 1800);
    return () => clearTimeout(t);
  }, [payReceived, payQrTabId]);

  // Open Exact Ledger-Style "Scan to Pay" Modal for Members
  const openMemberPayQr = async (member: any) => {
    const matchedPlan = membershipPlans.find(p => p.name === member?.plan_name);
    const price = Number(member?.plan_price || matchedPlan?.price || 0);
    setPayQrMember(member);
    setPayQrTabId(null);
    setPayQrEmail(member.email || '');
    setPayQrIsUpi(!!orgUpiId && price > 0 && currSym === '₹');
    setPayReceived(false);
    setPayShareUrl('');
    setPayQrTitle(member.name);
    setPayQrSubtitle(`${member.plan_name} Plan`);
    setPayQrAmount(price);
    setPayQrPhone(member.phone);
    setPayQrDataUrl('');
    setShowPayQrModal(true);
    setLoadingPayQr(true);

    try {
      // 1. Always check gateway first with the current outlet_id (Razorpay, Cashfree, Stripe)
      let data: any = null;
      try {
        const outletQs = outletId ? `?outlet_id=${outletId}` : '';
        data = await api<any>(`/payments/member/${member.id}${outletQs}`);
      } catch (gwErr) {
        console.warn('[gateway member lookup failed]', gwErr);
      }

      const link = data?.payment_link || data?.url || data?.short_url || '';
      const planSubtitle = `${member.plan_name || 'Membership'} Plan`;
      if (data?.qr_image_content) {
        setPayQrIsUpi(true);
        setPayShareUrl(link || data.qr_image_content);
        const qr = await QRCode.toDataURL(data.qr_image_content, { width: 220, margin: 1 });
        setPayQrDataUrl(qr);
        pushBillToDisplay(member.name, price, qr, link || data.qr_image_content, planSubtitle);
        return;
      } else if (link && String(link).startsWith('http')) {
        setPayQrIsUpi(false);
        setPayShareUrl(link);
        const qr = await QRCode.toDataURL(link, { width: 220, margin: 1 });
        setPayQrDataUrl(qr);
        pushBillToDisplay(member.name, price, qr, link, planSubtitle);
        return;
      }

      // 2. Direct UPI VPA fallback
      if (orgUpiId && price > 0 && currSym === '₹') {
        const upiUri = `upi://pay?pa=${orgUpiId}&pn=${encodeURIComponent(authUser?.name || 'Merchant')}&am=${price.toFixed(2)}&cu=INR&tn=${encodeURIComponent('Membership ' + member.name)}`;
        setPayQrIsUpi(true);
        setPayShareUrl(upiUri);
        const qr = await QRCode.toDataURL(upiUri, { width: 220, margin: 1 });
        setPayQrDataUrl(qr);
        pushBillToDisplay(member.name, price, qr, upiUri, `${member.plan_name || 'Membership'} Plan`);
        return;
      }

      // 3. Fallback to hosted member payment link (Allows display push even if gateway keys aren't set)
      const fallbackUrl = `${window.location.origin}/pay/member/${member.id}`;
      setPayQrIsUpi(false);
      setPayShareUrl(fallbackUrl);
      const qr = await QRCode.toDataURL(fallbackUrl, { width: 220, margin: 1 });
      setPayQrDataUrl(qr);
      pushBillToDisplay(member.name, price, qr, fallbackUrl, `${member.plan_name || 'Membership'} Plan`);
    } catch (e: any) {
      toast$(e.message || 'Could not generate QR', 'error');
      setShowPayQrModal(false);
    } finally {
      setLoadingPayQr(false);
    }
  };

  const handleSendPayLink = async (tab: Tab) => {
    await openTabPayQr(tab);
  };

  // ── Member Renewal Handlers (Continuous Cycle) ─────────────────────────
  const openRenewModal = (m: any) => {
    setRenewMember(m);
    const matchedPlan = membershipPlans.find(p => p.name === m.plan_name) || membershipPlans[0] || null;
    setSelectedRenewPlan(matchedPlan);
    setRenewPaymentMethod('cash');
    setShowRenewModal(true);
  };

  const openRenewPaymentQr = async () => {
    if (!renewMember || !selectedRenewPlan) return;
    setShowRenewModal(false);
    await openMemberPayQr({
      ...renewMember,
      plan_price: selectedRenewPlan.price,
      plan_name: selectedRenewPlan.name,
    });
  };

  const handleConfirmRenew = async () => {
    if (!renewMember || !selectedRenewPlan) return;
    if (renewInFlightRef.current) return;
    renewInFlightRef.current = true;
    setRenewing(true);
    try {
      const { newEndDate } = calculateContinuousEndDate(renewMember.end_date, selectedRenewPlan.duration_days);

      await api(`/subscriptions/members/${renewMember.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          plan_name: selectedRenewPlan.name,
          plan_price: selectedRenewPlan.price,
          end_date: newEndDate,
          verified: true,
          slots: selectedRenewPlan.slots && selectedRenewPlan.slots.length > 0 ? selectedRenewPlan.slots : renewMember.slots,
        }),
      });

      // Send fresh QR badge via WhatsApp & Email (App Parity)
      try {
        const qrUrl = await QRCode.toDataURL(`SUB_TOKEN:${renewMember.qr_token}`, { width: 300, margin: 2 });
        const isGymMod = mainTab === 'gym';
        const modTitle = isGymMod ? 'Gym' : 'Dining';
        const modIcon = isGymMod ? '🏋️' : '🍽️';
        const cycleNote = isGymMod ? 'Continuous workout streak intact.' : 'Continuous meals without lost days.';
        const arrivalNote = isGymMod ? 'See you at the gym! 🏋️' : 'Enjoy your meals! 🍽️';

        const confirmMsg = `🎉 *${modTitle} Membership Renewed!*\n\nHi ${renewMember.name}! Your ${isGymMod ? 'membership' : 'food plan'} for *${selectedRenewPlan.name}* is active.\n\n• *New Expiry:* ${newEndDate}\n• *Paid via:* ${renewPaymentMethod.toUpperCase()} (${currSym}${selectedRenewPlan.price})\n• *Cycle Preserved:* ${cycleNote}\n\nYour entrance QR badge is attached below. ${arrivalNote}\n\n— ${companyName || authUser?.name || 'Our Company'}`;

        await sendWhatsAppImage(renewMember.phone, qrUrl, confirmMsg, outletId);

        if (renewMember.email) {
          const modColor = isGymMod ? '#7c3aed' : '#065f46';
          const emailHtml = `
            <div style="font-family:sans-serif;max-width:480px;margin:auto;padding:24px;border:1px solid #e2e8f0;border-radius:16px">
              <h2 style="color:${modColor};margin-bottom:8px">${modIcon} ${modTitle} Membership Renewed!</h2>
              <p>Hi <strong>${renewMember.name}</strong>, your ${isGymMod ? 'membership' : 'meal plan'} has been renewed successfully.</p>
              <table style="width:100%;border-collapse:collapse;margin:16px 0">
                <tr style="border-bottom:1px solid #f1f5f9"><td style="padding:8px;color:#6b7280">Plan</td><td style="padding:8px;font-weight:700">${selectedRenewPlan.name}</td></tr>
                <tr style="border-bottom:1px solid #f1f5f9"><td style="padding:8px;color:#6b7280">New Expiry</td><td style="padding:8px;font-weight:900;color:#16a34a">${newEndDate}</td></tr>
                <tr style="border-bottom:1px solid #f1f5f9"><td style="padding:8px;color:#6b7280">Amount Paid</td><td style="padding:8px;font-weight:700">${currSym}${selectedRenewPlan.price} (${renewPaymentMethod.toUpperCase()})</td></tr>
              </table>
              <div style="text-align:center;margin:16px 0">
                <p style="font-size:12px;color:#64748b;margin-bottom:8px">Your Entrance QR Badge:</p>
                <img src="${qrUrl}" style="width:180px;height:180px;border-radius:12px;border:1px solid #e2e8f0" />
              </div>
            </div>
          `;
          await sendEmail(renewMember.email, `Your ${modTitle} Membership Has Been Renewed!`, emailHtml, outletId);
        }
      } catch (err) {}

      setShowRenewModal(false);
      loadSubMembers(currentModule, true);
      toast$(`Renewed ${renewMember.name} until ${newEndDate}! ✅`);
    } catch (e: any) {
      toast$(e.message || 'Renewal failed', 'error');
    } finally {
      setRenewing(false);
      renewInFlightRef.current = false;
    }
  };

  const handleShareCatalogToLead = async () => {
    const cleanLeadPhone = getCleanE164Phone(leadPhone);
    if (!cleanLeadPhone || cleanLeadPhone.replace(/\D/g, '').length < 7) {
      toast$('Please enter a valid phone number (7 to 15 digits).', 'error');
      return;
    }
    if (!membershipPlans || membershipPlans.length === 0) {
      toast$('No plans available to share. Create a plan first.', 'error');
      return;
    }

    setSendingCatalog(true);
    try {
      const planItems = membershipPlans
        .map(p => `• *${p.name}* (${p.duration_days} Days) — *${currSym}${p.price}*${p.description ? `\n  _${p.description}_` : ''}`)
        .join('\n\n');

      const greeting = leadName.trim() ? `Hi ${leadName.trim()}!` : 'Hi there!';
      const moduleName = mainTab === 'gym' ? 'Fitness' : 'Food & Dining';
      const icon = mainTab === 'gym' ? '🏋️' : '🍽️';

      const msg = `📋 *${moduleName} Membership Plans*\n\n${greeting} Here are our current membership plans and pricing:\n\n${planItems}\n\n📍 Visit our front desk or reply to this message to register today! ${icon}\n\n— ${companyName || authUser?.name || 'Our Company'}`;

      await sendWhatsApp(cleanLeadPhone, msg, outletId);
      setShowShareCatalogModal(false);
      setLeadPhone('');
      setLeadName('');
      toast$('Pricing menu sent via WhatsApp! 📲');
      postLog(mainTab === 'gym' ? 'GYM_CATALOG_SHARED' : 'CANTEEN_CATALOG_SHARED', `Pricing menu sent to ${leadName || cleanLeadPhone}`, outletId);
    } catch (e: any) {
      toast$(e.message || 'Could not send pricing catalog', 'error');
    } finally {
      setSendingCatalog(false);
    }
  };

const handleSaveTabEdit = async () => {
  if (!editTab || !editTabName.trim() || !editTabPhone.trim()) return;
  const cleanPhone = getCleanE164Phone(editTabPhone);
  if (!cleanPhone) {
    toast$('Please enter a valid phone number (between 7 and 15 digits)', 'error');
    return;
  }
  const editTabPhoneDigits = cleanPhone.replace(/\D/g, '');
  const existingTabDigits = (editTab.phone || '').replace(/\D/g, '');
  const phoneChanged = editTabPhoneDigits.slice(-10) !== existingTabDigits.slice(-10);

  if (phoneChanged) {
    setSendingTabPhoneOtp(true);
    try {
      const res = await api<any>('/subscriptions/members/send-precreate-otp', {
        method: 'POST',
        body: JSON.stringify({ phone: cleanPhone, name: editTabName }),
      });
      await sendWhatsApp(cleanPhone,
        `🔐 *Verification OTP*\n\nHi ${editTabName}! Your OTP to verify your new number is: *${res.otp}*\n\nValid for 10 minutes.\n— ${companyName}`,
        outletId
      );
      setPendingTabEditData({ name: editTabName, phone: cleanPhone, email: editTabEmail, address: editTabAddress });
      setTabPhoneOtp('');
      setShowTabPhoneOtp(true);
      toast$('OTP sent to new number 📲');
    } catch (e: any) { toast$(e.message, 'error'); }
    finally { setSendingTabPhoneOtp(false); }
    return;
  }
  setSavingTabEdit(true);
  try {
    await api(`/ledger/tabs/${editTab.id}?outlet_id=${outletId || ''}`, {
      method: 'PATCH',
      body: JSON.stringify({
        customer_name: editTabName,
        phone: cleanPhone,
        email: editTabEmail || null,
        address: editTabAddress || null,
      }),
    });
    toast$('Customer updated!');
    setEditTab(null);
    loadTabs(true);
    if (activeTab?.id === editTab.id) loadTabDetail(editTab.id);
  } catch (e: any) { toast$(e.message, 'error'); }
  finally { setSavingTabEdit(false); }
};

const handleDeleteTab = async (tab: Tab) => {
  if (!confirm(`Delete ${tab.customer_name}'s tab? This will permanently remove all charges and payments.`)) return;
  try {
    await api(`/ledger/tabs/${tab.id}?outlet_id=${outletId || ''}`, { method: 'DELETE' });
    toast$('Tab deleted', 'info');
 postLog('LEDGER_TAB_DELETED', `Tab deleted: ${tab.customer_name} | ${tab.phone}`, outletId);
    setView('list');
    setActiveTab(null);
    loadTabs(true);
  } catch (e: any) { toast$(e.message, 'error'); }
};

  const groupedEntries = (entries: Entry[]) => {
    const groups: Record<string, Entry[]> = {};
    entries.forEach(e => { if (!groups[e.date]) groups[e.date] = []; groups[e.date].push(e); });
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
  };

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return tabs.filter(t =>
      (!q || t.customer_name.toLowerCase().includes(q) || t.phone.includes(q)) &&
      (statusFilter === 'all' || t.status === statusFilter)
    );
  }, [tabs, search, statusFilter]);

  const { totalBalance, openCount } = React.useMemo(() => {
    let bal = 0;
    let open = 0;
    for (const t of tabs) {
      if (t.status !== 'settled') bal += (t.balance || 0);
      if (t.status === 'open' || t.status === 'partially_paid') open++;
    }
    return { totalBalance: bal, openCount: open };
  }, [tabs]);

  // ── Shared Sheet Style ─────────────────────────────────────────────────
  const sheetStyle: React.CSSProperties = {
    background: '#fff', borderRadius: 20, padding: 24,
    width: '100%', maxWidth: 500, maxHeight: '88vh',
    overflowY: 'auto', boxShadow: '0 32px 80px rgba(0,0,0,0.2)',
    scrollbarWidth: 'none', msOverflowStyle: 'none',
  } as React.CSSProperties;
  const inputStyle: React.CSSProperties = {
    border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px',
    fontSize: 13, outline: 'none', background: '#f8fafc', width: '100%',
    boxSizing: 'border-box', color: '#1e293b', fontWeight: 600,
    transition: 'border-color 0.15s',
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 6, display: 'block',
  };
  const modalStyle: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)',
    backdropFilter: 'blur(4px)', zIndex: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  };
  const loadPlans = async (module: string) => {
  try {
    const outletQs = outletId ? `&outlet_id=${outletId}` : '';
    const data = await api<any[]>(`/subscriptions/plans?module=${module}${outletQs}`);
    setMembershipPlans(data);
  } catch { }
};

const loadSlots = async (module: string) => {
  try {
    const outletQs = outletId ? `&outlet_id=${outletId}` : '';
    const data = await api<any[]>(`/subscriptions/slots?module=${module}${outletQs}`);
    setOrgSlots(data);
  } catch { }
};

const loadDeviceKey = async () => {
  try {
    const outletQs = outletId ? `&outlet_id=${outletId}` : '';
    const devices = await api<any[]>(`/subscriptions/kiosk-devices?module=${currentModule}${outletQs}`);
    setDeviceApiKey(devices.length > 0 ? devices[0].api_key : '');
  } catch { }
};
const subMembersRef = useRef<any[]>([]);
const todayBoardRef = useRef<any[]>([]);
const loadSubMembers = async (module: string, silent = false) => {
  if (!silent) setSubLoading(true);
  try {
    const outletQs = outletId ? `&outlet_id=${outletId}` : '';
    const [members, board] = await Promise.all([
      api<any[]>(`/subscriptions/members?module=${module}${outletQs}`),
      api<any[]>(`/subscriptions/attendance/today?module=${module}${outletQs}`),
    ]);
    // Skip state update if nothing actually changed — avoids re-render flash on silent polls
    if (JSON.stringify(members) !== JSON.stringify(subMembersRef.current)) {
      subMembersRef.current = members;
      setSubMembers(members);
    }
    if (JSON.stringify(board) !== JSON.stringify(todayBoardRef.current)) {
      todayBoardRef.current = board;
      setTodayBoard(board);
    }
  } catch (e: any) { if (!silent) setError(e.message); }
  finally { if (!silent) setSubLoading(false); }
};

useEffect(() => {
  if (mainTab !== 'ledger') {
    loadSubMembers(currentModule);
    loadPlans(currentModule);
    loadSlots(currentModule);
    loadDeviceKey();

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadSubMembers(currentModule, true);
    }, 8000);
    const onFocus = () => loadSubMembers(currentModule, true);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }
}, [mainTab, outletId, currentModule]);

const handleCreateMember = async () => {
  if (!memberName.trim() || !memberPhone.trim() || !memberEnd) return;
  const cleanPhone = getCleanE164Phone(memberPhone);
  if (!cleanPhone) {
    setError('Please enter a valid phone number (between 7 and 15 digits).');
    return;
  }
  setSendingPreOtp(true);
  try {
    const res = await api<any>('/subscriptions/members/send-precreate-otp', {
      method: 'POST',
      body: JSON.stringify({ phone: cleanPhone, name: memberName }),
    });
    await sendWhatsApp(cleanPhone,
      `🔐 *Verification OTP*\n\nHi ${memberName}! Your verification code to join is: *${res.otp}*\n\nValid for 10 minutes.\n— ${companyName}`,
      outletId
    );
    if (memberEmail.trim()) {
      await sendEmail(memberEmail, 'Your Verification OTP',
        `<div style="font-family:sans-serif;max-width:400px;margin:auto;padding:32px;background:#f8f9fb;border-radius:16px"><h2 style="color:#1e1b4b">Verification OTP</h2><div style="font-size:36px;font-weight:900;letter-spacing:12px;color:#6366f1;background:#fff;padding:20px;border-radius:12px;text-align:center">${res.otp}</div><p style="color:#6b7280;margin-top:16px">Valid for 10 minutes. Do not share this with anyone.</p></div>`,
        outletId
      );
    }
    setPendingMemberData({
      name: memberName,
      phone: cleanPhone,
      email: memberEmail,
      module: currentModule,
      plan_name: memberPlan,
      plan_price: membershipPlans.find(p => p.name === memberPlan)?.price || 0,
      slots: memberSlots,
      start_date: memberStart,
      end_date: memberEnd,
      outlet_id: outletId || undefined,
    });
    setPreOtpPhone(cleanPhone);
    setPreOtpValue('');
    setShowNewMember(false);
    setShowPreOtp(true);
    toast$('OTP sent to ' + cleanPhone + ' 📲');
  } catch (e: any) { setError(e.message); }
  finally { setSendingPreOtp(false); }
};

const handleVerifyPreOtp = async () => {
  if (!pendingMemberData) return;
  setVerifyingPreOtp(true);
  try {
    await api('/subscriptions/members/verify-precreate-otp', {
      method: 'POST',
      body: JSON.stringify({ phone: preOtpPhone, otp: preOtpValue }),
    });
    setCreatingMember(true);
    const m = await api<any>('/subscriptions/members', {
      method: 'POST',
      body: JSON.stringify({ ...pendingMemberData, verified: true }),
    });

    // Save/update this member in the shared Client Accounts list and backend directory
    syncTabToAccounts(m.name, m.phone, m.email);
    const qrUrl = await QRCode.toDataURL(`SUB_TOKEN:${m.qr_token}`, {
      width: 300, margin: 2, errorCorrectionLevel: 'L',
      color: { dark: mainTab === 'gym' ? '#7c3aed' : '#065f46', light: '#ffffff' }
    });
    // Instead of closing, open biometric setup
    setShowPreOtp(false);
    setPendingMemberData(null);
    if (mainTab === 'gym' ? perms.canGymViewBadge : perms.canFoodViewBadge) {
      setMemberQR({ url: qrUrl, name: m.name, member: m });
    }
   const slots = m.slots || [];
   const moduleTitle = mainTab === 'gym' ? 'Fitness' : 'Food & Dining';
   const moduleIcon = mainTab === 'gym' ? '🏋️' : '🍽️';
   const moduleColor = mainTab === 'gym' ? '#7c3aed' : '#065f46';
   const memberPortalUrl = `${window.location.origin}/pay/member/${m.id}`;

   const caption = `🎉 *Welcome to ${moduleTitle}!* ${moduleIcon}\n\nHi ${m.name}! Your membership has been registered successfully.\n\n• *Plan:* ${m.plan_name}\n• *Validity:* ${m.start_date} → ${m.end_date}${slots.length ? `\n• *${mainTab === 'gym' ? 'Slots' : 'Meal Slots'}:* ${slots.join(', ')}` : ''}\n\nShow your entrance QR badge at the entrance counter! ${moduleIcon}\n\n— ${companyName || authUser?.name || 'Our Company'}`;

   if (m.phone) {
     try {
       const qrRes = await fetch(`${BASE}/subscriptions/members/${m.id}/send-qr-whatsapp`, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
       });
       if (!qrRes.ok) {
         await sendWhatsAppImage(m.phone, qrUrl, caption, outletId);
       }
     } catch (e: any) {
       console.warn('WhatsApp QR badge send failed:', e);
     }
   }

   if (m.email) {
     try {
       await sendEmail(m.email, `🎉 Welcome! Your ${moduleTitle} Membership QR Badge`,
         `<div style="font-family:sans-serif;max-width:500px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px;border:1px solid #e2e8f0">
           <h2 style="color:${moduleColor};margin-bottom:6px">${moduleIcon} Welcome to ${moduleTitle}!</h2>
           <p>Hi <strong>${m.name}</strong>, your membership is active. Your entrance QR badge is below.</p>
           <div style="background:#fff;border-radius:12px;padding:20px;text-align:center;margin:20px 0;border:1px solid #e2e8f0">
             <img src="${qrUrl}" style="width:200px;height:200px" />
           </div>
           <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:20px">
             <tr style="border-bottom:1px solid #f1f5f9"><td style="padding:8px;color:#6b7280">Plan</td><td style="padding:8px;font-weight:700">${m.plan_name}</td></tr>
             <tr style="background:#f1f5f9"><td style="padding:8px;color:#6b7280">Validity</td><td style="padding:8px;font-weight:700">${m.start_date} → ${m.end_date}</td></tr>
             <tr><td style="padding:8px;color:#6b7280">${mainTab === 'gym' ? 'Slots' : 'Meal Slots'}</td><td style="padding:8px;font-weight:700">${slots.join(', ') || 'All slots'}</td></tr>
           </table>
           <div style="text-align:center;margin:24px 0">
             <a href="${memberPortalUrl}" style="background:${moduleColor};color:#fff;padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:bold;display:inline-block;font-size:14px">
               💳 View Plans & Upgrade Online →
             </a>
           </div>
         </div>`,
         outletId
       );
     } catch (e: any) {
       console.warn('Email QR badge send failed:', e);
     }
   }
    setBiometricMember(m);
    setFaceSetupStatus('idle');
    setFingerprintStatus('idle');
    setShowBiometricSetup(true);
    setMemberName(''); setMemberPhone(''); setMemberEmail('');
    setMemberSlots([]); setMemberEnd('');
    loadSubMembers(currentModule);
postLog(mainTab === 'gym' ? 'GYM_MEMBER_CREATED' : 'CANTEEN_MEMBER_CREATED', `New member: ${m.name} | ${m.phone} | ${m.plan_name} | ${m.slots.join(', ')} | ${m.start_date} → ${m.end_date}`, outletId);
    toast$(`Member ${m.name} created! Set up biometrics 👆`);
  } catch (e: any) { setError(e.message); }
  finally { setVerifyingPreOtp(false); setCreatingMember(false); }
};

const handleDeleteMember = async (id: number) => {
  const member = subMembers.find(m => m.id === id);
  if (!confirm('Delete this member?')) return;
  // Optimistic removal — updates the UI instantly, no waiting on the network
  const prevMembers = subMembers;
  const nextMembers = subMembers.filter(m => m.id !== id);
  subMembersRef.current = nextMembers;
  setSubMembers(nextMembers);
  try {
    await api(`/subscriptions/members/${id}`, { method: 'DELETE' });
    postLog(mainTab === 'gym' ? 'GYM_MEMBER_DELETED' : 'CANTEEN_MEMBER_DELETED', `Member deleted: ${member?.name || '#' + id} | ${member?.phone || ''}`, outletId);
    toast$('Member deleted', 'info');
    loadSubMembers(currentModule, true); // silent sync with server
  } catch (e: any) {
    // Roll back on failure
    subMembersRef.current = prevMembers;
    setSubMembers(prevMembers);
    setError(e.message);
  }
};

const openMemberQR = async (member: any) => {
  const qrUrl = await QRCode.toDataURL(`SUB_TOKEN:${member.qr_token}`, {
    width: 300, margin: 2, errorCorrectionLevel: 'L',
    color: { dark: mainTab === 'gym' ? '#7c3aed' : '#065f46', light: '#ffffff' }
  });
  setMemberQR({ url: qrUrl, name: member.name, member });
};

const openMemberHistory = async (member: any) => {
  setHistoryMember(member);
  setShowMemberHistory(true);
  setHistoryLoading(true);
  try {
    const data = await api<any>(`/subscriptions/members/${member.id}/logs`);
    setHistoryLogs(data.logs || []);
  } catch (e: any) {
    toast$('Failed to load history', 'error');
    setHistoryLogs([]);
  } finally {
    setHistoryLoading(false);
  }
};

const printMemberBadge = (member: any, qrUrl: string) => {
  const color = mainTab === 'gym' ? '#7c3aed' : '#065f46';
  const icon = mainTab === 'gym' ? '🏋️' : '🍽️';
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(`
    <html><head><title>${member.name} Badge</title>
    <style>
      body{font-family:system-ui;text-align:center;padding:40px;margin:0;background:#f8fafc;}
      .badge{display:inline-block;border:3px solid ${color};border-radius:16px;padding:24px 32px;background:#fff;}
      h2{margin:0 0 4px;color:${color};font-size:20px;}
      p{margin:0 0 4px;color:#6b7280;font-size:12px;}
      img{display:block;margin:12px auto;width:180px;height:180px;}
      .plan{margin-top:8px;background:${color}22;color:${color};padding:4px 14px;border-radius:20px;font-size:11px;font-weight:700;display:inline-block;}
    </style></head><body>
    <div class="badge">
      <div style="font-size:32px">${icon}</div>
      <h2>${member.name}</h2>
      <p>${member.phone}</p>
      <p>${member.plan_name} · ${member.start_date} → ${member.end_date}</p>
      <img src="${qrUrl}" onload="window.print()" />
      <div class="plan">${member.slots.join(' · ')}</div>
    </div>
    </body></html>
  `);
  win.document.close();
};
const shareQRWhatsApp = async (member: any, qrUrl: string) => {
  const icon = mainTab === 'gym' ? '🏋️' : '🍽️';
  const templates = await loadMessageTemplates();
  const caption = applyT(templates.qr_badge_whatsapp, {
    name: member.name,
    icon,
    module: mainTab === 'gym' ? 'Fitness' : 'Food & Dining',
    plan: member.plan_name,
    start: member.start_date,
    end: member.end_date,
    slots: member.slots.join(', '),
  });
  try {
    await sendWhatsAppImage(member.phone, qrUrl, caption, outletId);
  } catch (e: any) {
    alert(`WhatsApp QR badge failed to send: ${e.message}`);
    return;
  }
  if (member.email) {
    try {
      await sendEmail(member.email, `Your ${mainTab === 'gym' ? 'Fitness' : 'Food & Dining'} QR Badge`,
        `<div style="font-family:sans-serif;max-width:480px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px">
          <h2 style="color:${mainTab === 'gym' ? '#7c3aed' : '#065f46'}">${mainTab === 'gym' ? '💪 Fitness' : '🍽️ Food & Dining'} QR Badge</h2>
          <p>Hi <strong>${member.name}</strong>! Your QR badge is attached below. Show this to staff when you arrive.</p>
          <div style="background:#fff;border-radius:12px;padding:20px;text-align:center;margin:20px 0">
            <img src="${qrUrl}" style="width:200px;height:200px" />
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:13px">
            <tr><td style="padding:8px;color:#6b7280">Plan</td><td style="padding:8px;font-weight:700">${member.plan_name}</td></tr>
            <tr style="background:#f1f5f9"><td style="padding:8px;color:#6b7280">Valid</td><td style="padding:8px;font-weight:700">${member.start_date} → ${member.end_date}</td></tr>
            <tr><td style="padding:8px;color:#6b7280">Slots</td><td style="padding:8px;font-weight:700">${member.slots.join(', ')}</td></tr>
          </table>
        </div>`,
        outletId
      );
    } catch (e: any) {
      alert(`QR badge sent via WhatsApp, but email failed: ${e.message}`);
      return;
    }
  }
  toast$('QR badge sent via WhatsApp! 📲');
};

const sendWeeklyReport = async (member: any) => {
  try {
    const logs = await api<any>(`/subscriptions/members/${member.id}/logs`);
    const allLogs = logs.logs || [];
    
    // Get last 7 days
    const today = new Date();
    const weekDays: string[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      weekDays.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }

    const weekLogs = allLogs.filter((l: any) => weekDays.includes(l.date));
    const totalSlots = weekDays.length * member.slots.length;
    const attended = weekLogs.filter((l: any) => l.attended).length;
    const missed = totalSlots - attended;
    const pct = totalSlots > 0 ? Math.round((attended / totalSlots) * 100) : 0;

    // Build day-by-day table
    const dayRows = weekDays.map(date => {
      const dayLogs = weekLogs.filter((l: any) => l.date === date);
      const slots = member.slots.map((slot: string) => {
        const found = dayLogs.find((l: any) => l.slot === slot && l.attended);
        return `${found ? '✅' : '❌'} ${slot}`;
      }).join('  ');
      const label = new Date(date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
      return `${label}: ${slots}`;
    }).join('\n');

    const icon = mainTab === 'gym' ? '🏋️' : '🍽️';
    const templates = await loadMessageTemplates(outletId);
const waMsg = applyT(templates.weekly_report_whatsapp, {
  name: member.name,
  icon,
  module: mainTab === 'gym' ? 'Fitness' : 'Food & Dining',
  dayRows,
  attended,
  total: totalSlots,
  pct,
  missedLine: missed > 0 ? `\n❌ Missed: ${missed} slots` : '',
  encouragement: pct >= 80 ? '🌟 Great attendance this week!' : pct >= 50 ? '👍 Keep it up!' : '💪 Try to attend more regularly!',
});

    let waFailed = false;
    let emailFailed = false;

    // Independent WhatsApp Dispatch
    if (member.phone) {
      try {
        await sendWhatsApp(member.phone, waMsg, outletId);
      } catch (err) {
        waFailed = true;
      }
    } else {
      waFailed = true;
    }

    // Independent Email Dispatch
    if (member.email) {
      try {
        const htmlRows = weekDays.map(date => {
          const dayLogs = weekLogs.filter((l: any) => l.date === date);
          const label = new Date(date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
          const slotCells = member.slots.map((slot: string) => {
            const found = dayLogs.find((l: any) => l.slot === slot && l.attended);
            return `<td style="padding:8px;text-align:center;background:${found ? '#f0fdf4' : '#fff1f2'};color:${found ? '#15803d' : '#be123c'};font-weight:700">${found ? '✅' : '❌'}</td>`;
          }).join('');
          return `<tr><td style="padding:8px;font-weight:600;color:#374151">${label}</td>${slotCells}</tr>`;
        }).join('');

        const headerCells = member.slots.map((s: string) =>
          `<th style="padding:8px;background:${mainTab === 'gym' ? '#7c3aed' : '#065f46'};color:#fff;font-size:12px">${s}</th>`
        ).join('');

        await sendEmail(member.email,
          `Your Weekly ${mainTab === 'gym' ? 'Fitness' : 'Food & Dining'} Report`,
          `<div style="font-family:sans-serif;max-width:520px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px">
            <h2 style="color:${mainTab === 'gym' ? '#7c3aed' : '#065f46'}">${icon} Weekly Attendance Report</h2>
            <p>Hi <strong>${member.name}</strong>! Here's your attendance for the past 7 days.</p>
            <div style="background:#fff;border-radius:12px;padding:20px;margin:16px 0">
              <div style="display:flex;gap:20px;margin-bottom:16px">
                <div style="text-align:center;flex:1;background:#f0fdf4;border-radius:10px;padding:12px">
                  <div style="font-size:28px;font-weight:900;color:#15803d">${attended}</div>
                  <div style="font-size:11px;color:#6b7280">Attended</div>
                </div>
                <div style="text-align:center;flex:1;background:#fff1f2;border-radius:10px;padding:12px">
                  <div style="font-size:28px;font-weight:900;color:#be123c">${missed}</div>
                  <div style="font-size:11px;color:#6b7280">Missed</div>
                </div>
                <div style="text-align:center;flex:1;background:#eff6ff;border-radius:10px;padding:12px">
                  <div style="font-size:28px;font-weight:900;color:#1d4ed8">${pct}%</div>
                  <div style="font-size:11px;color:#6b7280">Rate</div>
                </div>
              </div>
              <table style="width:100%;border-collapse:collapse;font-size:13px">
                <thead><tr><th style="padding:8px;background:#f1f5f9;text-align:left">Day</th>${headerCells}</tr></thead>
                <tbody>${htmlRows}</tbody>
              </table>
            </div>
            <p style="color:#6b7280;font-size:12px">${pct >= 80 ? '🌟 Great attendance this week!' : pct >= 50 ? '👍 Keep it up!' : '💪 Try to attend more regularly!'}</p>
          </div>`,
          outletId
        );
      } catch (err) {
        emailFailed = true;
      }
    } else {
      emailFailed = true;
    }

    // Accurate Multi-Channel Feedback (App Parity)
    postLog(mainTab === 'gym' ? 'GYM_WEEKLY_REPORT_SENT' : 'CANTEEN_WEEKLY_REPORT_SENT', `Weekly report sent: ${member.name} | ${attended}/${totalSlots} (${pct}%)`, outletId);

    if (!waFailed && !emailFailed) {
      toast$(`Weekly report sent via WhatsApp & Email! 📊`);
    } else if (waFailed && emailFailed) {
      toast$('Could not send report via WhatsApp or Email.', 'error');
    } else if (waFailed) {
      toast$(`Report sent via Email only (WhatsApp failed) ✉️`, 'info');
    } else {
      toast$(`Report sent via WhatsApp only (No email or failed) 📲`, 'info');
    }
  } catch (e: any) {
    toast$(e.message || 'Failed to generate weekly report', 'error');
  }
};
// Scanner functions
const startScanner = async (slot: string) => {
  setScanSlot(slot);
  setScanResult(null);
  setShowScanner(true);
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
    streamRef.current = stream;
    if (videoRef.current) videoRef.current.srcObject = stream;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    scanInterval.current = setInterval(async () => {
      const v = videoRef.current;
      if (!v || v.readyState < 2) return;
      canvas.width = v.videoWidth; canvas.height = v.videoHeight;
      ctx.drawImage(v, 0, 0);
      const jsQR = (window as any).jsQR;
      if (!jsQR) return;
      const code = jsQR(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height);
      if (code?.data?.startsWith('SUB_TOKEN:')) {
        clearInterval(scanInterval.current);
        stopScanner();
        const token = code.data.replace('SUB_TOKEN:', '');
        let result: any;
        try {
          const qs = outletId ? `?outlet_id=${outletId}` : '';
          result = await api<any>(`/subscriptions/scan${qs}`, {
            method: 'POST',
            body: JSON.stringify({ qr_token: token, slot }),
          });
        } catch (e: any) {
          setScanResult({ status: 'error', message: e.message });
          return;
        }
        // Check-in itself succeeded — from here, any failure is notification-only and must not flip the result to error.
        setScanResult(result);
        if (result.slot) setScanSlot(result.slot); // show the resolved slot, not '__auto__'
        loadSubMembers(currentModule);
        if (result.status === 'success') {
          postLog(mainTab === 'gym' ? 'GYM_QR_CHECKIN' : 'CANTEEN_QR_CHECKIN', `QR scan: ${result.name} | ${result.scan_type === 'in' ? 'Checked In' : 'Checked Out'} | ${result.slot} | ${result.time}`, outletId);
          const member = subMembers.find(m => m.name === result.name);
          if (member?.phone) {
            try {
              const isIn = result.scan_type === 'in';
              const templates = await loadMessageTemplates(outletId);
              const isCanteen = mainTab === 'canteen';
              const templateKey = isCanteen
                ? templates.canteen_slot_message
                : isIn ? templates.gym_checkin_message : templates.gym_checkout_message;
              const scanMsg = applyT(templateKey, {
                name: result.name,
                time: result.time,
                date: result.date,
                slot: result.slot,
              });
              await sendWhatsApp(member.phone, scanMsg, outletId);
            } catch (e: any) {
              toast$(`Checked in, but WhatsApp notification failed: ${e.message}`, 'error');
            }
          }
        }
      }
    }, 300);
  } catch { setScanResult({ status: 'error', message: 'Camera access denied' }); }
};

// ── Unified Biometric (Face + Fingerprint) Strict Security OTP Guard ───────
const [biometricOtpTarget, setBiometricOtpTarget] = useState<'face' | 'fingerprint' | null>(null);

const checkIsBiometricLocked = (member: any, type: 'face' | 'fingerprint') => {
  if (!member) return false;
  if (type === 'face') {
    return !!(
      member.face_descriptor ||
      member.face_image_encoding ||
      member.has_face ||
      member.face_registered
    );
  }
  return !!(
    member.fingerprint_id ||
    member.fingerprint_credential_id ||
    member.has_fingerprint ||
    member.fingerprint_registered
  );
};

const handleBiometricAction = async (member: any, type: 'face' | 'fingerprint') => {
  const isLocked = checkIsBiometricLocked(member, type);

  if (isLocked) {
    // 🔒 Enrolled Biometric -> STRICT OTP REQUIRED BEFORE CAMERA / SENSOR OPENS
    setFaceOtpMember(member);
    setBiometricOtpTarget(type);
    setFaceOtpInput('');
    try {
      setFaceOtpLoading(true);
      const res: any = await api(`/subscriptions/members/${member.id}/request-face-otp`, { method: 'POST' });
      const otpCode = res?.otp || res?.debug_otp || '123456';
      const bioLabel = type === 'face' ? 'Face' : 'Fingerprint';

      // 1. Send via WhatsApp
      if (member.phone) {
        try {
          await sendWhatsApp(
            member.phone,
            `🔐 *Biometric Security OTP*\n\nHi ${member.name}! Your verification OTP to unlock and update your ${bioLabel} is: *${otpCode}*\n\nValid for 10 minutes.\n— ${companyName}`,
            outletId
          );
        } catch (waErr) {
          console.warn('WhatsApp OTP send failed:', waErr);
        }
      }

      // 2. Send via Email
      if (member.email) {
        try {
          await sendEmail(
            member.email,
            `Biometric Security Verification OTP — ${member.name}`,
            `<div style="font-family:sans-serif;max-width:440px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px;border:1px solid #e2e8f0">
              <h2 style="color:#7c3aed;margin-bottom:6px">🔒 ${bioLabel} Security OTP</h2>
              <p>Hi <strong>${member.name}</strong>,</p>
              <p style="color:#64748b;font-size:13px">Use the verification code below to authorize modifying your registered ${bioLabel.toLowerCase()} biometrics:</p>
              <div style="font-size:36px;font-weight:900;letter-spacing:10px;color:#7c3aed;background:#fff;padding:18px;border-radius:12px;text-align:center;margin:20px 0;border:1.5px solid #e2e8f0">
                ${otpCode}
              </div>
              <p style="color:#94a3b8;font-size:12px;margin-top:16px">Valid for 10 minutes. If you did not request this change, please inform the front desk immediately.</p>
            </div>`,
            outletId
          );
        } catch (emailErr) {
          console.warn('Email OTP send failed:', emailErr);
        }
      }

      toast$(`Security OTP sent via WhatsApp & Email for ${member.name} 📲`);
      console.log(`[Member ${bioLabel} OTP]: ${otpCode}`);
    } catch (err: any) {
      toast$(err.message || 'Failed to request OTP', 'error');
    } finally {
      setFaceOtpLoading(false);
    }
  } else {
    // First time enrollment -> open directly
    if (type === 'face') {
      startFaceRegister(member);
    } else {
      setBiometricMember(member);
      // ⚡ Check if member already has fingerprint saved in DB
      const hasFp = !!(member.fingerprint_id || member.fingerprint_credential_id);
      setFingerprintStatus(hasFp ? 'done' : 'idle');
      setFaceSetupStatus((member.face_descriptor || member.face_image_encoding) ? 'done' : 'idle');
      setPhoneEnrollQR('');
      setWaitingPhoneBio(false);
      setShowBiometricSetup(true);
    }
  }
};

const verifyBiometricOtpAndProceed = async () => {
  if (!faceOtpMember || !faceOtpInput) return;
  try {
    setFaceOtpLoading(true);
    await api(`/subscriptions/members/${faceOtpMember.id}/verify-face-otp`, {
      method: 'POST',
      body: JSON.stringify({ otp: faceOtpInput }),
    });
    toast$('OTP Verified! Access Granted 🔓');
    const target = faceOtpMember;
    const targetType = biometricOtpTarget;
    setFaceOtpMember(null);
    setFaceOtpInput('');
    setBiometricOtpTarget(null);

    if (targetType === 'face') {
      startFaceRegister(target);
    } else {
      setBiometricMember(target);
      setFingerprintStatus('idle');
      setFaceSetupStatus((target.face_descriptor || target.face_image_encoding) ? 'done' : 'idle');
      setPhoneEnrollQR('');
      setWaitingPhoneBio(false);
      setShowBiometricSetup(true);
    }
  } catch (e: any) {
    toast$(e.message || 'Invalid Security OTP code', 'error');
  } finally {
    setFaceOtpLoading(false);
  }
};
const startRemotePhonePunch = async () => {
  setShowRemotePunchModal(true);
  setRemotePunchQR('');
  try {
    const q = outletId ? `?outlet_id=${outletId}` : '';
    const res: any = await api(`/employees/attendance/create-phone-punch-session${q}`, { method: 'POST' });
    if (!res?.session_id) {
      toast$('Could not start attendance punch session.', 'error');
      setShowRemotePunchModal(false);
      return;
    }

    const qr = await QRCode.toDataURL(`${window.location.origin}/remote-attendance?session=${res.session_id}`, {
      width: 220, margin: 1,
    });
    setRemotePunchQR(qr);

    if (remotePunchPollRef.current) clearInterval(remotePunchPollRef.current);
    remotePunchPollRef.current = setInterval(async () => {
      try {
        const check: any = await api(`/employees/attendance/remote-punch-session/${res.session_id}`);
        if (check?.status === 'completed' && check?.result) {
          clearInterval(remotePunchPollRef.current);
          remotePunchPollRef.current = null;
          setShowRemotePunchModal(false);
          setRemotePunchQR('');

          const r = check.result;
          const isGymMod = r.module === 'gym' || mainTab === 'gym';
          const icon = r.person_type === 'member' ? (isGymMod ? '🏋️' : '🍽️') : '👔';
          
          toast$(`${icon} ${r.name} Checked In! (${r.time}) 🎉`);
          postLog(
            mainTab === 'gym' ? 'GYM_FINGERPRINT_CHECKIN' : 'CANTEEN_FINGERPRINT_CHECKIN',
            `Phone punch: ${r.name} | ${r.time}`,
            outletId
          );
          loadSubMembers(currentModule, true);
        }
      } catch (e) {
        clearInterval(remotePunchPollRef.current);
      }
    }, 1500);
  } catch (e: any) {
    toast$(e.message || 'Failed to generate punch QR', 'error');
    setShowRemotePunchModal(false);
  }
};
const startFaceRegister = async (member: any) => {
  setFaceRegisterMember(member);
  setFaceRegisterStatus('loading');
  setShowFaceRegister(true);
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
    faceStreamRef.current = stream;
    setTimeout(() => {
      if (faceVideoRef.current) {
        faceVideoRef.current.srcObject = stream;
        setFaceRegisterStatus('capturing');
      }
    }, 300);
  } catch {
    setFaceRegisterStatus('error');
  }
};

const stopFaceStream = () => {
  clearInterval(faceIntervalRef.current);
  faceStreamRef.current?.getTracks().forEach(t => t.stop());
  faceStreamRef.current = null;
};

const captureFaceDescriptor = async () => {
  if (!faceVideoRef.current || !faceRegisterMember) return;
  setFaceRegisterStatus('loading');
  try {
    const video = faceVideoRef.current;
    const faceapi = (window as any).faceapi;

    // 1. Compute Web Face Descriptor (if face-api.js loaded)
    let descriptor: number[] | null = null;
    if (faceapi) {
      const detection = await faceapi
        .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();
      if (detection) descriptor = Array.from(detection.descriptor);
    }

    // 2. Capture clean canvas snapshot for mobile AI encoding
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const base64Image = canvas.toDataURL('image/jpeg', 0.8);

    // 3. Send BOTH to populate Web and Mobile database columns in 1 request!
    await api(`/subscriptions/members/${faceRegisterMember.id}/face`, {
      method: 'PATCH',
      body: JSON.stringify({
        face_descriptor: descriptor,
        image_base64: base64Image,
      }),
    });

    setFaceRegisterStatus('done');
    stopFaceStream();
    postLog(mainTab === 'gym' ? 'GYM_FACE_REGISTERED' : 'CANTEEN_FACE_REGISTERED', `Face registered: ${faceRegisterMember.name}`, outletId);
    toast$(`Face registered for ${faceRegisterMember.name}! 🎉`);
    loadSubMembers(currentModule);
  } catch (e: any) {
    const isDuplicate = e.message?.includes('already registered');
    toast$(isDuplicate ? e.message : 'Failed to capture face. Make sure your face is clearly visible.', 'error');
    setFaceRegisterStatus('capturing');
  }
};
const startFaceSetup = async () => {
  setFaceSetupStatus('loading');
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
    faceSetupStreamRef.current = stream;
    setTimeout(() => {
      if (faceSetupVideoRef.current) {
        faceSetupVideoRef.current.srcObject = stream;
        setFaceSetupStatus('capturing');
      }
    }, 300);
  } catch {
    setFaceSetupStatus('error');
  }
};

const stopFaceSetupStream = () => {
  faceSetupStreamRef.current?.getTracks().forEach(t => t.stop());
  faceSetupStreamRef.current = null;
};

const captureFaceSetup = async () => {
  if (!faceSetupVideoRef.current || !biometricMember) return;
  setFaceSetupStatus('loading');
  try {
    const video = faceSetupVideoRef.current;
    const faceapi = (window as any).faceapi;

    let descriptor: number[] | null = null;
    if (faceapi) {
      const detection = await faceapi
        .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();
      if (detection) descriptor = Array.from(detection.descriptor);
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const base64Image = canvas.toDataURL('image/jpeg', 0.8);

    await api(`/subscriptions/members/${biometricMember.id}/face`, {
      method: 'PATCH',
      body: JSON.stringify({
        face_descriptor: descriptor,
        image_base64: base64Image,
      }),
    });

    stopFaceSetupStream();
    setFaceSetupStatus('done');
    postLog(mainTab === 'gym' ? 'GYM_FACE_REGISTERED' : 'CANTEEN_FACE_REGISTERED', `Face registered: ${biometricMember.name}`, outletId);
    toast$('Face registered for Web & Mobile! ✅');
    loadSubMembers(currentModule);
  } catch (e: any) {
    const isDuplicate = e.message?.includes('already registered');
    toast$(isDuplicate ? e.message : 'Failed to capture face. Make sure your face is clearly visible.', 'error');
    setFaceSetupStatus('capturing');
  }
};
const scanFingerprint = async (slot: string) => {
  if (!window.PublicKeyCredential) {
    toast$('WebAuthn not supported on this device', 'error');
    return;
  }
  setFingerprintScanStatus('scanning');
  try {
    const challenge = new Uint8Array(32);
    crypto.getRandomValues(challenge);
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        userVerification: 'required',
        timeout: 60000,
        allowCredentials: [],
      },
    }) as PublicKeyCredential;
    const credentialId = btoa(String.fromCharCode(...new Uint8Array(assertion.rawId)));
    const res = await api<any>('/subscriptions/fingerprint-scan', {
      method: 'POST',
      body: JSON.stringify({ credential_id: credentialId, slot, module: currentModule }),
    });
    setFingerprintScanStatus('idle');
    if (res.status === 'success') {
      setScanResult(res);
      loadSubMembers(currentModule);
      postLog(mainTab === 'gym' ? 'GYM_FINGERPRINT_CHECKIN' : 'CANTEEN_FINGERPRINT_CHECKIN', `Fingerprint scan: ${res.name} | ${res.scan_type === 'in' ? 'Checked In' : 'Checked Out'} | ${res.slot} | ${res.time}`, outletId);
      toast$(`${res.scan_type === 'in' ? '✅ Checked In' : '🚪 Checked Out'} — ${res.name}`);
      const member = subMembers.find(m => m.name === res.name);
      if (member?.phone) {
        try {
          const isIn = res.scan_type === 'in';
          const templates = await loadMessageTemplates(outletId);
          const isCanteen = mainTab === 'canteen';
          const templateKey = isCanteen
            ? templates.canteen_slot_message
            : isIn ? templates.gym_checkin_message : templates.gym_checkout_message;
          const scanMsg = applyT(templateKey, {
            name: res.name, time: res.time, date: res.date, slot: res.slot,
          });
          await sendWhatsApp(member.phone, scanMsg, outletId);
        } catch (e: any) {
          toast$(`Checked in, but WhatsApp notification failed: ${e.message}`, 'error');
        }
      }
    } else {
      toast$(res.message || 'Not recognized', 'error');
    }
  } catch (e: any) {
    setFingerprintScanStatus('idle');
    if (e.name !== 'NotAllowedError') toast$('Fingerprint scan failed', 'error');
  }
};
const [manualFpInput, setManualFpInput] = useState('');
const [showManualFp, setShowManualFp] = useState(false);
const startPhoneEnrollment = async (member: any) => {
  try {
    const res: any = await api(`/subscriptions/members/${member.id}/create-phone-enroll-session`, { method: 'POST' });
    const enrollUrl = `${window.location.origin}/remote-enroll?token=${res.token}`;
    const qr = await QRCode.toDataURL(enrollUrl, { width: 220, margin: 1 });

    setPhoneEnrollQR(qr);
    setWaitingPhoneBio(true);

    if (phoneBioPollRef.current) clearInterval(phoneBioPollRef.current);
    phoneBioPollRef.current = setInterval(async () => {
      try {
        const check: any = await api(`/subscriptions/remote-enroll/${res.token}`);
        if (check.status === 'completed' || check.status === 'success') {
          clearInterval(phoneBioPollRef.current);
          phoneBioPollRef.current = null;
          
          // ⚡ Update state and member objects immediately
          setWaitingPhoneBio(false);
          setPhoneEnrollQR('');
          setFingerprintStatus('done');
          setBiometricMember((prev: any) => prev ? { ...prev, fingerprint_id: 'PHONE-BIO-OK', fingerprint_credential_id: 'enrolled' } : prev);
          setSubMembers((prev: any[]) => prev.map(m => m.id === member.id ? { ...m, fingerprint_id: 'PHONE-BIO-OK', fingerprint_credential_id: 'enrolled' } : m));
          
          toast$(`Fingerprint registered from phone for ${member.name}! 🎉`);
          await loadSubMembers(currentModule, true);
        }
      } catch (err) {
        clearInterval(phoneBioPollRef.current);
      }
    }, 1500);
  } catch (e: any) {
    toast$(e.message || 'Failed to start phone enrollment', 'error');
  }
};
const registerFingerprint = async () => {
  if (!biometricMember) return;

  // Check if browser supports WebAuthn platform biometrics
  const hasWebAuthn = !!window.PublicKeyCredential;
  let isPlatformAvailable = false;
  if (hasWebAuthn && PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
    try {
      isPlatformAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      isPlatformAvailable = false;
    }
  }

  // If on a desktop/OS with no built-in biometric sensor, show the USB / Hardware Scanner input directly
  if (!hasWebAuthn || !isPlatformAvailable) {
    setShowManualFp(true);
    setManualFpInput(biometricMember.fingerprint_id || '');
    return;
  }

  setFingerprintStatus('registering');
  try {
    const challenge = new Uint8Array(32);
    crypto.getRandomValues(challenge);
    const userId = new TextEncoder().encode(String(biometricMember.id));
    const credential = await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: { name: 'CloudAIRambo', id: window.location.hostname },
        user: {
          id: userId,
          name: biometricMember.phone,
          displayName: biometricMember.name,
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },   // ES256
          { type: 'public-key', alg: -257 },  // RS256
        ],
        authenticatorSelection: {
          userVerification: 'preferred', // Allows seamless fallback
        },
        timeout: 60000,
      },
    }) as PublicKeyCredential;

    if (!credential) throw new Error('No biometric device response');

    const response = credential.response as AuthenticatorAttestationResponse;
    const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
    const publicKey = btoa(String.fromCharCode(...new Uint8Array(response.getPublicKey?.() || new ArrayBuffer(0))));

    await api(`/subscriptions/members/${biometricMember.id}/fingerprint`, {
      method: 'PATCH',
      body: JSON.stringify({
        credential_id: credentialId,
        public_key: publicKey,
        sign_count: 0,
      }),
    });
    setFingerprintStatus('done');
    postLog(mainTab === 'gym' ? 'GYM_FINGERPRINT_REGISTERED' : 'CANTEEN_FINGERPRINT_REGISTERED', `Fingerprint registered: ${biometricMember.name}`, outletId);
    toast$('Fingerprint registered! ✅');
    loadSubMembers(currentModule);
  } catch (e: any) {
    // If WebAuthn fails on this machine, smoothly fall back to manual USB / Reader ID
    setShowManualFp(true);
    setManualFpInput(biometricMember.fingerprint_id || '');
    setFingerprintStatus('idle');
  }
};

const handleSaveManualFp = async () => {
  if (!biometricMember) return;
  try {
    await api(`/subscriptions/members/${biometricMember.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ fingerprint_id: manualFpInput.trim() || null }),
    });
    setFingerprintStatus('done');
    setShowManualFp(false);
    postLog(mainTab === 'gym' ? 'GYM_FINGERPRINT_REGISTERED' : 'CANTEEN_FINGERPRINT_REGISTERED', `Fingerprint ID linked: ${biometricMember.name} (#${manualFpInput})`, outletId);
    toast$('Biometric Scanner ID linked successfully! ✅');
    loadSubMembers(currentModule);
  } catch (e: any) {
    toast$(e.message || 'Failed to save biometric ID', 'error');
  }
};
// ── Face Scan Attendance ──────────────────────────────────────────────────
const startFaceScan = async (slot: string) => {
  setFaceScanSlot(slot);
  setFaceScanResult(null);
  setFaceScanStatus('scanning');
  setShowFaceScan(true);
  faceScanCancelledRef.current = false;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
    faceStreamRef.current = stream;
    setTimeout(() => {
      if (faceVideoRef.current) faceVideoRef.current.srcObject = stream;
    }, 300);

    const scanTick = async () => {
      if (faceScanCancelledRef.current) return;
      const v = faceVideoRef.current;
      if (!v || v.readyState < 2) {
        faceIntervalRef.current = setTimeout(scanTick, 800);
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(v, 0, 0);
      const base64Image = canvas.toDataURL('image/jpeg', 0.6);

      try {
        const qs = outletId ? `?outlet_id=${outletId}` : '';
        const result = await api<any>(`/subscriptions/face-scan-image${qs}`, {
          method: 'POST',
          body: JSON.stringify({ image: base64Image, slot, module: currentModule }),
        });

        faceScanCancelledRef.current = true;
        stopFaceStream();

        setFaceScanResult(result);
        setFaceScanStatus(
          result.status === 'success' ? 'match' :
          result.status === 'no_match' ? 'nomatch' :
          result.status
        );
        if (result.slot) setFaceScanSlot(result.slot);
        loadSubMembers(currentModule);
        if (result.status === 'success') {
          postLog(mainTab === 'gym' ? 'GYM_FACE_CHECKIN' : 'CANTEEN_FACE_CHECKIN', `Face scan: ${result.name} | ${result.scan_type === 'in' ? 'Checked In' : 'Checked Out'} | ${result.slot} | ${result.time}`, outletId);
          const member = subMembers.find(m => m.name === result.name);
          if (member?.phone) {
            try {
              const isIn = result.scan_type === 'in';
              const templates = await loadMessageTemplates(outletId);
              const isCanteen = mainTab === 'canteen';
              const templateKey = isCanteen
                ? templates.canteen_slot_message
                : isIn ? templates.gym_checkin_message : templates.gym_checkout_message;
              const scanMsg = applyT(templateKey, {
                name: result.name, time: result.time,
                date: result.date, slot: result.slot,
              });
              await sendWhatsApp(member.phone, scanMsg, outletId);
            } catch (e: any) {
              toast$(`Checked in, but WhatsApp notification failed: ${e.message}`, 'error');
            }
          }
        }
      } catch (e: any) {
        if (faceScanCancelledRef.current) return;
        faceScanCancelledRef.current = true;
        stopFaceStream();
        setFaceScanResult({ status: 'error', message: e.message });
        setFaceScanStatus('error');
      }
    };

    scanTick();
  } catch {
    setFaceScanStatus('error');
    setFaceScanResult({ status: 'error', message: 'Camera access denied' });
  }
};
const toggleAutoReport = (memberId: number) => {
  const member = subMembers.find(m => m.id === memberId);
  setAutoReportMembers(prev => {
    const next = new Set(prev);
    if (next.has(memberId)) {
      next.delete(memberId);
     postLog(mainTab === 'gym' ? 'GYM_AUTO_REPORT_DISABLED' : 'CANTEEN_AUTO_REPORT_DISABLED', `Auto report disabled: ${member?.name || memberId}`, outletId);
      toast$('Auto report disabled', 'info');
    } else {
      next.add(memberId);
     postLog(mainTab === 'gym' ? 'GYM_AUTO_REPORT_ENABLED' : 'CANTEEN_AUTO_REPORT_ENABLED', `Auto report enabled: ${member?.name || memberId}`, outletId);
      toast$('Auto report enabled — sends every Sunday 🗓️');
    }
    return next;
  });
};
const [editMemberPhoneExists, setEditMemberPhoneExists] = useState(false);
const [editMemberEmailExists, setEditMemberEmailExists] = useState(false);

useEffect(() => {
  const digits = editPhone.replace(/\D/g, '');
  if (digits.length < 7 || !editMember) { setEditMemberPhoneExists(false); return; }
  setEditMemberPhoneExists(!!subMembers.find(m =>
    m.id !== editMember.id && m.phone.replace(/\D/g, '').slice(-10) === digits.slice(-10)
  ));
}, [editPhone, subMembers, editMember]);

// Auto-fill name/email in the Edit Member form when phone matches a saved account (App Parity)
useEffect(() => {
  const digits = editPhone.replace(/\D/g, '');
  if (digits.length < 7 || !editMember) return;
  const match = accounts.find(a => a.phone?.replace(/\D/g, '').slice(-10) === digits.slice(-10));
  if (match) {
    if (!editName.trim()) setEditName(match.name || '');
    if (!editEmail.trim() && match.email) setEditEmail(match.email);
  }
}, [editPhone, accounts, editMember]);

// Auto-select single slot if only one slot is available (App Parity)
useEffect(() => {
  if (currentSlots.length === 1 && editMember && editSlots.length === 0) {
    setEditSlots([currentSlots[0]]);
  }
}, [currentSlots, editMember, editSlots.length]);

useEffect(() => {
  if (!editEmail.trim() || !editEmail.includes('@') || !editMember) { setEditMemberEmailExists(false); return; }
  setEditMemberEmailExists(!!subMembers.find(m =>
    m.id !== editMember.id && m.email?.toLowerCase() === editEmail.trim().toLowerCase()
  ));
}, [editEmail, subMembers, editMember]);

const openEditMember = (m: any) => {
  setEditMember(m);
  setEditName(m.name);
  setEditPhone(m.phone);
  setEditEmail(m.email || '');
  setEditPlan(m.plan_name);
  setEditSlots(m.slots);
  setEditStart(m.start_date);
  setEditEnd(m.end_date);
};

const handleSaveEdit = async () => {
  if (!editMember || !editName.trim() || !editPhone.trim()) return;
  const cleanPhone = getCleanE164Phone(editPhone);
  if (!cleanPhone) {
    toast$('Please enter a valid phone number (between 7 and 15 digits)', 'error');
    return;
  }
  const cleanDigits = cleanPhone.replace(/\D/g, '');
  const existingDigits = (editMember.phone || '').replace(/\D/g, '');
  const phoneChanged = cleanDigits.slice(-10) !== existingDigits.slice(-10);

  if (phoneChanged) {
    setSendingEditOtp(true);
    try {
      const res = await api<any>('/subscriptions/members/send-precreate-otp', {
        method: 'POST',
        body: JSON.stringify({ phone: cleanPhone, name: editName }),
      });
      await sendWhatsApp(cleanPhone,
        `🔐 *Verification OTP*\n\nHi ${editName}! Your OTP to verify your new number is: *${res.otp}*\n\nValid for 10 minutes.\n— ${companyName}`,
        outletId
      );
      setPendingEditData({ name: editName, phone: cleanPhone, email: editEmail, plan_name: editPlan, slots: editSlots, start_date: editStart, end_date: editEnd });
      setEditPhoneOtp('');
      setShowEditPhoneOtp(true);
      toast$('OTP sent to new number 📲');
    } catch (e: any) { setError(e.message); }
    finally { setSendingEditOtp(false); }
    return;
  }
  setSavingEdit(true);
  try {
    await api(`/subscriptions/members/${editMember.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: editName,
        phone: cleanPhone,
        email: editEmail || undefined,
        plan_name: editPlan,
        slots: editSlots,
        start_date: editStart,
        end_date: editEnd,
      }),
    });
    postLog(mainTab === 'gym' ? 'GYM_MEMBER_UPDATED' : 'CANTEEN_MEMBER_UPDATED', `Member updated: ${editName} | ${editPlan} | ${editSlots.join(', ')} | ${editStart} → ${editEnd}`, outletId);
    toast$('Member updated!');
    setEditMember(null);
    loadSubMembers(currentModule);
  } catch (e: any) { setError(e.message); }
  finally { setSavingEdit(false); }
};

const handleVerifyEditPhoneOtp = async () => {
  if (!editMember || !pendingEditData) return;
  setVerifyingEditOtp(true);
  try {
    await api('/subscriptions/members/verify-precreate-otp', {
      method: 'POST',
      body: JSON.stringify({ phone: pendingEditData.phone, otp: editPhoneOtp }),
    });
    setSavingEdit(true);
    await api(`/subscriptions/members/${editMember.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: pendingEditData.name, phone: pendingEditData.phone,
        email: pendingEditData.email || undefined,
        plan_name: pendingEditData.plan_name, slots: pendingEditData.slots,
        start_date: pendingEditData.start_date, end_date: pendingEditData.end_date,
      }),
    });
    postLog(mainTab === 'gym' ? 'GYM_MEMBER_UPDATED' : 'CANTEEN_MEMBER_UPDATED', `Member updated with new phone: ${pendingEditData.name} | ${pendingEditData.phone}`, outletId);
    toast$('Member updated! ✅');
    setShowEditPhoneOtp(false);
    setEditMember(null);
    setPendingEditData(null);
    loadSubMembers(currentModule);
  } catch (e: any) { setError(e.message); }
  finally { setVerifyingEditOtp(false); setSavingEdit(false); }
};

const handleSendOtp = async (m: any) => {
  setSendingOtp(true);
  try {
    const res = await api<any>(`/subscriptions/members/${m.id}/send-otp`, { method: 'POST' });
    await sendWhatsApp(m.phone,
      `🔐 *Verification OTP*\n\nHi ${m.name}! Your verification code is: *${res.otp}*\n\nValid for 10 minutes.\n— ${companyName}`,
      outletId
    );
    setOtpMember(m);
    setOtpValue('');
    toast$('OTP sent to ' + m.phone);
  } catch (e: any) { setError(e.message); }
  finally { setSendingOtp(false); }
};

const handleVerifyOtp = async () => {
  if (!otpMember) return;
  setVerifyingOtp(true);
  try {
    await api(`/subscriptions/members/${otpMember.id}/verify-otp`, {
      method: 'POST',
      body: JSON.stringify({ otp: otpValue }),
    });
    toast$('Member verified! ✅');
    setOtpMember(null);
    setOtpValue('');
    loadSubMembers(currentModule);
  } catch (e: any) { setError(e.message); }
  finally { setVerifyingOtp(false); }
};
// Auto-send reports every Sunday at 9am
useEffect(() => {
  if (autoReportMembers.size === 0) return;
  const interval = setInterval(async () => {
    const now = new Date();
    const isSunday = now.getDay() === 0;
    const isNineAM = now.getHours() === 9 && now.getMinutes() === 0;
    if (!isSunday || !isNineAM) return;
    for (const memberId of autoReportMembers) {
      const member = subMembers.find(m => m.id === memberId);
      if (member) await sendWeeklyReport(member);
    }
  }, 60000); // checks every minute
  return () => clearInterval(interval);
}, [autoReportMembers, subMembers]);
const stopScanner = () => {
  clearInterval(scanInterval.current);
  streamRef.current?.getTracks().forEach(t => t.stop());
  streamRef.current = null;
};

// At the very top of return, before the existing ledger list view
if (mainTab !== 'ledger') {
  const isGym = mainTab === 'gym';
  const color = isGym ? '#7c3aed' : '#065f46';
  const bgColor = isGym ? '#f5f3ff' : '#f0fdf4';
  const borderColor = isGym ? '#ddd6fe' : '#bbf7d0';
  const icon = isGym ? '🏋️' : '🍽️';
  const title = isGym ? 'Fitness Members' : 'Food & Dining Members';

  return (
    <div style={{ 
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', 
      background: '#f1f5f9', 
      height: '100vh', 
      overflowY: 'auto', 
      overscrollBehavior: 'contain', 
      paddingBottom: 80 
    }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '14px 20px', position: 'sticky', top: 0, zIndex: 30 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' as const }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, color: '#0f172a' }}>{icon} {title}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginTop: 1 }}>{subMembers.length} active members</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
            {(isGym ? perms.canGymViewStatus : perms.canFoodViewStatus) && (
              <button onClick={() => setShowStatusDash(true)} style={{
                height: 36, background: '#fff7ed', border: '1.5px solid #fed7aa', color: '#c2410c',
                borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>📊 Status</button>
            )}
            <button onClick={() => setShowTodayAttendance(true)} style={{
              height: 36, background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#15803d',
              borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>Attendance</button>
            {(isGym ? perms.canGymManageKiosk : perms.canFoodManageKiosk) && (
              <button onClick={() => setShowKioskSetup(true)} style={{
                height: 36, background: '#eff6ff', border: '1.5px solid #bfdbfe', color: '#1d4ed8',
                borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>📺 Kiosk</button>
            )}
            {/* Counter Display Button */}
            <button onClick={openCounterDisplay} style={{
              height: 36, background: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#0f172a',
              borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>🖥️ Display</button>
            {(isGym ? perms.canGymManagePlans : perms.canFoodManagePlans) && (
              <button onClick={() => setShowPlansManager(true)} style={{
                height: 36, background: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#475569',
                borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>⚙️ Plans</button>
            )}
            {isOwnerOrAdmin && (
              <button onClick={() => { setShowModuleSettings(true); fetchModuleSettings(currentModule); }} style={{
                height: 36, background: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#475569',
                borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>🔒 Scan Settings</button>
            )}
            {(isGym ? perms.canGymAddMember : perms.canFoodAddMember) && (
              <button onClick={() => setShowNewMember(true)} style={{
                height: 36, background: color, color: '#fff', border: 'none', borderRadius: 10,
                padding: '0 18px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
                boxShadow: `0 4px 14px ${color}40`,
              }}>
                + Add Member
              </button>
            )}
          </div>
        </div>
      </div>



      {/* Scan Buttons — one row always, slot resolved internally */}
      <div style={{ margin: '16px 16px 0' }}>
        <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', marginBottom: 10 }}>📷 Scan to Mark Attendance</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(isGym ? perms.canGymCheckInQR : perms.canFoodCheckInQR) && (
            <button onClick={() => startScanner('__auto__')} style={{
              height: 36, background: bgColor, border: `1.5px solid ${borderColor}`, color, borderRadius: 10,
              padding: '0 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>📷 Scan QR</button>
          )}
          {(isGym ? perms.canGymCheckInFace : perms.canFoodCheckInFace) && (
            <button onClick={() => startFaceScan('__auto__')} style={{
              height: 36, background: '#fdf4ff', border: '1.5px solid #e9d5ff', color: '#7c3aed',
              borderRadius: 10, padding: '0 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>👁️ Face</button>
          )}
        </div>
      </div>

      {/* Member List */}
      <div style={{ margin: '16px 16px 0' }}>
        <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', marginBottom: 10 }}>👥 All Members</div>
        {subMembers.length === 0 && !subLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>{icon}</div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>No members yet</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Add your first member above</div>
          </div>
        )}
        {subMembers.map(m => {
          const now2 = new Date();
          const today2 = `${now2.getFullYear()}-${String(now2.getMonth() + 1).padStart(2, '0')}-${String(now2.getDate()).padStart(2, '0')}`;
          const expired = today2 > m.end_date;
          return (
            <div key={m.id} style={{ background: '#fff', borderRadius: 14, padding: '14px', marginBottom: 10, border: `1px solid ${expired ? '#fecdd3' : '#f1f5f9'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Line 1: Name — Bigger & Jet Black */}
                  <div style={{ fontWeight: 900, fontSize: 18, color: '#0f172a', letterSpacing: '-0.02em', textTransform: 'capitalize' }}>
                    {m.name}
                  </div>

                  {/* Line 2: Phone & Email — Bold Dark Slate (No more washed-out light gray) */}
                  <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 800, marginTop: 3 }}>
                    <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>{m.phone}</span>
                    {m.email && <span style={{ color: '#475569', fontWeight: 600 }}> · {m.email}</span>}
                  </div>

                  {/* Line 3: Plan Badge — Larger, Crisp Border, High Contrast */}
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ 
                      background: bgColor, color: color, border: `1.5px solid ${borderColor}`, 
                      borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 800 
                    }}>
                      {m.plan_name}
                    </span>
                    {m.slots.map((s: string) => (
                      <span key={s} style={{ 
                        background: '#f8fafc', color: '#1e293b', border: '1.5px solid #cbd5e1', 
                        borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 700 
                      }}>
                        {s}
                      </span>
                    ))}
                    {expired && (
                      <span style={{ 
                        background: '#fef2f2', color: '#dc2626', border: '1.5px solid #fecaca', 
                        borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 900 
                      }}>
                        EXPIRED
                      </span>
                    )}
                  </div>

                  {/* Line 4: Dates — Clear, Dark, Visible Numbers */}
                  <div style={{ fontSize: 13, color: '#334155', fontWeight: 800, marginTop: 6, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                    <span>{m.start_date}</span>
                    <span style={{ color: '#94a3b8', margin: '0 6px', fontWeight: 900 }}>→</span>
                    <span style={{ color: expired ? '#dc2626' : '#059669', fontWeight: 900 }}>{m.end_date}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexShrink: 0, flexWrap: 'nowrap', overflowX: 'auto', padding: '2px 0' }}>
                  {/* Walk-In Continuous Renewal */}
                  <button 
                    onClick={() => openRenewModal(m)} 
                    style={{ 
                      height: 36, display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: '#ecfdf5', border: '1.5px solid #34d399', color: '#047857', 
                      borderRadius: 10, padding: '0 14px', fontSize: 12, fontWeight: 800, 
                      cursor: 'pointer', boxShadow: '0 1px 3px rgba(16,185,129,0.12)', transition: 'all 0.15s' 
                    }}>
                    🔄 Renew
                  </button>

                  {/* Instant Scannable QR / Pay Popup */}
                  <button 
                    onClick={() => openMemberPayQr(m)} 
                    title="Collect Payment & Show QR" 
                    style={{ 
                      height: 36, display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: '#eff6ff', border: '1.5px solid #93c5fd', color: '#1d4ed8', 
                      borderRadius: 10, padding: '0 12px', fontSize: 12, fontWeight: 800, 
                      cursor: 'pointer', boxShadow: '0 1px 3px rgba(59,130,246,0.12)', transition: 'all 0.15s' 
                    }}>
                    💳 Pay
                  </button>

                  {/* View Member QR Badge */}
                  {(isGym ? perms.canGymViewBadge : perms.canFoodViewBadge) && (
                    <button 
                      onClick={() => openMemberQR(m)} 
                      style={{ 
                        height: 36, display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: bgColor, border: `1.5px solid ${borderColor}`, color: color, 
                        borderRadius: 10, padding: '0 12px', fontSize: 12, fontWeight: 800, 
                        cursor: 'pointer', transition: 'all 0.15s' 
                      }}>
                      📷 QR
                    </button>
                  )}

                  {/* Attendance History */}
                  <button 
                    onClick={() => openMemberHistory(m)} 
                    title="View Attendance Scan History" 
                    style={{ 
                      height: 36, width: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: '#f8fafc', border: '1.5px solid #cbd5e1', color: '#334155', 
                      borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer', transition: 'all 0.15s' 
                    }}>
                    🕐
                  </button>

                  {/* Face ID Registration (Protected by OTP) */}
                  {(isGym ? perms.canGymRegisterFace : perms.canFoodRegisterFace) && (
                    <button 
                      onClick={() => handleBiometricAction(m, 'face')} 
                      title={(m.face_descriptor || m.face_image_encoding) ? 'Face registered ✓ (Click to re-enroll with OTP)' : 'Register Face for Auto Scan'} 
                      style={{ 
                        height: 36, width: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: (m.face_descriptor || m.face_image_encoding) ? '#f3e8ff' : '#f8fafc', 
                        border: `1.5px solid ${(m.face_descriptor || m.face_image_encoding) ? '#c084fc' : '#cbd5e1'}`, 
                        color: (m.face_descriptor || m.face_image_encoding) ? '#7e22ce' : '#64748b', 
                        borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer', transition: 'all 0.15s' 
                      }}>
                      {(m.face_descriptor || m.face_image_encoding) ? '👁️✓' : '📸'}
                    </button>
                  )}

                  {/* Fingerprint Biometric Setup (Protected by OTP) */}
                  {(isGym ? perms.canGymCheckInFinger : perms.canFoodCheckInFinger) && (
                    <button 
                      onClick={() => handleBiometricAction(m, 'fingerprint')} 
                      title={(m.fingerprint_id || m.fingerprint_credential_id) ? 'Fingerprint registered ✓ (Click to update with OTP)' : 'Set up Fingerprint / Biometrics'} 
                      style={{ 
                        height: 36, width: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: (m.fingerprint_id || m.fingerprint_credential_id) ? '#ecfdf5' : '#f8fafc', 
                        border: `1.5px solid ${(m.fingerprint_id || m.fingerprint_credential_id) ? '#86efac' : '#cbd5e1'}`, 
                        color: (m.fingerprint_id || m.fingerprint_credential_id) ? '#047857' : '#64748b', 
                        borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer', transition: 'all 0.15s' 
                      }}>
                      {(m.fingerprint_id || m.fingerprint_credential_id) ? '👆✓' : '👆'}
                    </button>
                  )}

                  {/* Edit Member Details */}
                  {(isGym ? perms.canGymEditMember : perms.canFoodEditMember) && (
                    <button 
                      onClick={() => openEditMember(m)} 
                      style={{ 
                        height: 36, display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: '#f8fafc', border: '1.5px solid #cbd5e1', color: '#1e293b', 
                        borderRadius: 10, padding: '0 12px', fontSize: 12, fontWeight: 800, 
                        cursor: 'pointer', transition: 'all 0.15s' 
                      }}>
                      ✏️ Edit
                    </button>
                  )}

                  {/* OTP Verification */}
                  {(isGym ? perms.canGymVerifyOtp : perms.canFoodVerifyOtp) && (
                    <button 
                      onClick={() => handleSendOtp(m)} 
                      title={m.verified ? 'Phone Verified ✓' : 'Send WhatsApp OTP to Verify'} 
                      style={{ 
                        height: 36, width: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: m.verified ? '#f0fdf4' : '#fffbeb', 
                        border: `1.5px solid ${m.verified ? '#86efac' : '#fde68a'}`, 
                        color: m.verified ? '#15803d' : '#d97706', 
                        borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer', transition: 'all 0.15s' 
                      }}>
                      {m.verified ? '✓' : '🔐'}
                    </button>
                  )}

                  {/* Send Report */}
                  {(isGym ? perms.canGymSendReports : perms.canFoodSendReports) && (
                    <button 
                      onClick={() => sendWeeklyReport(m)} 
                      title="Send Weekly Attendance Report" 
                      style={{ 
                        height: 36, width: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: '#eff6ff', border: '1.5px solid #93c5fd', color: '#1d4ed8', 
                        borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer', transition: 'all 0.15s' 
                      }}>
                      📊
                    </button>
                  )}

                  {/* Auto Weekly Report Toggle */}
                  {(isGym ? perms.canGymSendReports : perms.canFoodSendReports) && (
                    <button 
                      onClick={() => toggleAutoReport(m.id)} 
                      title={autoReportMembers.has(m.id) ? 'Auto-report is Active (Sundays)' : 'Enable Weekly Auto-report'} 
                      style={{ 
                        height: 36, display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: autoReportMembers.has(m.id) ? '#fef9c3' : '#f8fafc', 
                        border: `1.5px solid ${autoReportMembers.has(m.id) ? '#facc15' : '#cbd5e1'}`, 
                        color: autoReportMembers.has(m.id) ? '#854d0e' : '#64748b', 
                        borderRadius: 10, padding: '0 11px', fontSize: 12, fontWeight: 800, 
                        cursor: 'pointer', transition: 'all 0.15s' 
                      }}>
                      {autoReportMembers.has(m.id) ? '🔔 Auto' : '🔕 Auto'}
                    </button>
                  )}

                  {/* Delete Member */}
                  {(isGym ? perms.canGymDeleteMember : perms.canFoodDeleteMember) && (
                    <button 
                      onClick={() => handleDeleteMember(m.id)} 
                      style={{ 
                        height: 36, display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: '#fef2f2', border: '1.5px solid #fca5a5', color: '#dc2626', 
                        borderRadius: 10, padding: '0 12px', fontSize: 12, fontWeight: 800, 
                        cursor: 'pointer', transition: 'all 0.15s' 
                      }}>
                      🗑 Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

{/* Today's Attendance Modal */}
{showTodayAttendance && (
  <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowTodayAttendance(false)}>
    <div style={{ ...sheetStyle, maxWidth: 500 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>📅 Today's Attendance</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{todayBoard.length} checked in today</div>
        </div>
        <button onClick={() => setShowTodayAttendance(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
      </div>
      {subLoading ? (
        <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>Loading...</div>
      ) : todayBoard.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>No check-ins yet today</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {todayBoard.map((m: any) => (
            <div key={m.id} style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', border: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{m.name}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{m.plan_name}</div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {m.slots.map((s: any) => (
                  <div key={s.slot} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: s.attended ? '#f0fdf4' : '#fff1f2',
                    border: `1px solid ${s.attended ? '#bbf7d0' : '#fecdd3'}`,
                    borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700,
                    color: s.attended ? '#15803d' : '#be123c',
                  }}>
                    {s.attended ? '✅' : '❌'} {s.slot}{s.scanned_at ? ` · ${new Date(s.scanned_at.endsWith('Z') || s.scanned_at.includes('+') ? s.scanned_at : s.scanned_at + 'Z').toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
)}
{/* Status Dashboard Modal */}
{showStatusDash && (() => {
  const fmtDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const today = fmtDate(new Date());

  const in3 = new Date(); in3.setDate(in3.getDate() + 3);
  const in3str = fmtDate(in3);

  const overdue2 = new Date(); overdue2.setDate(overdue2.getDate() - 2);
  const overdue2str = fmtDate(overdue2);

  const dueIn3Days = subMembers.filter(m => m.end_date > today && m.end_date <= in3str);
  const dueToday = subMembers.filter(m => m.end_date === today);
  const overdue2Days = subMembers.filter(m => m.end_date < today && m.end_date >= overdue2str);
  const activeMembers = subMembers.filter(m => m.end_date > in3str);

  return (
    <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowStatusDash(false)}>
      <div style={{ ...sheetStyle, maxWidth: 500 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>📊 Member Status</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{subMembers.length} total members</div>
          </div>
          <button onClick={() => setShowStatusDash(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>

        {/* 3-Stage Expiry Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
          <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 14, padding: '14px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: '#b45309' }}>{dueIn3Days.length}</div>
            <div style={{ fontSize: 10, color: '#b45309', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>IN 3 DAYS</div>
          </div>
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 14, padding: '14px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: '#c2410c' }}>{dueToday.length}</div>
            <div style={{ fontSize: 10, color: '#c2410c', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>TODAY</div>
          </div>
          <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 14, padding: '14px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: '#be123c' }}>{overdue2Days.length}</div>
            <div style={{ fontSize: 10, color: '#be123c', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>OVERDUE (2D)</div>
          </div>
        </div>

        {/* 3 Days Before Expiry Section */}
        {dueIn3Days.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                🟡 Expiring in 1–3 Days ({dueIn3Days.length})
              </div>
              {(isGym ? perms.canGymSendReports : perms.canFoodSendReports) && (
                <button
                  type="button"
                  disabled={sendingReminders}
                  onClick={async () => {
                    setSendingReminders(true); setRemindersDone(0);
                    for (const m of dueIn3Days) {
                      await handleSendMemberPayLink(m, undefined, '3days');
                      setRemindersDone(d => d + 1);
                      await new Promise(r => setTimeout(r, 600));
                    }
                    setSendingReminders(false);
                    toast$(`Reminders sent to ${dueIn3Days.length} members! 📲`);
                  }}
                  style={{ background: '#fefce8', border: '1px solid #fde68a', color: '#b45309', borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: sendingReminders ? 0.6 : 1 }}
                >
                  {sendingReminders ? `Sending ${remindersDone}/${dueIn3Days.length}...` : '📲 Remind All 3D'}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {dueIn3Days.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fefce8', border: '1px solid #fde68a', borderRadius: 12, padding: '10px 14px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{m.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{m.plan_name} · expires {m.end_date}</div>
                  </div>
                  {(isGym ? perms.canGymSendReports : perms.canFoodSendReports) && (
                    <button
                      type="button"
                      onClick={() => handleSendMemberPayLink(m, undefined, '3days')}
                      style={{ background: '#fff', border: '1px solid #fde68a', color: '#b45309', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                    >
                      📲 Send
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Expiring Today Section */}
        {dueToday.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#c2410c', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                🟠 Expiring Today ({dueToday.length})
              </div>
              {(isGym ? perms.canGymSendReports : perms.canFoodSendReports) && (
                <button
                  type="button"
                  disabled={sendingReminders}
                  onClick={async () => {
                    setSendingReminders(true); setRemindersDone(0);
                    for (const m of dueToday) {
                      await handleSendMemberPayLink(m, undefined, 'today');
                      setRemindersDone(d => d + 1);
                      await new Promise(r => setTimeout(r, 600));
                    }
                    setSendingReminders(false);
                    toast$(`Reminders sent to ${dueToday.length} members! 📲`);
                  }}
                  style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c', borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: sendingReminders ? 0.6 : 1 }}
                >
                  {sendingReminders ? `Sending ${remindersDone}/${dueToday.length}...` : '📲 Remind Today'}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {dueToday.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: '10px 14px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{m.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{m.plan_name} · ends today</div>
                  </div>
                  {(isGym ? perms.canGymSendReports : perms.canFoodSendReports) && (
                    <button
                      type="button"
                      onClick={() => handleSendMemberPayLink(m, undefined, 'today')}
                      style={{ background: '#fff', border: '1px solid #fed7aa', color: '#c2410c', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                    >
                      📲 Send
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Overdue 2 Days Section */}
        {overdue2Days.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#be123c', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                🔴 Overdue 2 Days ({overdue2Days.length})
              </div>
              {(isGym ? perms.canGymSendReports : perms.canFoodSendReports) && (
                <button
                  type="button"
                  disabled={sendingReminders}
                  onClick={async () => {
                    setSendingReminders(true); setRemindersDone(0);
                    for (const m of overdue2Days) {
                      await handleSendMemberPayLink(m, undefined, 'overdue2d');
                      setRemindersDone(d => d + 1);
                      await new Promise(r => setTimeout(r, 600));
                    }
                    setSendingReminders(false);
                    toast$(`Reminders sent to ${overdue2Days.length} members! 📲`);
                  }}
                  style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#be123c', borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: sendingReminders ? 0.6 : 1 }}
                >
                  {sendingReminders ? `Sending ${remindersDone}/${overdue2Days.length}...` : '📲 Remind Overdue'}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {overdue2Days.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 12, padding: '10px 14px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{m.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{m.plan_name} · expired {m.end_date}</div>
                  </div>
                  {(isGym ? perms.canGymSendReports : perms.canFoodSendReports) && (
                    <button
                      type="button"
                      onClick={() => handleSendMemberPayLink(m, undefined, 'overdue2d')}
                      style={{ background: '#fff', border: '1px solid #fecdd3', color: '#be123c', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                    >
                      📲 Send
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Members Section */}
        {activeMembers.length > 0 && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              🟢 Active ({activeMembers.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {activeMembers.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '10px 14px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{m.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{m.plan_name} · valid till {m.end_date}</div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d' }}>✓ Active</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {subMembers.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>No members yet</div>
        )}
      </div>
    </div>
  );
})()}
{/* Kiosk Setup Modal */}
{showKioskSetup && (() => {
  const kioskUrl = isGym
    ? `${window.location.origin}/kiosk/gym?key=${deviceApiKey}&slot=Gym&module=gym`
    : `${window.location.origin}/kiosk/gym?key=${deviceApiKey}&slot=Canteen&module=canteen`;

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast$('Copied to clipboard! 📋');
  };

  return (
    <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowKioskSetup(false)}>
      <div style={sheetStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>📺 Kiosk Setup</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Set up a self check-in screen at your entrance</div>
          </div>
          <button onClick={() => setShowKioskSetup(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>

        {!deviceApiKey ? (
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: '14px', fontSize: 13, color: '#c2410c', fontWeight: 600 }}>
            {outletId ? '⚠️ No device key set up yet for this outlet.' : '⚠️ Select a branch from the top navbar before generating a kiosk key.'}
           <button onClick={async () => {
  if (!outletId) {
    toast$('Select a branch from the top navbar first — kiosk keys must be tied to a specific outlet.', 'error');
    return;
  }
  try {
    const res = await api<{ api_key: string }>('/subscriptions/kiosk-devices/generate-key', {
      method: 'POST',
      body: JSON.stringify({ outlet_id: outletId, module: currentModule }),
    });
    setDeviceApiKey(res.api_key);
    postLog(mainTab === 'gym' ? 'GYM_KIOSK_KEY_GENERATED' : 'CANTEEN_KIOSK_KEY_GENERATED', `Kiosk device key generated for ${mainTab} (outlet ${outletId})`, outletId);
    toast$('Device key generated! ✅');
  } catch (e: any) { toast$(e.message || 'Failed to generate key', 'error'); }
}} disabled={!outletId} style={{ marginTop: 10, display: 'block', background: outletId ? '#c2410c' : '#e2e8f0', color: outletId ? '#fff' : '#94a3b8', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: outletId ? 'pointer' : 'not-allowed' }}>
  {outletId ? 'Generate Key' : 'Select a branch first'}
</button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Your Kiosk URL ({isGym ? 'Gym' : 'Canteen'})</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input readOnly value={kioskUrl} style={{ ...inputStyle, flex: 1, fontSize: 11, color: '#475569' }} />
                <button onClick={() => copy(kioskUrl)} style={{ background: color, color: '#fff', border: 'none', borderRadius: 10, padding: '0 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>📋 Copy</button>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>How to set it up</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  ['1', 'On a tablet, phone, or laptop, open a browser and paste the link above.'],
                  ['2', 'Allow camera access when prompted — this lets the kiosk recognize members\' faces.'],
                  ['3', 'Place the device at your entrance, facing where people stand to check in.'],
                  ['4', 'Keep the browser fullscreen and the screen always on for best results.'],
                  ['5', `Members just walk up — the camera detects their face and shows ${isGym ? 'check-in/check-out' : 'meal'} confirmation automatically.`],
                ].map(([num, text]) => (
                  <div key={num} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: bgColor, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{num}</div>
                    <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{text}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '12px 14px', fontSize: 12, color: '#15803d', fontWeight: 600 }}>
              💡 Tip: Make sure each member has registered their face first (📸 icon next to their name in the member list).
            </div>
          </>
        )}
      </div>
    </div>
  );
})()}
{/* Plans Manager Modal */}
      {showPlansManager && (
        <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowPlansManager(false)}>
          <div style={sheetStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>⚙️ Membership Plans</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Create plans for {isGym ? 'Fitness' : 'Food & Dining'}</div>
              </div>
              <button onClick={() => setShowPlansManager(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>

            {/* Share Catalog to Lead Banner */}
            {membershipPlans.length > 0 && (
              <div style={{
                background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 14,
                padding: 12, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#c2410c' }}>📢 Share Pricing Menu to a Lead</div>
                  <div style={{ fontSize: 11, color: '#9a3412', marginTop: 2 }}>Send your full list of plans to prospective customers on WhatsApp</div>
                </div>
                <button onClick={() => setShowShareCatalogModal(true)} style={{
                  background: '#ea580c', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '6px 14px', fontSize: 11, fontWeight: 800, cursor: 'pointer',
                }}>📲 Send</button>
              </div>
            )}

            {/* Existing plans */}
            {membershipPlans.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Your Plans</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {membershipPlans.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 14px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{p.name}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{currSym}{p.price} · {p.duration_days} days · {p.slots.join(', ')}</div>
                        {p.description && <div style={{ fontSize: 11, color: '#6366f1', marginTop: 3, fontStyle: 'italic' }}>{p.description}</div>}
                      </div>
                      <button onClick={() => handleDeletePlan(p.id)} style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>🗑 Delete</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
{/* Slot Manager */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Manage Slots</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {orgSlots.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 20, padding: '4px 6px 4px 10px' }}>
  <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{s.name}</span>
  <button onClick={async () => {
    await api(`/subscriptions/slots/${s.id}?outlet_id=${outletId || ''}`, { method: 'DELETE' });
    postLog(mainTab === 'gym' ? 'GYM_SLOT_DELETED' : 'CANTEEN_SLOT_DELETED', `Slot removed: ${s.name}`, outletId);
    loadSlots(currentModule);
    toast$('Slot removed', 'info');
  }} style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 14, padding: '2px 8px', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>🗑 Delete</button>
</div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...inputStyle, flex: 1 }} placeholder="Add slot e.g. Swimming, Evening Session..."
                  value={newSlotName} onChange={e => setNewSlotName(e.target.value)}
                  onKeyDown={async e => {
                    if (e.key === 'Enter' && newSlotName.trim()) {
                      setAddingSlot(true);
                      try {
                        await api('/subscriptions/slots', { method: 'POST', body: JSON.stringify({ module: currentModule, name: newSlotName.trim(), outlet_id: outletId || undefined }) });
                        postLog(mainTab === 'gym' ? 'GYM_SLOT_ADDED' : 'CANTEEN_SLOT_ADDED', `Slot added: ${newSlotName.trim()}`, outletId);
                        setNewSlotName('');
                        loadSlots(currentModule);
                        toast$('Slot added!');
                      } catch (e: any) { toast$(e.message, 'error'); }
                      finally { setAddingSlot(false); }
                    }
                  }} />
                <button disabled={addingSlot || !newSlotName.trim()} onClick={async () => {
                  if (!newSlotName.trim()) return;
                  setAddingSlot(true);
                  try {
                    await api('/subscriptions/slots', { method: 'POST', body: JSON.stringify({ module: currentModule, name: newSlotName.trim(), outlet_id: outletId || undefined }) });
                    postLog(mainTab === 'gym' ? 'GYM_SLOT_ADDED' : 'CANTEEN_SLOT_ADDED', `Slot added: ${newSlotName.trim()}`, outletId);
                    setNewSlotName('');
                    loadSlots(currentModule);
                    toast$('Slot added!');
                  } catch (e: any) { toast$(e.message, 'error'); }
                  finally { setAddingSlot(false); }
                }} style={{ background: color, color: '#fff', border: 'none', borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: !newSlotName.trim() ? 0.5 : 1 }}>
                  {addingSlot ? '...' : '+ Add'}
                </button>
              </div>
            </div>
            {/* New plan form */}
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>New Plan</div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Plan Name *</label>
              <input style={inputStyle} placeholder="e.g. Monthly, Student, Premium..." value={planName} onChange={e => setPlanName(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                 <label style={labelStyle}>Price ({currSym}) *</label>
                <input type="number" style={inputStyle} placeholder="999" value={planPrice} onChange={e => setPlanPrice(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Duration (days) *</label>
                <input type="number" style={inputStyle} placeholder="30" value={planDays} onChange={e => setPlanDays(e.target.value)} />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>What's included <span style={{ fontWeight: 400, textTransform: 'none', color: '#94a3b8' }}>(optional)</span></label>
              <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3}
                placeholder="e.g. Access to all equipment, locker, 1 personal training session..."
                value={planDescription} onChange={e => setPlanDescription(e.target.value)} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Slots (optional)</label>
              {currentSlots.length === 0 ? (
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c', borderRadius: 10, padding: 10, fontSize: 12, fontWeight: 600 }}>
                  Add a slot above first (e.g. Morning, Evening) — you need at least one before you can create a plan.
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {currentSlots.map(s => (
                    <button key={s} onClick={() => setPlanSlots(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                      style={{
                        padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        background: planSlots.includes(s) ? color : '#f1f5f9',
                        color: planSlots.includes(s) ? '#fff' : '#64748b',
                        border: planSlots.includes(s) ? `1px solid ${color}` : '1px solid #e2e8f0',
                      }}>{s}</button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowPlansManager(false)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Close</button>
              <button onClick={handleSavePlan} disabled={savingPlan || !planName || !planPrice || !planDays}
                style={{ flex: 1, background: !planName || !planPrice || !planDays ? '#e2e8f0' : color, color: !planName || !planPrice || !planDays ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {savingPlan ? 'Saving...' : '+ Save Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Scan Settings Modal (owner/admin only) */}
      {isOwnerOrAdmin && showModuleSettings && (
        <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowModuleSettings(false)}>
          <div style={{ ...sheetStyle, maxWidth: 380 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>🔒 Scan Settings</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{isGym ? 'Fitness' : 'Food & Dining'} · owner/admin only</div>
              </div>
              <button onClick={() => setShowModuleSettings(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>

            {moduleSettingsLoading || !moduleSettings ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8', fontSize: 13 }}>Loading…</div>
            ) : (
              <>
                {[
                  { key: 'allow_face', label: 'Allow Face Scan' },
                  { key: 'allow_fingerprint', label: 'Allow Fingerprint Scan' },
                  { key: 'allow_qr', label: 'Allow QR Scan' },
                  { key: 'single_scan_only', label: 'Single Scan Per Day' },
                ].map(({ key, label }) => (
                  <div key={key} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 0', borderBottom: '1px solid #f1f5f9',
                  }}>
                    <span style={{ fontSize: 14, color: '#0f172a', fontWeight: 700 }}>{label}</span>
                    <input
                      type="checkbox"
                      checked={!!moduleSettings[key]}
                      onChange={e => setModuleSettings((prev: any) => ({ ...prev, [key]: e.target.checked }))}
                      style={{ width: 18, height: 18, cursor: 'pointer', accentColor: color }}
                    />
                  </div>
                ))}

                <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                  <button onClick={() => setShowModuleSettings(false)} style={{
                    flex: 1, padding: '11px', borderRadius: 12, border: '1px solid #e2e8f0',
                    background: '#f1f5f9', color: '#475569', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                  }}>Cancel</button>
                  <button
                    onClick={async () => { await saveModuleSettings(currentModule, moduleSettings); setShowModuleSettings(false); }}
                    style={{
                      flex: 1, padding: '11px', borderRadius: 12, border: 'none',
                      background: color, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    }}>Save</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showNewMember && (
        <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowNewMember(false)}>
          <div style={sheetStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>{icon} New {isGym ? 'Fitness' : 'Food & Dining'} Member</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Create membership + QR badge</div>
              </div>
              <button onClick={() => setShowNewMember(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Name *</label>
              <input style={inputStyle} placeholder="Member name" value={memberName} onChange={e => setMemberName(e.target.value)} autoFocus />
            </div>
            <div style={{ marginBottom: 12 }}>
             <label style={labelStyle}>Phone *</label>
{memberPhoneExists && <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, marginBottom: 6 }}>⚠️ This phone number already has an active membership</div>}
<PhoneInput value={memberPhone} onChange={setMemberPhone} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Email</label>
              {memberEmailExists && <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, marginBottom: 6 }}>⚠️ This email already has an active membership</div>}
              <input style={inputStyle} placeholder="email@example.com" value={memberEmail} onChange={e => setMemberEmail(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={labelStyle}>Plan</label>
                <select style={inputStyle} value={memberPlan} onChange={e => {
                  const picked = membershipPlans.find(p => p.name === e.target.value);
                  setMemberPlan(e.target.value);
                  if (picked) {
                    const end = new Date(memberStart);
                    end.setDate(end.getDate() + picked.duration_days);
                    setMemberEnd(end.toISOString().split('T')[0]);
                    const validSlots = picked.slots.filter((s: string) => currentSlots.includes(s));
                    setMemberSlots(validSlots);
                    if (validSlots.length < picked.slots.length) {
                      toast$('This plan referenced a slot that no longer exists — please review the slots below.', 'error');
                    }
                  }
                }}>
                  <option value="">— Pick a plan —</option>
                  {membershipPlans.map(p => (
                    <option key={p.id} value={p.name}>
                      {p.name} · {currSym}{p.price} · {p.duration_days}d
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Start Date</label>
                <input type="date" style={inputStyle} value={memberStart} onChange={e => setMemberStart(e.target.value)} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>End Date *</label>
              <input type="date" style={inputStyle} value={memberEnd} onChange={e => setMemberEnd(e.target.value)} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Slots / Sessions (optional)</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {currentSlots.map(s => (
                  <button key={s} onClick={() => setMemberSlots(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                    style={{
                      padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      background: memberSlots.includes(s) ? color : '#f1f5f9',
                      color: memberSlots.includes(s) ? '#fff' : '#64748b',
                      border: memberSlots.includes(s) ? `1px solid ${color}` : '1px solid #e2e8f0',
                    }}>{s}</button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowNewMember(false)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCreateMember} disabled={sendingPreOtp || !memberName || !memberPhone || !memberEnd || memberPhoneExists || memberEmailExists}
                style={{ flex: 1, background: !memberName || !memberPhone || !memberEnd ? '#e2e8f0' : color, color: !memberName || !memberPhone || !memberEnd ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {sendingPreOtp ? 'Sending OTP...' : 'Send OTP to Verify →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Badge Modal */}
{memberQR && (
  <div style={modalStyle} onClick={e => e.target === e.currentTarget && setMemberQR(null)}>
    <div style={{ ...sheetStyle, maxWidth: 380, textAlign: 'center' }}>
      <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', marginBottom: 4 }}>{icon} Member Badge</div>
      <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>{memberQR.name}</div>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
        <img src={memberQR.url} style={{ width: 260, height: 260, borderRadius: 16, border: `2px solid ${borderColor}` }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <button onClick={() => { const a = document.createElement('a'); a.href = memberQR.url; a.download = `${memberQR.name}_badge.png`; a.click(); }}
          style={{ background: bgColor, color, border: `1px solid ${borderColor}`, borderRadius: 12, padding: '11px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>⬇ Download</button>
        <button onClick={() => {
          const m = memberQR.member || subMembers.find(x => x.name === memberQR.name);
          if (m) printMemberBadge(m, memberQR.url);
        }} style={{ background: color, color: '#fff', border: 'none', borderRadius: 12, padding: '11px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>🖨️ Print</button>
      </div>
      <button onClick={async () => {
        const m = memberQR.member || subMembers.find(x => x.name === memberQR.name);
        if (!m) return;
        const slots = m.slots || [];
        const isGymMod = mainTab === 'gym';
        const modTitle = isGymMod ? 'Fitness' : 'Food & Dining';
        const modIcon = isGymMod ? '🏋️' : '🍽️';
        const modColor = isGymMod ? '#7c3aed' : '#065f46';
        const memberPortalUrl = `${window.location.origin}/pay/member/${m.id}`;

        const caption = `${modIcon} Hi ${m.name}! Here's your ${modTitle} QR badge.\n\n📋 Plan: ${m.plan_name}\n📅 Valid: ${m.start_date} → ${m.end_date}\n⏰ ${isGymMod ? 'Slots' : 'Meal Slots'}: ${slots.join(', ') || 'All slots'}\n\nShow this badge at the counter when you arrive! — ${companyName || authUser?.name || 'Our Company'}`;

        let sent: string[] = [];
        let failed: string[] = [];

        if (m.phone) {
          try {
            const qrRes = await fetch(`${BASE}/subscriptions/members/${m.id}/send-qr-whatsapp`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
            });
            if (!qrRes.ok) {
              await sendWhatsAppImage(m.phone, memberQR.url, caption, outletId);
            }
            sent.push('WhatsApp');
          } catch (e: any) {
            failed.push(`WhatsApp (${e.message})`);
          }
        } else {
          failed.push('WhatsApp (no phone)');
        }

        if (m.email) {
          try {
            const emailHtml = `
              <div style="font-family:sans-serif;max-width:480px;margin:auto;padding:32px;background:#f8fafc;border-radius:16px;border:1px solid #e2e8f0">
                <h2 style="color:${modColor};margin-bottom:6px">${modIcon} ${modTitle} QR Badge</h2>
                <p>Hi <strong>${m.name}</strong>! Your entrance badge is below.</p>
                <div style="background:#fff;border-radius:12px;padding:20px;text-align:center;margin:20px 0;border:1px solid #e2e8f0">
                  <img src="${memberQR.url}" style="width:200px;height:200px" />
                </div>
                <table style="width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px">
                  <tr style="border-bottom:1px solid #f1f5f9"><td style="padding:8px;color:#6b7280">Plan</td><td style="padding:8px;font-weight:700">${m.plan_name}</td></tr>
                  <tr style="background:#f1f5f9"><td style="padding:8px;color:#6b7280">Valid</td><td style="padding:8px;font-weight:700">${m.start_date} → ${m.end_date}</td></tr>
                  <tr><td style="padding:8px;color:#6b7280">${isGymMod ? 'Slots' : 'Meal Slots'}</td><td style="padding:8px;font-weight:700">${slots.join(', ') || 'All slots'}</td></tr>
                </table>
                <div style="text-align:center;margin:20px 0">
                  <a href="${memberPortalUrl}" style="background:${modColor};color:#fff;padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:bold;display:inline-block;font-size:13px">
                    💳 View Plans & Upgrade Online →
                  </a>
                </div>
              </div>
            `;
            await sendEmail(m.email, `Your ${modTitle} Membership QR Badge`, emailHtml, outletId);
            sent.push('Email');
          } catch (e: any) {
            failed.push(`Email (${e.message})`);
          }
        } else {
          failed.push('Email (no email)');
        }

        if (sent.length) toast$(`Sent via ${sent.join(' & ')}! 📲`);
        if (failed.length) alert(`⚠️ Failed/skipped: ${failed.join(', ')}`);
      }} style={{ width: '100%', background: `linear-gradient(135deg, #25D366, ${color})`, color: '#fff', border: 'none', borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 700, cursor: 'pointer', marginBottom: 8 }}>
        📲 WhatsApp + 📧 Email
      </button>
      {(isGym ? perms.canGymSendReports : perms.canFoodSendReports) && (
      <button onClick={async () => {
        const m = memberQR.member || subMembers.find(x => x.name === memberQR.name);
        if (m) await sendWeeklyReport(m);
      }} style={{ width: '100%', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 12, padding: '11px', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginBottom: 8 }}>📊 Weekly Report</button>
      )}
      <button onClick={() => setMemberQR(null)} style={{ width: '100%', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '11px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Close</button>
    </div>
  </div>
)}


{showMemberHistory && historyMember && (
  <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowMemberHistory(false)}>
    <div style={sheetStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>🕐 Scan History</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{historyMember.name}</div>
        </div>
        <button onClick={() => setShowMemberHistory(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
      </div>

      {historyLoading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</div>
      ) : historyLogs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>No scans recorded yet</div>
        </div>
      ) : (
        (() => {
          const groups: Record<string, any[]> = {};
          historyLogs.forEach(l => { if (!groups[l.date]) groups[l.date] = []; groups[l.date].push(l); });
          const sortedDates = Object.keys(groups).sort((a, b) => b.localeCompare(a));
          return sortedDates.map(date => (
            <div key={date} style={{ marginBottom: 16 }}>
              <div style={{ background: '#e2e8f0', borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 800, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                {new Date(date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
              <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', border: '1px solid #f1f5f9' }}>
                {groups[date].map((l, i) => (
                  <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: i < groups[date].length - 1 ? '1px solid #f8fafc' : 'none' }}>
                    <div style={{ fontSize: 18 }}>{l.scan_type === 'in' ? '✅' : '🚪'}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{l.slot} — {l.scan_type === 'in' ? 'Checked In' : 'Checked Out'}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {new Date(l.scanned_at.endsWith('Z') || l.scanned_at.includes('+') ? l.scanned_at : l.scanned_at + 'Z').toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ));
        })()
      )}
    </div>
  </div>
)}


      {/* Scanner Modal */}
      {showScanner && (
        <div style={modalStyle}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 320, textAlign: 'center' }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>📷 Scan — {scanSlot}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>Point at member's QR badge</div>
            {!scanResult ? (
              <>
                <div style={{ borderRadius: 12, overflow: 'hidden', background: '#000', marginBottom: 16, position: 'relative', height: 220 }}>
                  <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <div style={{ width: 140, height: 140, border: `3px solid ${color}`, borderRadius: 12, boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)' }} />
                  </div>
                </div>
                <button onClick={() => { stopScanner(); setShowScanner(false); }} style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              </>
            ) : (
              <div>
                <div style={{ fontSize: 48, marginBottom: 12 }}>
                  {scanResult.status === 'success' ? '✅'
                    : scanResult.status === 'already_scanned' ? '⚠️'
                    : scanResult.status === 'method_disabled' ? '🚫'
                    : scanResult.status === 'expired' ? '⏳'
                    : '❌'}
                </div>
               <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a', marginBottom: 6 }}>
  {scanResult.status === 'success'
    ? scanResult.scan_type === 'in' ? '✅ Checked In!' : '🚪 Checked Out!'
    : scanResult.status === 'already_scanned' ? 'Already Scanned'
    : scanResult.status === 'method_disabled' ? 'QR Scan Disabled'
    : scanResult.status === 'expired' ? 'Plan Expired'
    : 'Error'}
</div>
<div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>
  {scanResult.name && <strong>{scanResult.name}</strong>}
  {scanResult.status === 'success' && (
    <span> — {scanResult.scan_type === 'in' ? 'Entry' : 'Exit'} logged at <strong>{scanResult.time}</strong></span>
  )}
  {scanResult.status === 'expired' && ` — plan expired on ${scanResult.end_date}`}
  {scanResult.message && scanResult.message}
</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button onClick={() => { setScanResult(null); startScanner(scanSlot); }} style={{ flex: 1, background: bgColor, color, border: `1px solid ${borderColor}`, borderRadius: 8, padding: '10px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Scan Again</button>
                  <button onClick={() => { setShowScanner(false); setScanResult(null); }} style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pre-Create OTP Modal */}
{showPreOtp && (
  <div style={modalStyle}>
    <div style={{ ...sheetStyle, maxWidth: 360, textAlign: 'center', overflowY: 'auto', maxHeight: '90vh' }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>🔐</div>
      <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', marginBottom: 4 }}>Verify Phone Number</div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>{pendingMemberData?.name}</div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 20 }}>
        Sent via WhatsApp to <strong>{preOtpPhone}</strong><br/>
        <span style={{ color: '#94a3b8' }}>Enter the code to complete registration. Didn't get it? Check WhatsApp on this number, then tap Resend below.</span>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '20px 0' }}>
        {[0,1,2,3,4,5].map(i => (
          <input
            key={i}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={preOtpValue[i] || ''}
            onChange={e => {
              const char = e.target.value.replace(/\D/g, '').slice(-1);
              const arr = preOtpValue.padEnd(6, ' ').split('');
              arr[i] = char || ' ';
              const next = arr.join('').trimEnd();
              setPreOtpValue(next);
              if (char) {
                const nextEl = document.getElementById(`pre-otp-${i+1}`);
                if (nextEl) (nextEl as HTMLInputElement).focus();
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Backspace') {
                const arr = preOtpValue.padEnd(6, ' ').split('');
                arr[i] = ' ';
                setPreOtpValue(arr.join('').trimEnd());
                if (i > 0) {
                  const prev = document.getElementById(`pre-otp-${i-1}`);
                  if (prev) (prev as HTMLInputElement).focus();
                }
              }
            }}
            id={`pre-otp-${i}`}
            style={{
              width: 44, height: 52, textAlign: 'center', fontSize: 22, fontWeight: 900,
              border: `2px solid ${preOtpValue[i] && preOtpValue[i] !== ' ' ? '#6366f1' : '#94a3b8'}`,
              borderRadius: 12, outline: 'none',
              background: preOtpValue[i] && preOtpValue[i] !== ' ' ? '#f5f3ff' : '#fff',
              color: '#1e1b4b', fontFamily: 'monospace', boxSizing: 'border-box',
            }}
          />
        ))}
      </div>
      {error && <div style={{ background: '#fff1f2', color: '#be123c', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 600, marginBottom: 12 }}>{error}<button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 8, color: '#be123c' }}>×</button></div>}
      <button onClick={handleVerifyPreOtp} disabled={verifyingPreOtp || creatingMember || preOtpValue.replace(/\s/g,'').length !== 6}
        style={{ width: '100%', background: preOtpValue.length !== 6 ? '#e2e8f0' : color, color: preOtpValue.length !== 6 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 800, cursor: 'pointer', marginBottom: 10 }}>
        {verifyingPreOtp || creatingMember ? 'Verifying & Creating...' : 'Verify & Create Member →'}
      </button>
      <button onClick={async () => {
        setSendingPreOtp(true);
        try {
          const res = await api<any>('/subscriptions/members/send-precreate-otp', {
            method: 'POST', body: JSON.stringify({ phone: preOtpPhone, name: pendingMemberData?.name }),
          });
          await sendWhatsApp(preOtpPhone, `🔐 *New OTP*\n\nHi ${pendingMemberData?.name}! Your new verification code is: *${res.otp}*\n\nValid for 10 minutes.\n— ${companyName}`, outletId);
          setPreOtpValue('');
          toast$('New OTP sent 📲');
        } catch (e: any) { setError(e.message); }
        finally { setSendingPreOtp(false); }
      }} disabled={sendingPreOtp} style={{ background: 'none', border: 'none', fontSize: 12, color: '#6366f1', cursor: 'pointer', fontWeight: 700, marginBottom: 8 }}>
        {sendingPreOtp ? 'Sending...' : '🔄 Resend OTP'}
      </button>
      <button onClick={() => { setShowPreOtp(false); setPendingMemberData(null); setShowNewMember(true); }}
        style={{ background: 'none', border: 'none', fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>← Back to form</button>
    </div>
  </div>
)}
{/* Edit Member Modal */}
{editMember && (
  <div style={modalStyle} onClick={e => e.target === e.currentTarget && setEditMember(null)}>
    <div style={sheetStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>✏️ Edit Member</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{editMember.name}</div>
        </div>
        <button onClick={() => setEditMember(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Name</label>
        <input style={inputStyle} value={editName} onChange={e => setEditName(e.target.value)} />
      </div>
     <div style={{ marginBottom: 12 }}>
  <label style={labelStyle}>Phone</label>
  {editMemberPhoneExists && <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, marginBottom: 6 }}>⚠️ This phone number already has an active membership</div>}
  <PhoneInput value={editPhone} onChange={setEditPhone} />
</div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Email</label>
        {editMemberEmailExists && <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, marginBottom: 6 }}>⚠️ This email already has an active membership</div>}
        <input style={inputStyle} value={editEmail} onChange={e => setEditEmail(e.target.value)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Plan</label>
          <select style={inputStyle} value={editPlan} onChange={e => {
            const picked = membershipPlans.find(p => p.name === e.target.value);
            setEditPlan(e.target.value);
            if (picked) {
              const end = new Date(editStart);
              end.setDate(end.getDate() + picked.duration_days);
              setEditEnd(end.toISOString().split('T')[0]);
              const validSlots = picked.slots.filter((s: string) => currentSlots.includes(s));
              setEditSlots(validSlots);
              if (validSlots.length < picked.slots.length) {
                toast$('This plan referenced a slot that no longer exists — please review the slots below.', 'error');
              }
            }
          }}>
            <option value="">— Pick a plan —</option>
            {membershipPlans.map(p => (
              <option key={p.id} value={p.name}>
                {p.name} · {currSym}{p.price} · {p.duration_days}d
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Start Date</label>
          <input type="date" style={inputStyle} value={editStart} onChange={e => setEditStart(e.target.value)} />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>End Date</label>
        <input type="date" style={inputStyle} value={editEnd} onChange={e => setEditEnd(e.target.value)} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Slots</label>
        {currentSlots.length === 1 && (
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>Auto-selected (only slot available)</div>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {currentSlots.map(s => (
            <button key={s} onClick={() => setEditSlots(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
              style={{
                padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                background: editSlots.includes(s) ? color : '#f1f5f9',
                color: editSlots.includes(s) ? '#fff' : '#64748b',
                border: editSlots.includes(s) ? `1px solid ${color}` : '1px solid #e2e8f0',
              }}>{s}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => setEditMember(null)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
        <button onClick={handleSaveEdit} disabled={savingEdit || !editName.trim() || !editPhone.trim() || editMemberPhoneExists || editMemberEmailExists}
  style={{ flex: 1, background: (!editName.trim() || !editPhone.trim() || editMemberPhoneExists || editMemberEmailExists) ? '#e2e8f0' : color, color: (!editName.trim() || !editPhone.trim() || editMemberPhoneExists || editMemberEmailExists) ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
  {savingEdit ? 'Saving...' : 'Save Changes ✓'}
</button>
      </div>
    </div>
  </div>
)}
{/* Edit Phone OTP Modal */}
{showEditPhoneOtp && (
  <div style={modalStyle}>
    <div style={{ ...sheetStyle, maxWidth: 360, textAlign: 'center', overflowY: 'auto' }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>📱</div>
      <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', marginBottom: 4 }}>Verify New Number</div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>{pendingEditData?.name}</div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 20 }}>
        Sent via WhatsApp to <strong>{pendingEditData?.phone}</strong><br/>
        <span style={{ color: '#94a3b8' }}>Enter the code to confirm phone change. Didn't get it? Check WhatsApp is active on this number, then tap Resend below.</span>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '20px 0' }}>
        {[0,1,2,3,4,5].map(i => (
          <input key={i} id={`edit-otp-${i}`} type="text" inputMode="numeric" maxLength={1}
            value={editPhoneOtp[i] || ''}
            onChange={e => {
              const char = e.target.value.replace(/\D/g, '').slice(-1);
              const arr = editPhoneOtp.padEnd(6, ' ').split('');
              arr[i] = char || ' ';
              setEditPhoneOtp(arr.join('').trimEnd());
              if (char) { const n = document.getElementById(`edit-otp-${i+1}`); if (n) (n as HTMLInputElement).focus(); }
            }}
            onKeyDown={e => {
              if (e.key === 'Backspace') {
                const arr = editPhoneOtp.padEnd(6, ' ').split('');
                arr[i] = ' '; setEditPhoneOtp(arr.join('').trimEnd());
                if (i > 0) { const p = document.getElementById(`edit-otp-${i-1}`); if (p) (p as HTMLInputElement).focus(); }
              }
            }}
            style={{ width: 44, height: 52, textAlign: 'center', fontSize: 22, fontWeight: 900,
              border: `2px solid ${editPhoneOtp[i] && editPhoneOtp[i] !== ' ' ? '#6366f1' : '#94a3b8'}`,
              borderRadius: 12, outline: 'none',
              background: editPhoneOtp[i] && editPhoneOtp[i] !== ' ' ? '#f5f3ff' : '#fff',
              color: '#1e1b4b', fontFamily: 'monospace', boxSizing: 'border-box' }} />
        ))}
      </div>
      {error && <div style={{ background: '#fff1f2', color: '#be123c', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 600, marginBottom: 12 }}>{error}<button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 8, color: '#be123c' }}>×</button></div>}
      <button onClick={handleVerifyEditPhoneOtp}
        disabled={verifyingEditOtp || savingEdit || editPhoneOtp.replace(/\s/g,'').length !== 6}
        style={{ width: '100%', background: editPhoneOtp.replace(/\s/g,'').length !== 6 ? '#e2e8f0' : color, color: editPhoneOtp.replace(/\s/g,'').length !== 6 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 800, cursor: 'pointer', marginBottom: 10 }}>
        {verifyingEditOtp || savingEdit ? 'Verifying...' : 'Verify & Save →'}
      </button>
      <button onClick={async () => {
        setSendingEditOtp(true);
        try {
          const res = await api<any>('/subscriptions/members/send-precreate-otp', {
            method: 'POST', body: JSON.stringify({ phone: pendingEditData?.phone, name: pendingEditData?.name }),
          });
          await sendWhatsApp(pendingEditData?.phone, `🔐 *New OTP*\n\nHi ${pendingEditData?.name}! Your new verification code is: *${res.otp}*\n\nValid for 10 minutes.\n— ${companyName}`, outletId);
          setEditPhoneOtp('');
          toast$('New OTP sent 📲');
        } catch (e: any) { setError(e.message); }
        finally { setSendingEditOtp(false); }
      }} disabled={sendingEditOtp} style={{ background: 'none', border: 'none', fontSize: 12, color: '#6366f1', cursor: 'pointer', fontWeight: 700, marginBottom: 8 }}>
        {sendingEditOtp ? 'Sending...' : '🔄 Resend OTP'}
      </button>
      <button onClick={() => { setShowEditPhoneOtp(false); setPendingEditData(null); setEditPhoneOtp(''); }}
        style={{ background: 'none', border: 'none', fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>← Cancel</button>
    </div>
  </div>
)}
{/* OTP Verify Modal */}
{otpMember && (
  <div style={modalStyle} onClick={e => e.target === e.currentTarget && setOtpMember(null)}>
    <div style={{ ...sheetStyle, maxWidth: 360, textAlign: 'center', overflowY: 'auto' }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>🔐</div>
      <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', marginBottom: 4 }}>Verify Member</div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>{otpMember.name}</div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 20 }}>
        OTP sent to <strong>{otpMember.phone}</strong>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '20px 0' }}>
        {[0,1,2,3,4,5].map(i => (
          <input key={i} id={`mem-otp-${i}`} type="text" inputMode="numeric" maxLength={1}
            value={otpValue[i] || ''}
            onChange={e => {
              const char = e.target.value.replace(/\D/g, '').slice(-1);
              const arr = otpValue.padEnd(6, ' ').split('');
              arr[i] = char || ' ';
              setOtpValue(arr.join('').trimEnd());
              if (char) { const n = document.getElementById(`mem-otp-${i+1}`); if (n) (n as HTMLInputElement).focus(); }
            }}
            onKeyDown={e => {
              if (e.key === 'Backspace') {
                const arr = otpValue.padEnd(6, ' ').split('');
                arr[i] = ' '; setOtpValue(arr.join('').trimEnd());
                if (i > 0) { const p = document.getElementById(`mem-otp-${i-1}`); if (p) (p as HTMLInputElement).focus(); }
              }
            }}
            style={{ width: 44, height: 52, textAlign: 'center', fontSize: 22, fontWeight: 900, border: `2px solid ${otpValue[i] && otpValue[i] !== ' ' ? '#6366f1' : '#94a3b8'}`, borderRadius: 12, outline: 'none', background: otpValue[i] && otpValue[i] !== ' ' ? '#f5f3ff' : '#fff', color: '#1e1b4b', fontFamily: 'monospace', boxSizing: 'border-box' }} />
        ))}
      </div>
      {error && <div style={{ background: '#fff1f2', color: '#be123c', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 600, marginBottom: 12 }}>{error}</div>}
      <button onClick={handleVerifyOtp} disabled={verifyingOtp || otpValue.replace(/\s/g,'').length !== 6}
        style={{ width: '100%', background: otpValue.length !== 6 ? '#e2e8f0' : color, color: otpValue.length !== 6 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 800, cursor: 'pointer', marginBottom: 10 }}>
        {verifyingOtp ? 'Verifying...' : 'Verify →'}
      </button>
      <button onClick={() => { setOtpMember(null); setOtpValue(''); }}
        style={{ background: 'none', border: 'none', fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>Cancel</button>
    </div>
  </div>
)}{/* 🔒 Face & Fingerprint Security OTP Modal */}
{faceOtpMember && (
  <div style={modalStyle} onClick={e => e.target === e.currentTarget && setFaceOtpMember(null)}>
    <div style={{ ...sheetStyle, maxWidth: 380, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>🔒</div>
      <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', marginBottom: 4 }}>Biometric Lock Security</div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 18, lineHeight: 1.5 }}>
        A biometric credential is already enrolled for <strong>{faceOtpMember.name}</strong>.<br />
        Sent via <strong>WhatsApp</strong> & <strong>Email</strong> to authorize changes.
      </div>

      <div style={{ marginBottom: 18 }}>
        <input
          type="text"
          maxLength={6}
          placeholder="Enter 6-Digit OTP"
          value={faceOtpInput}
          onChange={e => setFaceOtpInput(e.target.value.replace(/\D/g, ''))}
          onKeyDown={e => { if (e.key === 'Enter') verifyBiometricOtpAndProceed(); }}
          style={{
            width: '100%', padding: '12px', borderRadius: 12, border: '2px solid #c084fc',
            fontSize: 22, fontWeight: 900, letterSpacing: 6, textAlign: 'center',
            background: '#faf5ff', color: '#581c87', outline: 'none', boxSizing: 'border-box',
          }}
          autoFocus
        />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          type="button"
          onClick={() => { setFaceOtpMember(null); setFaceOtpInput(''); }}
          style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={verifyBiometricOtpAndProceed}
          disabled={faceOtpLoading || faceOtpInput.length < 4}
          style={{
            flex: 1, background: faceOtpInput.length < 4 ? '#e2e8f0' : '#7c3aed',
            color: faceOtpInput.length < 4 ? '#94a3b8' : '#fff', border: 'none',
            borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
          }}
        >
          {faceOtpLoading ? 'Verifying...' : 'Verify & Unlock 🔓'}
        </button>
      </div>

      <button
        type="button"
        onClick={() => handleBiometricAction(faceOtpMember, biometricOtpTarget || 'face')}
        disabled={faceOtpLoading}
        style={{
          background: 'none', border: 'none', color: '#7c3aed',
          fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0,
        }}
      >
        🔄 Resend OTP via WhatsApp & Email
      </button>
    </div>
  </div>
)}

{/* Face Register Modal */}
{showFaceRegister && faceRegisterMember && (
  <div style={modalStyle}>
    <div style={{ background: '#fff', borderRadius: 20, padding: 24, width: 320, textAlign: 'center' }}>
      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>📸 Register Face</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>{faceRegisterMember.name}</div>

      {faceRegisterStatus === 'done' ? (
        <>
          <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#15803d', marginBottom: 16 }}>Face Registered!</div>
          <button onClick={() => { setShowFaceRegister(false); setFaceRegisterStatus('idle'); }}
            style={{ width: '100%', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 12, padding: 12, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Done</button>
        </>
      ) : faceRegisterStatus === 'error' ? (
        <>
          <div style={{ fontSize: 56, marginBottom: 12 }}>❌</div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#be123c', marginBottom: 16 }}>Camera access denied</div>
          <button onClick={() => { setShowFaceRegister(false); stopFaceStream(); }}
            style={{ width: '100%', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: 12, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Close</button>
        </>
      ) : (
        <>
          <div style={{ borderRadius: 12, overflow: 'hidden', background: '#000', marginBottom: 16, position: 'relative', height: 220 }}>
            <video ref={faceVideoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ width: 150, height: 180, border: '3px solid #7c3aed', borderRadius: '50%', boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)' }} />
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
            {faceRegisterStatus === 'loading' ? 'Loading camera...' : 'Position face in the oval and tap capture'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { stopFaceStream(); setShowFaceRegister(false); setFaceRegisterStatus('idle'); }}
              style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 10, padding: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={captureFaceDescriptor} disabled={faceRegisterStatus !== 'capturing'}
              style={{ flex: 1, background: faceRegisterStatus !== 'capturing' ? '#e2e8f0' : '#7c3aed', color: faceRegisterStatus !== 'capturing' ? '#94a3b8' : '#fff', border: 'none', borderRadius: 10, padding: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {faceRegisterStatus === 'loading' ? 'Loading...' : '📸 Capture'}
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)}

{/* Face Scan Modal */}
{showFaceScan && (
  <div style={modalStyle}>
    <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 320, textAlign: 'center' }}>
      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>👁️ Face Scan{faceScanSlot && faceScanSlot !== '__auto__' ? ` — ${faceScanSlot}` : ''}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>Look at the camera to mark attendance</div>

      {faceScanStatus === 'scanning' ? (
        <>
          <div style={{ borderRadius: 12, overflow: 'hidden', background: '#000', marginBottom: 16, position: 'relative', height: 220 }}>
            <video ref={faceVideoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ width: 150, height: 180, border: '3px solid #7c3aed', borderRadius: '50%', boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)', animation: 'pulse 1.5s infinite' }} />
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>Scanning... keep face visible</div>
          <button onClick={() => { faceScanCancelledRef.current = true; stopFaceStream(); setShowFaceScan(false); }}
            style={{ width: '100%', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 10, padding: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        </>
      ) : (
        <>
          <div style={{ fontSize: 56, marginBottom: 12 }}>
            {faceScanStatus === 'match' ? (faceScanResult?.scan_type === 'in' ? '✅' : '🚪')
              : faceScanStatus === 'nomatch' ? '❓'
              : faceScanStatus === 'already_scanned' ? '⚠️'
              : faceScanStatus === 'method_disabled' ? '🚫'
              : faceScanStatus === 'expired' ? '⏳'
              : '❌'}
          </div>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 6 }}>
            {faceScanStatus === 'match'
              ? faceScanResult?.scan_type === 'in' ? 'Checked In!' : 'Checked Out!'
              : faceScanStatus === 'nomatch' ? 'Face Not Recognized'
              : faceScanStatus === 'already_scanned' ? 'Already Scanned'
              : faceScanStatus === 'method_disabled' ? 'Face Scan Disabled'
              : faceScanStatus === 'expired' ? 'Membership Expired'
              : 'Error'}
          </div>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
            {faceScanResult?.name && <strong>{faceScanResult.name}</strong>}
            {faceScanStatus === 'match' && <span> — {faceScanResult?.scan_type === 'in' ? 'Entry' : 'Exit'} at <strong>{faceScanResult?.time}</strong></span>}
            {faceScanStatus === 'nomatch' && <span> — No matching member found</span>}
            {faceScanResult?.message && <span> — {faceScanResult.message}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setFaceScanResult(null); setFaceScanStatus('scanning'); startFaceScan(faceScanSlot); }}
              style={{ flex: 1, background: '#fdf4ff', color: '#7c3aed', border: '1px solid #e9d5ff', borderRadius: 8, padding: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Scan Again</button>
            <button onClick={() => { setShowFaceScan(false); setFaceScanResult(null); }}
              style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Done</button>
          </div>
        </>
      )}
    </div>
  </div>
)}
{/* ✨ Sleek Enterprise Biometric Setup Modal (Scan-to-Pay Clean Design Parity) */}
{showBiometricSetup && biometricMember && (() => {
  const isFaceDone = !!(
    biometricMember.face_descriptor ||
    biometricMember.face_image_encoding ||
    biometricMember.has_face ||
    faceSetupStatus === 'done'
  );
  const isFpDone = !!(
    biometricMember.fingerprint_id ||
    biometricMember.fingerprint_credential_id ||
    biometricMember.has_fingerprint ||
    fingerprintStatus === 'done'
  );

  return (
    <div style={modalStyle} onClick={e => e.target === e.currentTarget && (
      phoneBioPollRef.current && clearInterval(phoneBioPollRef.current),
      setShowBiometricSetup(false),
      setBiometricMember(null)
    )}>
      <div style={{
        background: '#ffffff', borderRadius: 28, padding: '32px 24px',
        width: '100%', maxWidth: 420, textAlign: 'center',
        boxShadow: '0 32px 80px rgba(0,0,0,0.25)', border: '1px solid #f1f5f9',
        maxHeight: '92vh', overflowY: 'auto',
      }}>
        
        {/* Top Header Icon */}
        <div style={{
          width: 52, height: 52, borderRadius: 16, background: '#f5f3ff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 26, margin: '0 auto 12px', border: '1.5px solid #ddd6fe',
        }}>
          🔐
        </div>

        <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', letterSpacing: -0.5 }}>
          Set Up Biometrics
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginTop: 4, marginBottom: 20 }}>
          {biometricMember.name} · {biometricMember.phone}
        </div>

        {/* 1. Face Biometric Card */}
        <div style={{
          border: `1.5px solid ${isFaceDone ? '#bbf7d0' : '#e2e8f0'}`,
          borderRadius: 18, padding: 14, marginBottom: 14,
          background: isFaceDone ? '#f0fdf4' : '#f8fafc',
          transition: 'all 0.15s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: isFaceDone ? '#dcfce7' : '#ffffff',
              border: `1px solid ${isFaceDone ? '#86efac' : '#e2e8f0'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
            }}>
              {isFaceDone ? '✅' : '👁️'}
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>Face Recognition</div>
              <div style={{ fontSize: 11, color: isFaceDone ? '#15803d' : '#64748b', fontWeight: 600 }}>
                {isFaceDone ? 'Face ID enrolled & active' : 'Touchless entrance kiosk check-in'}
              </div>
            </div>
            {!isFaceDone ? (
              <button
                onClick={() => startFaceRegister(biometricMember)}
                style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}
              >
                Scan Face
              </button>
            ) : (
              <button
                onClick={() => handleBiometricAction(biometricMember, 'face')}
                style={{ background: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 10, padding: '6px 12px', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}
              >
                Change (OTP)
              </button>
            )}
          </div>
        </div>

        {/* 2. Fingerprint Biometric Card */}
        <div style={{
          border: `1.5px solid ${isFpDone ? '#bbf7d0' : '#e2e8f0'}`,
          borderRadius: 18, padding: 16, marginBottom: 20,
          background: isFpDone ? '#f0fdf4' : '#ffffff',
          transition: 'all 0.15s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: (!isFpDone && phoneEnrollQR) ? 14 : 0 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: isFpDone ? '#dcfce7' : '#f5f3ff',
              border: `1px solid ${isFpDone ? '#86efac' : '#ddd6fe'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
            }}>
              {isFpDone ? '✅' : '👆'}
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>Fingerprint Biometric</div>
              <div style={{ fontSize: 11, color: isFpDone ? '#15803d' : '#64748b', fontWeight: 600 }}>
                {isFpDone ? 'Fingerprint registered & active' : 'Touch phone sensor or USB reader'}
              </div>
            </div>
            {isFpDone && (
              <button
                onClick={() => handleBiometricAction(biometricMember, 'fingerprint')}
                style={{ background: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 10, padding: '6px 12px', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}
              >
                Change (OTP)
              </button>
            )}
          </div>

          {/* 📱 Scan with Phone Section (Only if not enrolled) */}
          {!isFpDone && (
            <div style={{ marginTop: 10 }}>
              {!phoneEnrollQR ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => startPhoneEnrollment(biometricMember)}
                    style={{
                      width: '100%', padding: '13px', background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff', border: 'none', borderRadius: 14, fontSize: 13, fontWeight: 800,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
                    }}
                  >
                    📱 Scan QR with Phone to Enroll
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowManualFp(!showManualFp)}
                    style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 11, fontWeight: 700, cursor: 'pointer', marginTop: 4 }}
                  >
                    {showManualFp ? 'Hide USB scanner input ▲' : 'Or enter USB Reader ID manually ▼'}
                  </button>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                  {/* Modern QR Container */}
                  <div style={{
                    padding: 14, background: '#fff', borderRadius: 20,
                    border: '2px solid #e2e8f0', margin: '0 auto 12px',
                    width: 200, height: 200,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                  }}>
                    <img src={phoneEnrollQR} alt="Phone Enroll QR" style={{ width: 172, height: 172, borderRadius: 10 }} />
                  </div>

                  {/* Pulsing Status Pill */}
                  <div style={{
                    background: '#eef2ff', borderRadius: 99, padding: '7px 16px',
                    fontSize: 11.5, color: '#4f46e5', fontWeight: 800,
                    display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8,
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#6366f1', animation: 'pulse 1.5s infinite' }} />
                    <span>Waiting for fingerprint on phone...</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    Point camera at QR to touch fingerprint sensor
                  </div>
                </div>
              )}

              {/* Manual USB Reader ID */}
              {showManualFp && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e2e8f0' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      placeholder="e.g. FP-10029 or tap reader"
                      value={manualFpInput}
                      onChange={e => setManualFpInput(e.target.value)}
                      style={{ ...inputStyle, flex: 1, padding: '9px 12px', fontSize: 12 }}
                      autoFocus
                    />
                    <button
                      onClick={handleSaveManualFp}
                      style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 10, padding: '0 16px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom Completion Action */}
        <button
          onClick={() => {
            if (phoneBioPollRef.current) {
              clearInterval(phoneBioPollRef.current);
              phoneBioPollRef.current = null;
            }
            setPhoneEnrollQR('');
            setWaitingPhoneBio(false);
            stopFaceSetupStream();
            setShowBiometricSetup(false);
            setBiometricMember(null);
            setFaceSetupStatus('idle');
            setFingerprintStatus('idle');
            if (memberQR && (mainTab === 'gym' ? perms.canGymViewBadge : perms.canFoodViewBadge)) {
              setMemberQR(memberQR);
            }
          }}
          style={{
            width: '100%', background: (isFaceDone || isFpDone) ? '#0f172a' : '#f1f5f9',
            color: (isFaceDone || isFpDone) ? '#ffffff' : '#475569',
            border: 'none', borderRadius: 14, padding: '14px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
            boxShadow: (isFaceDone || isFpDone) ? '0 4px 14px rgba(15,23,42,0.25)' : 'none',
          }}
        >
          {(isFaceDone || isFpDone) ? '✓ Done — View Member Badge →' : 'Skip for now →'}
        </button>
      </div>
    </div>
  );
})()}
          {/* Exact POS-Style Payment Modal (Screenshot Parity) */}
      {showPayQrModal && (
        <div style={modalStyle} onClick={e => e.target === e.currentTarget && closePayQr()}>
          <style>{`@keyframes ledgerSpin { to { transform: rotate(360deg); } }`}</style>
          <div style={{
            background: '#fff', borderRadius: 24, padding: '28px 24px',
            width: '100%', maxWidth: 440, textAlign: 'center',
            boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
          }}>
            {/* Big Vibrant Purple Top Amount (Image 2 style) */}
            <div style={{ fontSize: 44, fontWeight: 900, color: '#7c3aed', letterSpacing: -1, lineHeight: 1 }}>
              {currSym}{fmt(payQrAmount)}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginTop: 6, marginBottom: 14 }}>
              {payQrTitle}{payQrSubtitle ? ` — ${payQrSubtitle}` : ' — Ask customer to scan'}
            </div>

            {/* QR Card */}
            <div style={{
              padding: 16, background: '#fff', borderRadius: 20,
              border: '2px solid #e2e8f0', margin: '0 auto 12px',
              minHeight: 230, minWidth: 230, maxWidth: 230,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
              boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
            }}>
              {payReceived ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 56 }}>✅</div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: '#16a34a' }}>Payment Received</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Account updated</div>
                </div>
              ) : loadingPayQr ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 28, height: 28, border: '3px solid #c7d2fe', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'ledgerSpin 0.8s linear infinite' }} />
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Generating QR Code...</div>
                </div>
              ) : payQrDataUrl ? (
                <img src={payQrDataUrl} alt="Payment QR" style={{ width: 200, height: 200, borderRadius: 12 }} />
              ) : (
                <div style={{ fontSize: 12, color: '#ef4444' }}>Failed to generate QR.</div>
              )}
            </div>

            {/* Instruction / Polling Pill */}
            {!payReceived && (
              <div style={{
                background: '#f1f5f9', borderRadius: 99, padding: '7px 14px',
                fontSize: 11, color: '#475569', fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16,
              }}>
                {loadingPayQr ? (
                  <span>Generating payment session...</span>
                ) : (payQrTabId || payQrMember) ? (
                  <>
                    <div style={{ width: 10, height: 10, border: '2px solid #a78bfa', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'ledgerSpin 0.8s linear infinite' }} />
                    <span>Customer scans → auto-detects payment</span>
                  </>
                ) : (
                  <span>Customer scans → pays via UPI, Card or Netbanking</span>
                )}
              </div>
            )}

            {/* Contact Details (Read-Only Display) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              <div style={{
                display: 'flex', alignItems: 'center', background: '#f8fafc',
                border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden',
              }}>
                <div style={{
                  padding: '10px 14px', background: '#f1f5f9', borderRight: '1.5px solid #e2e8f0',
                  fontSize: 12, fontWeight: 800, color: '#475569', display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  📞
                </div>
                <input
                  readOnly
                  value={payQrPhone || 'No phone number'}
                  style={{
                    flex: 1, border: 'none', outline: 'none', background: 'transparent',
                    padding: '10px 14px', fontSize: 13, fontWeight: 700, color: '#1e293b',
                    cursor: 'default', userSelect: 'all',
                  }}
                />
              </div>

              {payQrEmail ? (
                <div style={{
                  display: 'flex', alignItems: 'center', background: '#f8fafc',
                  border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '10px 14px', background: '#f1f5f9', borderRight: '1.5px solid #e2e8f0',
                    fontSize: 12, fontWeight: 800, color: '#475569', display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    ✉️
                  </div>
                  <input
                    readOnly
                    value={payQrEmail}
                    style={{
                      flex: 1, border: 'none', outline: 'none', background: 'transparent',
                      padding: '10px 14px', fontSize: 13, fontWeight: 700, color: '#1e293b',
                      cursor: 'default', userSelect: 'all',
                    }}
                  />
                </div>
              ) : null}
            </div>

            {/* Row 1: Primary Dispatch Buttons (Green WhatsApp + Blue Email side-by-side) */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button
                type="button"
                onClick={handleSharePayLinkWA}
                disabled={!payQrDataUrl || !payQrPhone}
                style={{
                  flex: 1, background: '#10b981', color: '#fff', border: 'none',
                  borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 800,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  opacity: !payQrDataUrl || !payQrPhone ? 0.6 : 1,
                  boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
                }}
              >
                💬 Send on WhatsApp
              </button>

              <button
                type="button"
                onClick={handleSharePayLinkEmail}
                disabled={!payShareUrl || !payQrEmail || !payQrEmail.includes('@')}
                style={{
                  flex: 1, background: '#4f46e5', color: '#fff', border: 'none',
                  borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 800,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  opacity: !payShareUrl || !payQrEmail || !payQrEmail.includes('@') ? 0.6 : 1,
                  boxShadow: '0 2px 8px rgba(79,70,229,0.3)',
                }}
              >
                ✉️ Email
              </button>
            </div>

            {/* Row 2: Secondary Tool Actions (Counter Display + Save QR + Confirm) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 8 }}>
              <button
                type="button"
                onClick={openCounterDisplay}
                title="Mirror onto customer-facing screen"
                style={{
                  background: '#0f172a', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '10px 4px', fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                🖥️ Display
              </button>

              <button
                type="button"
                onClick={() => {
                  if (!payQrDataUrl) return;
                  const a = document.createElement('a');
                  a.href = payQrDataUrl;
                  a.download = `${payQrTitle.replace(/\s+/g, '_')}_pay_qr.png`;
                  a.click();
                  toast$('Payment QR downloaded! ⬇');
                }}
                disabled={!payQrDataUrl}
                style={{
                  background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe',
                  borderRadius: 10, padding: '10px 4px', fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                ⬇ Save QR
              </button>

              <button
                type="button"
                onClick={handleConfirmPaid}
                disabled={confirmingPaid}
                style={{
                  background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a',
                  borderRadius: 10, padding: '10px 4px', fontSize: 11, fontWeight: 800,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  opacity: confirmingPaid ? 0.6 : 1,
                }}
              >
                {confirmingPaid ? '...' : '✅ Confirm'}
              </button>
            </div>

            {/* Row 3: Bottom Status Actions (Done + Discard) */}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button
                type="button"
                onClick={closePayQr}
                style={{
                  flex: 1, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                  borderRadius: 12, padding: '11px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                }}
              >
                ✓ Done
              </button>

              <button
                type="button"
                onClick={closePayQr}
                style={{
                  flex: 1, background: '#fee2e2', color: '#dc2626', border: 'none',
                  borderRadius: 12, padding: '11px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                }}
              >
                🗑 Discard
              </button>
            </div>

          </div>
        </div>
      )}
      {/* ── 📱 Remote Phone Fingerprint Attendance Punch Modal (Gym & Food) ── */}
      {showRemotePunchModal && (
        <div style={modalStyle} onClick={e => e.target === e.currentTarget && (
          remotePunchPollRef.current && clearInterval(remotePunchPollRef.current),
          setShowRemotePunchModal(false),
          setRemotePunchQR('')
        )}>
          <div style={{
            background: '#ffffff', borderRadius: 28, padding: '32px 24px',
            width: '100%', maxWidth: 420, textAlign: 'center',
            boxShadow: '0 32px 80px rgba(0,0,0,0.25)', border: '1px solid #f1f5f9',
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: 18, background: '#ecfdf5',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 26, margin: '0 auto 12px', border: '1.5px solid #a7f3d0',
            }}>
              {mainTab === 'gym' ? '🏋️' : '🍽️'}
            </div>

            <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', letterSpacing: -0.5 }}>
              Scan to Punch {mainTab === 'gym' ? 'Fitness' : 'Dining'}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginTop: 4, marginBottom: 18 }}>
              Open terminal phone camera ➔ touch sensor to check in
            </div>

            {/* QR Card Container */}
            <div style={{
              padding: 16, background: '#fff', borderRadius: 22,
              border: '2px solid #e2e8f0', margin: '0 auto 14px',
              width: 210, height: 210,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(0,0,0,0.05)',
            }}>
              {remotePunchQR ? (
                <img src={remotePunchQR} alt="Punch QR" style={{ width: 180, height: 180, borderRadius: 10 }} />
              ) : (
                <div style={{ color: '#94a3b8', fontSize: 12 }}>Generating QR...</div>
              )}
            </div>

            {/* Pulsing Status Pill */}
            <div style={{
              background: '#eef2ff', borderRadius: 99, padding: '7px 18px',
              fontSize: 12, color: '#4f46e5', fontWeight: 800,
              display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 14,
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#6366f1', animation: 'pulse 1.5s infinite' }} />
              <span>Waiting for member fingerprint on terminal...</span>
            </div>

            <button
              type="button"
              onClick={() => {
                if (remotePunchPollRef.current) clearInterval(remotePunchPollRef.current);
                setShowRemotePunchModal(false);
                setRemotePunchQR('');
              }}
              style={{
                width: '100%', padding: '12px', background: '#f1f5f9', color: '#475569',
                border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 800, cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      {/* Walk-In Renewal Modal (Continuous Cycle Math) */}
      {showRenewModal && renewMember && (
        <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowRenewModal(false)}>
          <div style={{ ...sheetStyle, maxWidth: 420 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>
                  🔄 Renew {mainTab === 'gym' ? 'Membership' : 'Meal Plan'}
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{renewMember.name} · {renewMember.phone}</div>
              </div>
              <button onClick={() => setShowRenewModal(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>

            <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>
              Select Plan to Renew / Upgrade
            </div>
            <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 14 }}>
              {membershipPlans.map((p: any) => {
                const isSelected = selectedRenewPlan?.id === p.id;
                return (
                  <div
                    key={p.id}
                    onClick={() => setSelectedRenewPlan(p)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: 12, borderRadius: 12, marginBottom: 6, cursor: 'pointer',
                      background: isSelected ? (mainTab === 'gym' ? '#f5f3ff' : '#f0fdf4') : '#f8fafc',
                      border: `1.5px solid ${isSelected ? color : '#e2e8f0'}`,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: isSelected ? color : '#0f172a' }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        {p.duration_days} Days · {p.slots?.join(', ') || (mainTab === 'gym' ? 'All gym slots' : 'All meal slots')}
                      </div>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 900, color: isSelected ? color : '#1e293b' }}>
                      {currSym}{p.price}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Continuous Cycle Preview */}
            {selectedRenewPlan && (() => {
              const { newEndDate } = calculateContinuousEndDate(renewMember.end_date, selectedRenewPlan.duration_days);
              return (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 12, marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#16a34a', textTransform: 'uppercase' }}>
                    📅 Continuous Cycle Preview
                  </div>
                  <div style={{ fontSize: 12, color: '#15803d', marginTop: 4 }}>
                    • Starts from: <strong>{renewMember.end_date}</strong> (Previous Expiry)
                  </div>
                  <div style={{ fontSize: 13, color: '#15803d', fontWeight: 800, marginTop: 2 }}>
                    • New Expiry: {newEndDate} ({selectedRenewPlan.duration_days} Days added)
                  </div>
                  <div style={{ fontSize: 11, color: '#16a34a', fontStyle: 'italic', marginTop: 3 }}>
                    ✓ {mainTab === 'gym' ? 'Continuous workout streak intact.' : 'Continuous meal cycle preserved without broken dates.'}
                  </div>
                </div>
              );
            })()}

            {/* Payment Method */}
            <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: 8 }}>
              Payment Method
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
              {[
                { id: 'cash', label: '💵 Cash' },
                { id: 'upi', label: '📱 UPI' },
                { id: 'card', label: '💳 Card' },
              ].map(m => (
                <button
                  key={m.id}
                  onClick={() => setRenewPaymentMethod(m.id as any)}
                  style={{
                    flex: 1, padding: '10px', borderRadius: 10, cursor: 'pointer',
                    border: `1.5px solid ${renewPaymentMethod === m.id ? '#7c3aed' : '#e2e8f0'}`,
                    background: renewPaymentMethod === m.id ? '#f5f3ff' : '#f8fafc',
                    fontWeight: 800, fontSize: 12, color: renewPaymentMethod === m.id ? '#7c3aed' : '#64748b',
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {!!renewMember?.phone && (
                <button
                  type="button"
                  onClick={async () => {
                    const m = renewMember;
                    const p = selectedRenewPlan;
                    setShowRenewModal(false);
                    await handleSendMemberPayLink(m, p, 'manual');
                  }}
                  disabled={!selectedRenewPlan || sendingMemberPayLink === renewMember.id}
                  style={{
                    width: '100%', background: '#16a34a', color: '#fff', border: 'none',
                    borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 800,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    opacity: !selectedRenewPlan ? 0.6 : 1,
                  }}
                >
                  📲 Send Pay Link for {selectedRenewPlan?.name || 'Plan'} via WhatsApp
                </button>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowRenewModal(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                <button
                  type="button"
                  onClick={() => {
                    if (renewPaymentMethod === 'cash') {
                      handleConfirmRenew();
                    } else {
                      openRenewPaymentQr();
                    }
                  }}
                  disabled={renewing || !selectedRenewPlan}
                  style={{
                    flex: 1, background: color, color: '#fff', border: 'none',
                    borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 800,
                    cursor: 'pointer', opacity: renewing || !selectedRenewPlan ? 0.6 : 1,
                  }}
                >
                  {renewPaymentMethod === 'cash'
                    ? (renewing ? 'Renewing...' : `In-Person Cash (${currSym}${selectedRenewPlan?.price || 0}) →`)
                    : `📷 Show Payment QR (${currSym}${selectedRenewPlan?.price || 0}) →`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Catalog Modal */}
      {showShareCatalogModal && (
        <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowShareCatalogModal(false)}>
          <div style={{ ...sheetStyle, maxWidth: 380 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 17, fontWeight: 900, color: '#0f172a' }}>📋 Send Plans to Inquiry</div>
              <button onClick={() => setShowShareCatalogModal(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 30, height: 30, cursor: 'pointer' }}>×</button>
            </div>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
              Enter the customer's phone number to send them your complete pricing menu via WhatsApp.
            </p>
            <label style={labelStyle}>Customer Name (optional)</label>
            <input style={{ ...inputStyle, marginBottom: 12 }} placeholder="e.g. Rahul" value={leadName} onChange={e => setLeadName(e.target.value)} />
            <label style={labelStyle}>Phone Number *</label>
            <PhoneInput value={leadPhone} onChange={setLeadPhone} />
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button onClick={() => setShowShareCatalogModal(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleShareCatalogToLead} disabled={sendingCatalog || !leadPhone.trim()}
                style={{ flex: 1, background: '#ea580c', color: '#fff', border: 'none', borderRadius: 12, padding: '12px', fontWeight: 800, cursor: 'pointer', opacity: sendingCatalog ? 0.6 : 1 }}>
                {sendingCatalog ? 'Sending...' : '📲 Send Menu via WhatsApp'}
              </button>
            </div>
          </div>
        </div>
      )}

      {renderNotConnectedModal()}
      {/* Toast */}
      {toast.msg && (
        <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', background: toast.type === 'error' ? '#ef4444' : '#0f172a', color: '#fff', borderRadius: 14, padding: '12px 24px', fontSize: 13, fontWeight: 700, zIndex: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>{toast.msg}</div>
      )}
    </div>
  );
}
  // ── DETAIL VIEW ────────────────────────────────────────────────────────
  if (view === 'detail' && activeTab) {
    const st = STATUS[activeTab.status] || STATUS.open;
    const isTabFullySettled = (activeTab.balance <= 0) || activeTab.status === 'settled';
    return (
      <div style={{ 
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', 
        background: '#f1f5f9', 
        height: '100vh', 
        overflowY: 'auto', 
        overscrollBehavior: 'contain', 
        paddingBottom: 80 
      }}>
        {/* Header */}
        <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 30, flexWrap: 'wrap' }}>
          <button onClick={() => { setView('list'); setActiveTab(null); }}
            style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 18, color: '#475569', flexShrink: 0 }}>←</button>
          <div style={{ flex: '1 1 160px', minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a', lineHeight: 1.2 }}>{activeTab.customer_name}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{activeTab.phone}{activeTab.email ? ` · ${activeTab.email}` : ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
            <span style={{ background: st.bg, color: st.text, border: `1px solid ${st.border}`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: st.dot, marginRight: 5, verticalAlign: 'middle' }} />
              {st.label}
            </span>
            {perms.canLedgerShareQR && (
            <button onClick={() => handleShowQR(activeTab)}
              title="Show QR Code"
              style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 36, height: 36, cursor: 'pointer', fontSize: 16 }}>⬜</button>
            )}
          {perms.canLedgerEditTab && !isTabFullySettled && (
          <button onClick={() => { setEditTab(activeTab); setEditTabName(activeTab.customer_name); setEditTabPhone(activeTab.phone); setEditTabEmail(activeTab.email || ''); setEditTabAddress(activeTab.address || ''); }}
  style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Edit</button>
          )}
{perms.canLedgerDeleteTab && !isTabFullySettled && (
<button onClick={() => handleDeleteTab(activeTab)}
  style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>🗑 Delete</button>
)}
          </div>
        </div>

        {/* Balance Card — Bright Vibrant Gradient */}
        <div style={{
          margin: '16px 16px 0',
          background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 50%, #7c3aed 100%)',
          borderRadius: 20, padding: '24px 22px', color: '#fff', position: 'relative', overflow: 'hidden',
          boxShadow: '0 12px 36px -8px rgba(79,70,229,0.45)',
        }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
          <div style={{ position: 'absolute', bottom: -20, left: -20, width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.9)', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Outstanding Balance
          </div>
          <div style={{ fontSize: 42, fontWeight: 900, letterSpacing: -1.5, marginTop: 4, lineHeight: 1, color: '#ffffff' }}>
            {currSym}{fmt(activeTab.balance)}
          </div>

          <div style={{ display: 'flex', gap: 28, marginTop: 18, fontSize: 12 }}>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Charged</div>
              <div style={{ fontWeight: 900, fontSize: 16, color: '#fff', marginTop: 2 }}>{currSym}{fmt(activeTab.total_charged)}</div>
            </div>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Paid</div>
              <div style={{ fontWeight: 900, fontSize: 16, color: '#86efac', marginTop: 2 }}>{currSym}{fmt(activeTab.total_paid)}</div>
            </div>
            {activeTab.address && (
              <div>
                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Location</div>
                <div style={{ fontWeight: 800, fontSize: 13, color: '#fff', marginTop: 2 }}>{activeTab.address}</div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
            {perms.canLedgerAddCharge && (
              <button onClick={() => setShowEntryForm(true)} style={{
                flex: 1, height: 40, background: 'rgba(255,255,255,0.22)', border: '1.5px solid rgba(255,255,255,0.4)',
                color: '#fff', borderRadius: 12, fontSize: 13, fontWeight: 800, cursor: 'pointer',
                backdropFilter: 'blur(10px)', transition: 'background 0.15s ease',
              }}>
                + Add Charge
              </button>
            )}
            {perms.canLedgerAddPayment && (
              <button onClick={() => setShowPaymentForm(true)} style={{
                flex: 1, height: 40, background: 'linear-gradient(135deg, #16a34a, #22c55e)', border: 'none',
                color: '#fff', borderRadius: 12, fontSize: 13, fontWeight: 800, cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(34,197,94,0.45)',
              }}>
                💰 Payment
              </button>
            )}
            {activeTab.status !== 'settled' && perms.canLedgerSettle && (
              <button onClick={handleSettle} style={{
                height: 40, padding: '0 18px', background: 'linear-gradient(135deg, #ea580c, #f59e0b)', border: 'none',
                color: '#fff', borderRadius: 12, fontSize: 13, fontWeight: 800, cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(245,158,11,0.45)',
              }}>
                ✓ Settle
              </button>
            )}
          </div>
        </div>

        {/* Action Bar */}
        <div style={{ margin: '14px 16px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {perms.canLedgerSendWA && activeTab.balance > 0 && (
            <button onClick={handleWAReminder}
              style={{ height: 36, display: 'flex', alignItems: 'center', gap: 7, background: '#dcfce7', border: '1.5px solid #bbf7d0', color: '#15803d', borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="#15803d"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
              WA Reminder
            </button>
          )}
          {Boolean(activeTab.email) && activeTab.balance > 0 && (
            <button onClick={handleEmailReminder}
              style={{ height: 36, display: 'flex', alignItems: 'center', gap: 7, background: '#eff6ff', border: '1.5px solid #bfdbfe', color: '#1d4ed8', borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              ✉️ Email Reminder
            </button>
          )}
          {perms.canLedgerExportExcel && (
            <button onClick={() => handleShareExcelWhatsApp(activeTab)}
              style={{ height: 36, display: 'flex', alignItems: 'center', gap: 7, background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#15803d', borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              📊 Export Excel
            </button>
          )}
          {perms.canLedgerShareQR && (
            <button onClick={() => handleShowQR(activeTab)}
              style={{ height: 36, display: 'flex', alignItems: 'center', gap: 7, background: '#f5f3ff', border: '1.5px solid #ddd6fe', color: '#7c3aed', borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              📱 Share QR
            </button>
          )}
          {perms.canLedgerQuickMsg && (
            <button onClick={() => setShowQuickMsg(true)}
              style={{ height: 36, display: 'flex', alignItems: 'center', gap: 7, background: '#fff7ed', border: '1.5px solid #fed7aa', color: '#c2410c', borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              💬 Quick Msg
            </button>
          )}
          {perms.canLedgerNotes && (
            <button onClick={() => { setEditNotes(activeTab.notes || ''); setEditAlertThreshold(String(activeTab.balance_alert_threshold || '')); setShowNotesModal(true); }}
              style={{ height: 36, display: 'flex', alignItems: 'center', gap: 7, background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#15803d', borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              📝 Notes
            </button>
          )}
          {perms.canLedgerRecurring && (
            <button onClick={() => { loadRecurring(activeTab.id); setShowRecurringModal(true); }}
              style={{ height: 36, display: 'flex', alignItems: 'center', gap: 7, background: '#f5f3ff', border: '1.5px solid #ddd6fe', color: '#7c3aed', borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              🔁 Recurring
            </button>
          )}
          {activeTab.status !== 'settled' && perms.canLedgerPayLink && (
            <button onClick={() => handleSendPayLink(activeTab)} disabled={sendingPayLink}
              style={{ height: 36, display: 'flex', alignItems: 'center', gap: 7, background: '#eff6ff', border: '1.5px solid #bfdbfe', color: '#1d4ed8', borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: sendingPayLink ? 0.6 : 1 }}>
              {sendingPayLink ? '⏳ Sending...' : '💳 Pay Link'}
            </button>
          )}
          <button onClick={openCounterDisplay} style={{
            height: 36, display: 'flex', alignItems: 'center', gap: 7,
            background: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#0f172a',
            borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>
            🖥️ Display
          </button>
        </div>
{/* Notes Banner */}
        {(activeTab as any).notes && (
          <div style={{ margin: '0 16px 12px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 12, padding: '10px 14px', fontSize: 12, color: '#92400e', fontWeight: 600 }}>
            📝 {(activeTab as any).notes}
          </div>
        )}
        {/* Transaction History */}
        <div style={{ margin: '0 16px' }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Transaction History</span>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{activeTab.entries?.length || 0} entries</span>
          </div>

          {!activeTab.entries?.length && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>No charges yet</div>
            </div>
          )}

          {groupedEntries(activeTab.entries || []).map(([date, entries]) => (
            <div key={date} style={{ marginBottom: 10 }}>
              <div style={{ background: '#e0e7ff', borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 800, color: '#3730a3', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block' }}>
                {new Date(date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
              <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', border: '1.5px solid #e2e8f0' }}>
                {entries.map((e, i) => (
                  <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: i < entries.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{CAT_ICON[e.category] || '📦'}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description}</div>
                      <div style={{ fontSize: 12, color: '#475569', fontWeight: 600, marginTop: 1 }}>
                        {e.time}{e.added_by ? ` · By ${e.added_by}` : ''}{e.qty !== 1 ? ` · ${e.qty} × ${currSym}${fmt(e.rate)}` : ''}
                      </div>
                      {e.note && <div style={{ fontSize: 11, color: '#4338ca', fontWeight: 600, marginTop: 2 }}>📝 {e.note}</div>}
                    </div>
                    <div style={{ fontWeight: 900, color: '#dc2626', fontSize: 16, flexShrink: 0 }}>{currSym}{fmt(e.amount)}</div>
                    
                    {/* If settled/paid: ONLY show ✓ Paid badge (no WA, no Email, no Delete) */}
                    {settledEntryIds.has(e.id) ? (
                      <span style={{
                        background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#16a34a',
                        borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 800, flexShrink: 0
                      }}>
                        ✓ Paid
                      </span>
                    ) : (
                      /* If unpaid: show WA, Email, and Delete */
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {perms.canLedgerSendWA && (
                          <button
                            onClick={() => handleSendEntryWA(e)}
                            disabled={sendingEntryAction === `wa_${e.id}`}
                            title="Send receipt on WhatsApp"
                            style={{
                              background: '#dcfce7', border: '1px solid #bbf7d0', color: '#15803d',
                              borderRadius: 8, padding: '5px 8px', fontSize: 12, fontWeight: 700,
                              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                            }}
                          >
                            {sendingEntryAction === `wa_${e.id}` ? '⏳' : '💬'} WA
                          </button>
                        )}

                        {Boolean(activeTab.email) && (
                          <button
                            onClick={() => handleSendEntryEmail(e)}
                            disabled={sendingEntryAction === `email_${e.id}`}
                            title="Send receipt via Email"
                            style={{
                              background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8',
                              borderRadius: 8, padding: '5px 8px', fontSize: 12, fontWeight: 700,
                              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                            }}
                          >
                            {sendingEntryAction === `email_${e.id}` ? '⏳' : '✉️'} Email
                          </button>
                        )}

                        {perms.canLedgerDeleteEntry && (
                          <button
                            onClick={() => handleDeleteEntry(e.id)}
                            title="Delete charge"
                            style={{
                              background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626',
                              borderRadius: 8, padding: '5px 9px', fontSize: 11, fontWeight: 700,
                              cursor: 'pointer', flexShrink: 0,
                            }}
                          >
                            🗑
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {(activeTab.payments?.length ?? 0) > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', marginBottom: 8 }}>Payments Received</div>
              <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', border: '1px solid #f0fdf4' }}>
                {activeTab.payments!.map((p, i) => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderBottom: i < activeTab.payments!.length - 1 ? '1px solid #f0fdf4' : 'none' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>💚</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d' }}>Payment received</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.date}{p.note ? ` · ${p.note}` : ''}{(p as any).method ? ` · ${(p as any).method.toUpperCase()}` : ''}</div>
                    </div>
                    <div style={{ fontWeight: 900, color: '#22c55e', fontSize: 15 }}>+{currSym}{fmt(p.amount)}</div>
                    {perms.canLedgerSendWA && (
                      <button
                        onClick={() => handleSendPaymentWA(p)}
                        title="Send Payment Receipt on WhatsApp"
                        style={{
                          background: '#dcfce7', border: '1px solid #bbf7d0', color: '#15803d',
                          borderRadius: 8, padding: '5px 9px', fontSize: 11, fontWeight: 700,
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
                        }}
                      >
                        💬 Receipt
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Edit Tab Modal */}
{editTab && (
  <div style={modalStyle} onClick={e => e.target === e.currentTarget && setEditTab(null)}>
    <div style={sheetStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>✏️ Edit Customer</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{editTab.customer_name}</div>
        </div>
        <button onClick={() => setEditTab(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Customer Name *</label>
        <input style={inputStyle} value={editTabName} onChange={e => setEditTabName(e.target.value)} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Phone *</label>
        <PhoneInput value={editTabPhone} onChange={setEditTabPhone} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Email</label>
        <input style={inputStyle} value={editTabEmail} onChange={e => setEditTabEmail(e.target.value)} />
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Address</label>
        <input style={inputStyle} value={editTabAddress} onChange={e => setEditTabAddress(e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => setEditTab(null)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
        <button onClick={handleSaveTabEdit} disabled={savingTabEdit || !editTabName || !editTabPhone}
          style={{ flex: 1, background: !editTabName || !editTabPhone ? '#e2e8f0' : '#0f172a', color: !editTabName || !editTabPhone ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          {savingTabEdit ? 'Saving...' : 'Save Changes ✓'}
        </button>
      </div>
    </div>
  </div>
)}
{/* Tab Phone OTP Modal */}
      {showTabPhoneOtp && (
        <div style={modalStyle}>
          <div style={{ ...sheetStyle, maxWidth: 360, textAlign: 'center', overflowY: 'auto' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📱</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', marginBottom: 4 }}>Verify New Number</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>{pendingTabEditData?.name}</div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 20 }}>
        Sent via WhatsApp to <strong>{pendingTabEditData?.phone}</strong><br/>
        <span style={{ color: '#94a3b8' }}>Enter the code to confirm phone change. Didn't get it? Check WhatsApp is active on this number, then tap Resend below.</span>
      </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '20px 0' }}>
              {[0,1,2,3,4,5].map(i => (
                <input key={i} id={`tab-phone-otp-${i}`} type="text" inputMode="numeric" maxLength={1}
                  value={tabPhoneOtp[i] || ''}
                  onChange={e => {
                    const char = e.target.value.replace(/\D/g, '').slice(-1);
                    const arr = tabPhoneOtp.padEnd(6, ' ').split('');
                    arr[i] = char || ' ';
                    setTabPhoneOtp(arr.join('').trimEnd());
                    if (char) { const n = document.getElementById(`tab-phone-otp-${i+1}`); if (n) (n as HTMLInputElement).focus(); }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Backspace') {
                      const arr = tabPhoneOtp.padEnd(6, ' ').split('');
                      arr[i] = ' '; setTabPhoneOtp(arr.join('').trimEnd());
                      if (i > 0) { const p = document.getElementById(`tab-phone-otp-${i-1}`); if (p) (p as HTMLInputElement).focus(); }
                    }
                  }}
                  style={{ width: 44, height: 52, textAlign: 'center', fontSize: 22, fontWeight: 900,
                    border: `2px solid ${tabPhoneOtp[i] && tabPhoneOtp[i] !== ' ' ? '#6366f1' : '#94a3b8'}`,
                    borderRadius: 12, outline: 'none',
                    background: tabPhoneOtp[i] && tabPhoneOtp[i] !== ' ' ? '#f5f3ff' : '#fff',
                    color: '#1e1b4b', fontFamily: 'monospace', boxSizing: 'border-box' }} />
              ))}
            </div>
            <button onClick={async () => {
              if (!editTab || !pendingTabEditData) return;
              setVerifyingTabPhoneOtp(true);
              try {
                await api('/subscriptions/members/verify-precreate-otp', {
                  method: 'POST',
                  body: JSON.stringify({ phone: pendingTabEditData.phone, otp: tabPhoneOtp }),
                });
                setSavingTabEdit(true);
                await api(`/ledger/tabs/${editTab.id}?outlet_id=${outletId || ''}`, {
  method: 'PATCH',
  body: JSON.stringify({
    customer_name: pendingTabEditData.name,
    phone: pendingTabEditData.phone,
    email: pendingTabEditData.email || null,
    address: pendingTabEditData.address || null,
  }),
});
                toast$('Customer updated! ✅');
                setShowTabPhoneOtp(false);
                setEditTab(null);
                setPendingTabEditData(null);
                setTabPhoneOtp('');
                loadTabs(true);
                if (activeTab?.id === editTab.id) loadTabDetail(editTab.id);
              } catch (e: any) { toast$(e.message, 'error'); }
              finally { setVerifyingTabPhoneOtp(false); setSavingTabEdit(false); }
            }} disabled={verifyingTabPhoneOtp || savingTabEdit || tabPhoneOtp.replace(/\s/g,'').length !== 6}
              style={{ width: '100%', background: tabPhoneOtp.replace(/\s/g,'').length !== 6 ? '#e2e8f0' : '#0f172a', color: tabPhoneOtp.replace(/\s/g,'').length !== 6 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 800, cursor: 'pointer', marginBottom: 10 }}>
              {verifyingTabPhoneOtp || savingTabEdit ? 'Verifying...' : 'Verify & Save →'}
            </button>
            <button onClick={async () => {
              setSendingTabPhoneOtp(true);
              try {
                const res = await api<any>('/subscriptions/members/send-precreate-otp', {
                  method: 'POST', body: JSON.stringify({ phone: pendingTabEditData?.phone, name: pendingTabEditData?.name }),
                });
                await sendWhatsApp(pendingTabEditData?.phone, `🔐 *New OTP*\n\nHi ${pendingTabEditData?.name}! Your new verification code is: *${res.otp}*\n\nValid for 10 minutes.\n— ${companyName}`, outletId);
                setTabPhoneOtp('');
                toast$('New OTP sent 📲');
              } catch (e: any) { toast$(e.message, 'error'); }
              finally { setSendingTabPhoneOtp(false); }
            }} disabled={sendingTabPhoneOtp}
              style={{ background: 'none', border: 'none', fontSize: 12, color: '#6366f1', cursor: 'pointer', fontWeight: 700, marginBottom: 8 }}>
              {sendingTabPhoneOtp ? 'Sending...' : '🔄 Resend OTP'}
            </button>
            <button onClick={() => { setShowTabPhoneOtp(false); setPendingTabEditData(null); setTabPhoneOtp(''); }}
              style={{ background: 'none', border: 'none', fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>← Cancel</button>
          </div>
        </div>
      )}

        {/* Notes & Alert Modal */}
        {showNotesModal && (
          <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowNotesModal(false)}>
            <div style={sheetStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div><div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>📝 Notes & Alerts</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{activeTab.customer_name}</div></div>
                <button onClick={() => setShowNotesModal(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Private Notes</label>
                <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={4}
                  placeholder="Add private notes about this customer — payment behaviour, preferences, contact info..."
                  value={editNotes} onChange={e => setEditNotes(e.target.value)} />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Balance Alert Threshold ({currSym})</label>
                <input type="number" style={inputStyle}
                  placeholder="e.g. 5000 — get WhatsApp alert when balance exceeds this"
                  value={editAlertThreshold} onChange={e => setEditAlertThreshold(e.target.value)} />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>You'll get a WhatsApp alert when this customer's balance exceeds this amount</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowNotesModal(false)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                <button onClick={async () => {
                  setSavingNotes(true);
                  try {
                    await api(`/ledger/tabs/${activeTab.id}/notes?outlet_id=${outletId || ''}`, {
  method: 'PATCH',
  body: JSON.stringify({ notes: editNotes, balance_alert_threshold: parseFloat(editAlertThreshold) || 0 }),
});
                    toast$('Notes saved!');
                    setShowNotesModal(false);
                    loadTabDetail(activeTab.id);
                  } catch (e: any) { toast$(e.message, 'error'); }
                  finally { setSavingNotes(false); }
                }} disabled={savingNotes} style={{ flex: 1, background: '#0f172a', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  {savingNotes ? 'Saving...' : 'Save Notes ✓'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recurring Charges Modal */}
        {showRecurringModal && (
          <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowRecurringModal(false)}>
            <div style={sheetStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div><div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>🔁 Recurring Charges</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{activeTab?.customer_name}</div></div>
                <button onClick={() => setShowRecurringModal(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
              </div>
              {recurringList.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Active Recurring</div>
                  {recurringList.map(r => (
                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 14px', marginBottom: 6 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{r.description}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{currSym}{r.amount} · day {r.day_of_month} of month · {r.last_charged ? `last: ${r.last_charged}` : 'not charged yet'}</div>
                      </div>
                      <button onClick={async () => {
  await api(`/ledger/recurring/${r.id}`, { method: 'DELETE' });
  loadRecurring(activeTab!.id);
  toast$('Recurring removed', 'info');
}} style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>🗑 Delete</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Add Recurring Charge</div>
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Description *</label>
                <input style={inputStyle} placeholder="e.g. Monthly Room Rent, Weekly Meal Plan..." value={recDesc} onChange={e => setRecDesc(e.target.value)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Amount ({currSym}) *</label>
                  <input type="number" style={inputStyle} placeholder="0.00" value={recAmount} onChange={e => setRecAmount(e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Day of Month *</label>
                  <input type="number" style={inputStyle} placeholder="1-28" min="1" max="28" value={recDay} onChange={e => setRecDay(e.target.value)} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Category</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {CATEGORIES.map(c => (
                    <button key={c} onClick={() => setRecCategory(c)} style={{
                      border: recCategory === c ? '2px solid #6366f1' : '1.5px solid #e2e8f0',
                      background: recCategory === c ? '#f5f3ff' : '#f8fafc',
                      color: recCategory === c ? '#6366f1' : '#475569',
                      borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}>{CAT_ICON[c] || '📦'} {c}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowRecurringModal(false)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Close</button>
                <button onClick={async () => {
                  if (!recDesc || !recAmount) return;
                  setAddingRec(true);
                  try {
                    await api(`/ledger/tabs/${activeTab!.id}/recurring?outlet_id=${outletId || ''}`, {
  method: 'POST',
  body: JSON.stringify({ description: recDesc, amount: parseFloat(recAmount), category: recCategory, day_of_month: parseInt(recDay) || 1 }),
});
                    toast$('Recurring charge added!');
                    setRecDesc(''); setRecAmount(''); setRecDay('1'); setRecCategory('general');
                    loadRecurring(activeTab!.id);
                  } catch (e: any) { toast$(e.message, 'error'); }
                  finally { setAddingRec(false); }
                }} disabled={addingRec || !recDesc || !recAmount}
                  style={{ flex: 1, background: !recDesc || !recAmount ? '#e2e8f0' : '#7c3aed', color: !recDesc || !recAmount ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  {addingRec ? 'Adding...' : '+ Add Recurring'}
                </button>
              </div>
            </div>
          </div>
        )}
{/* Quick Message Modal */}
        {showQuickMsg && (
          <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowQuickMsg(false)}>
            <div style={sheetStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div><div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>Quick Message</div><div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Send to {activeTab.customer_name}</div></div>
                <button onClick={() => setShowQuickMsg(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>×</button>
              </div>

              {/* Templates */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Quick Templates</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    { icon: '🍽️', label: 'Food is ready', msg: `Hi ${activeTab.customer_name}! Your food is ready. Please come and collect. 🍽️` },
                    { icon: '📋', label: "Today's menu", msg: `Hi ${activeTab.customer_name}! Here is today's menu:\n\n` },
                    { icon: '📦', label: 'Order is ready', msg: `Hi ${activeTab.customer_name}! Your order is ready for pickup. 📦` },
                    { icon: '⏰', label: 'Payment reminder', msg: `Hi ${activeTab.customer_name}! This is a friendly reminder that your balance of ${currSym}${fmt(activeTab.balance)} is pending. Please clear when convenient. 🙏` },
                    { icon: '🎉', label: 'Special offer today', msg: `Hi ${activeTab.customer_name}! We have a special offer for you today. Visit us to know more! 🎉` },
                    { icon: '🛏️', label: 'Room is ready', msg: `Hi ${activeTab.customer_name}! Your room is ready. Welcome! 🛏️` },
                    { icon: '🚗', label: 'Delivery on the way', msg: `Hi ${activeTab.customer_name}! Your delivery is on the way. Expected to arrive shortly. 🚗` },
                    { icon: '✅', label: 'Service completed', msg: `Hi ${activeTab.customer_name}! Your service has been completed. Thank you for choosing us! ✅` },
                  ].map(t => (
                    <button key={t.label} onClick={() => setCustomMsg(t.msg)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, background: customMsg === t.msg ? '#f5f3ff' : '#f8fafc', border: customMsg === t.msg ? '1.5px solid #a5b4fc' : '1.5px solid #e2e8f0', borderRadius: 12, padding: '10px 14px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
                      <span style={{ fontSize: 20, flexShrink: 0 }}>{t.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom editor */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Message</div>
                <textarea value={customMsg} onChange={e => setCustomMsg(e.target.value)}
                  placeholder="Type a custom message or pick a template above..."
                  rows={5}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '12px 14px', fontSize: 13, outline: 'none', background: '#f8fafc', resize: 'vertical', fontFamily: 'inherit', color: '#1e293b', boxSizing: 'border-box' }} />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setShowQuickMsg(false); setCustomMsg(''); }}
                  style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                <button onClick={async () => {
                  if (!customMsg.trim()) return;
                  try {
                    await sendWhatsApp(activeTab.phone, customMsg, outletId);
                    toast$('Message sent! 💬');
                    setShowQuickMsg(false); setCustomMsg('');
                  } catch (e: any) {
                    alert(`Message failed to send: ${e.message}`);
                  }
                }}
                  disabled={!customMsg.trim()}
                  style={{ flex: 1, background: !customMsg.trim() ? '#e2e8f0' : 'linear-gradient(135deg, #25D366, #128C7E)', color: !customMsg.trim() ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  📲 Send via WhatsApp
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Add Entry Modal */}
        {showEntryForm && (
          <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowEntryForm(false)}>
            <div style={sheetStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div><div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>Add Charge</div><div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>to {activeTab.customer_name}</div></div>
                <button onClick={() => setShowEntryForm(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>×</button>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Description *</label>
                <input style={inputStyle} placeholder="e.g. Lunch, Room 101, Service..." value={entryDesc} onChange={e => setEntryDesc(e.target.value)} autoFocus />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={labelStyle}>Quantity</label>
                  <input type="number" style={inputStyle} value={entryQty} onChange={e => setEntryQty(e.target.value)} min="0.1" step="0.1" />
                </div>
                <div>
                  <label style={labelStyle}>Rate ({currSym}) *</label>
                  <input type="number" style={inputStyle} placeholder="0.00" value={entryRate} onChange={e => setEntryRate(e.target.value)} />
                </div>
              </div>
              {entryRate && entryQty && (
                <div style={{ background: 'linear-gradient(135deg, #fef9ec, #fef3c7)', border: '1px solid #fcd34d', borderRadius: 12, padding: '10px 14px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>Total Amount</span>
                  <span style={{ fontSize: 18, fontWeight: 900, color: '#b45309' }}>{currSym}{fmt(parseFloat(entryRate || '0') * parseFloat(entryQty || '1'))}</span>
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Category</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {CATEGORIES.map(c => (
                    <button key={c} onClick={() => setEntryCategory(c)} style={{
                      border: entryCategory === c ? '2px solid #6366f1' : '1.5px solid #e2e8f0',
                      background: entryCategory === c ? '#f5f3ff' : '#f8fafc',
                      color: entryCategory === c ? '#6366f1' : '#475569',
                      borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}>{CAT_ICON[c]} {c}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={labelStyle}>Note (optional)</label>
                  <input style={inputStyle} placeholder="Any note..." value={entryNote} onChange={e => setEntryNote(e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Added by</label>
                  <input style={inputStyle} placeholder="Staff name..." value={entryAddedBy} onChange={e => setEntryAddedBy(e.target.value)} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={() => setShowEntryForm(false)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleAddEntry} disabled={addingEntry || !entryDesc || !entryRate} style={{ flex: 1, background: addingEntry || !entryDesc || !entryRate ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: addingEntry || !entryDesc || !entryRate ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  {addingEntry ? 'Adding...' : '+ Add & Notify'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentForm && (
          <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowPaymentForm(false)}>
            <div style={sheetStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div><div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>Record Payment</div><div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Balance: {currSym}{fmt(activeTab.balance)}</div></div>
                <button onClick={() => setShowPaymentForm(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>×</button>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Amount ({currSym}) *</label>
                <input type="number" style={{ ...inputStyle, fontSize: 22, fontWeight: 900, color: '#15803d' }} placeholder="0.00" value={payAmount} onChange={e => setPayAmount(e.target.value)} autoFocus />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Payment Method</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[
                    { key: 'cash', label: '💵 Cash' },
                    { key: 'upi', label: '📱 UPI' },
                    { key: 'card', label: '💳 Card' },
                    { key: 'bank', label: '🏦 Bank' },
                    { key: 'cheque', label: '📄 Cheque' },
                    { key: 'other', label: '🔁 Other' },
                  ].map(m => (
                    <button key={m.key} onClick={() => setPayMethod(m.key)}
                      style={{
                        padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        background: payMethod === m.key ? '#0f172a' : '#f1f5f9',
                        color: payMethod === m.key ? '#fff' : '#475569',
                        border: payMethod === m.key ? '1px solid #0f172a' : '1px solid #e2e8f0',
                      }}>{m.label}</button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Note (optional)</label>
                <input style={inputStyle} placeholder="Any note..." value={payNote} onChange={e => setPayNote(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowPaymentForm(false)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleAddPayment} disabled={addingPayment || !payAmount} style={{ flex: 1, background: addingPayment || !payAmount ? '#e2e8f0' : 'linear-gradient(135deg, #16a34a, #22c55e)', color: addingPayment || !payAmount ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  {addingPayment ? 'Saving...' : '💰 Record & Notify'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Exact POS-Style Payment Modal (Screenshot Parity) */}
        {showPayQrModal && (
          <div style={modalStyle} onClick={e => e.target === e.currentTarget && closePayQr()}>
            <style>{`@keyframes ledgerSpin { to { transform: rotate(360deg); } }`}</style>
            <div style={{
              background: '#fff', borderRadius: 24, padding: '28px 24px',
              width: '100%', maxWidth: 440, textAlign: 'center',
              boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
            }}>
              {/* Big Vibrant Purple Top Amount */}
              <div style={{ fontSize: 44, fontWeight: 900, color: '#7c3aed', letterSpacing: -1, lineHeight: 1 }}>
                {currSym}{fmt(payQrAmount)}
              </div>
              <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginTop: 6, marginBottom: 14 }}>
                {payQrTitle}{payQrSubtitle ? ` — ${payQrSubtitle}` : ' — Ask customer to scan'}
              </div>

              {/* QR Card */}
              <div style={{
                padding: 16, background: '#fff', borderRadius: 20,
                border: '2px solid #e2e8f0', margin: '0 auto 12px',
                minHeight: 230, minWidth: 230, maxWidth: 230,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
                boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
              }}>
                {payReceived ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 56 }}>✅</div>
                    <div style={{ fontSize: 16, fontWeight: 900, color: '#16a34a' }}>Payment Received</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>Balance updated</div>
                  </div>
                ) : loadingPayQr ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 28, height: 28, border: '3px solid #c7d2fe', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'ledgerSpin 0.8s linear infinite' }} />
                    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Generating QR Code...</div>
                  </div>
                ) : payQrDataUrl ? (
                  <img src={payQrDataUrl} alt="Payment QR" style={{ width: 200, height: 200, borderRadius: 12 }} />
                ) : (
                  <div style={{ fontSize: 12, color: '#ef4444' }}>Failed to generate QR.</div>
                )}
              </div>

              {/* Instruction / Polling Pill */}
              {!payReceived && (
                <div style={{
                  background: '#f1f5f9', borderRadius: 99, padding: '7px 14px',
                  fontSize: 11, color: '#475569', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16,
                }}>
                  {loadingPayQr ? (
                    <span>Generating payment session...</span>
                  ) : payQrTabId ? (
                    <>
                      <div style={{ width: 10, height: 10, border: '2px solid #a78bfa', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'ledgerSpin 0.8s linear infinite' }} />
                      <span>Customer scans → auto-detects payment</span>
                    </>
                  ) : (
                    <span>Customer scans → pays via UPI, Card or Netbanking</span>
                  )}
                </div>
              )}

              {/* Contact Details (Read-Only Display) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', background: '#f8fafc',
                  border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '10px 14px', background: '#f1f5f9', borderRight: '1.5px solid #e2e8f0',
                    fontSize: 12, fontWeight: 800, color: '#475569', display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    📞
                  </div>
                  <input
                    readOnly
                    value={payQrPhone || 'No phone number'}
                    style={{
                      flex: 1, border: 'none', outline: 'none', background: 'transparent',
                      padding: '10px 14px', fontSize: 13, fontWeight: 700, color: '#1e293b',
                      cursor: 'default', userSelect: 'all',
                    }}
                  />
                </div>

                {payQrEmail ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', background: '#f8fafc',
                    border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden',
                  }}>
                    <div style={{
                      padding: '10px 14px', background: '#f1f5f9', borderRight: '1.5px solid #e2e8f0',
                      fontSize: 12, fontWeight: 800, color: '#475569', display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      ✉️
                    </div>
                    <input
                      readOnly
                      value={payQrEmail}
                      style={{
                        flex: 1, border: 'none', outline: 'none', background: 'transparent',
                        padding: '10px 14px', fontSize: 13, fontWeight: 700, color: '#1e293b',
                        cursor: 'default', userSelect: 'all',
                      }}
                    />
                  </div>
                ) : null}
              </div>

              {/* Row 1: Primary Dispatch Buttons (Green WhatsApp + Blue Email side-by-side) */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={handleSharePayLinkWA}
                  disabled={!payQrDataUrl || !payQrPhone}
                  style={{
                    flex: 1, background: '#10b981', color: '#fff', border: 'none',
                    borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 800,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    opacity: !payQrDataUrl || !payQrPhone ? 0.6 : 1,
                    boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
                  }}
                >
                  💬 Send on WhatsApp
                </button>

                <button
                  type="button"
                  onClick={handleSharePayLinkEmail}
                  disabled={!payShareUrl || !payQrEmail || !payQrEmail.includes('@')}
                  style={{
                    flex: 1, background: '#4f46e5', color: '#fff', border: 'none',
                    borderRadius: 12, padding: '12px', fontSize: 13, fontWeight: 800,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    opacity: !payShareUrl || !payQrEmail || !payQrEmail.includes('@') ? 0.6 : 1,
                    boxShadow: '0 2px 8px rgba(79,70,229,0.3)',
                  }}
                >
                  ✉️ Email
                </button>
              </div>

              {/* Row 2: Secondary Tool Actions (Counter Display + Save QR + Confirm) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={openCounterDisplay}
                  title="Mirror onto customer-facing screen"
                  style={{
                    background: '#0f172a', color: '#fff', border: 'none',
                    borderRadius: 10, padding: '10px 4px', fontSize: 11, fontWeight: 700,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  }}
                >
                  🖥️ Display
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (!payQrDataUrl) return;
                    const a = document.createElement('a');
                    a.href = payQrDataUrl;
                    a.download = `${payQrTitle.replace(/\s+/g, '_')}_pay_qr.png`;
                    a.click();
                    toast$('Payment QR downloaded! ⬇');
                  }}
                  disabled={!payQrDataUrl}
                  style={{
                    background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe',
                    borderRadius: 10, padding: '10px 4px', fontSize: 11, fontWeight: 700,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  }}
                >
                  ⬇ Save QR
                </button>

                <button
                  type="button"
                  onClick={handleConfirmPaid}
                  disabled={confirmingPaid}
                  style={{
                    background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a',
                    borderRadius: 10, padding: '10px 4px', fontSize: 11, fontWeight: 800,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    opacity: confirmingPaid ? 0.6 : 1,
                  }}
                >
                  {confirmingPaid ? '...' : '✅ Confirm'}
                </button>
              </div>

              {/* Row 3: Bottom Status Actions (Done + Discard) */}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button
                  type="button"
                  onClick={closePayQr}
                  style={{
                    flex: 1, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
                    borderRadius: 12, padding: '11px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                  }}
                >
                  ✓ Done
                </button>

                <button
                  type="button"
                  onClick={closePayQr}
                  style={{
                    flex: 1, background: '#fee2e2', color: '#dc2626', border: 'none',
                    borderRadius: 12, padding: '11px', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                  }}
                >
                  🗑 Discard
                </button>
              </div>

            </div>
          </div>
        )}

        {/* QR Modal */}
        {showQR && qrData && (
          <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowQR(false)}>
            <div style={{ ...sheetStyle, maxWidth: 340, textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', marginBottom: 4 }}>Customer QR Code</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 20 }}>Scan to open {activeTab.customer_name}'s ledger</div>
              <img src={qrData} alt="QR" style={{ width: 220, height: 220, borderRadius: 16, border: '2px solid #e2e8f0' }} />
              <div style={{ marginTop: 16, fontSize: 12, color: '#6b7280', background: '#f8fafc', borderRadius: 10, padding: '8px 12px' }}>
                {window.location.origin}/ledger?tab={activeTab.id}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button onClick={() => { const a = document.createElement('a'); a.href = qrData!; a.download = `${activeTab.customer_name}_qr.png`; a.click(); }}
                  style={{ flex: 1, background: '#f5f3ff', color: '#6366f1', border: '1px solid #ddd6fe', borderRadius: 12, padding: '10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>⬇ Download</button>
                <button onClick={async () => { await sendWhatsAppImage(activeTab.phone, qrData!, `📱 Hi ${activeTab.customer_name}! Scan this QR to view your ledger.\n${window.location.origin}/ledger?tab=${activeTab.id}\n— ${companyName}`, outletId); toast$('QR image sent! 📲'); setShowQR(false); }}
                  style={{ flex: 1, background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 12, padding: '10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>📲 Send WA</button>
              </div>
            </div>
          </div>
        )}
{renderNotConnectedModal()}
        {/* Toast */}
        {toast.msg && (
          <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', background: toast.type === 'error' ? '#ef4444' : toast.type === 'info' ? '#3b82f6' : '#0f172a', color: '#fff', borderRadius: 14, padding: '12px 24px', fontSize: 13, fontWeight: 700, zIndex: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', whiteSpace: 'nowrap' }}>{toast.msg}</div>
        )}
      </div>
    );
  }

  // ── LIST VIEW ──────────────────────────────────────────────────────────
  return (
    <div style={{ 
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', 
      background: '#f1f5f9', 
      height: '100vh', 
      overflowY: 'auto', 
      overscrollBehavior: 'contain', 
      paddingBottom: 80 
    }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 30, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 18, color: '#0f172a', letterSpacing: -0.5 }}>Credit Book </div>
          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginTop: 1 }}>Udhar Khata · Running Tabs</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {perms.canLedgerViewStatus && (
            <button onClick={() => setShowStatusDashLedger(true)}
              style={{ height: 36, background: '#fff1f2', border: '1.5px solid #fecdd3', color: '#be123c', borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>📊 Status</button>
          )}
          {perms.canLedgerManageCategories && (
            <button onClick={() => setShowCatManager(true)}
              style={{ height: 36, background: '#f5f3ff', border: '1.5px solid #ddd6fe', color: '#7c3aed', borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>🏷️ Categories</button>
          )}
          {perms.canLedgerBroadcast && (
            <button onClick={() => { setShowBroadcast(true); setBroadcastSelected(tabs.filter(t => t.status !== 'settled').map(t => t.id)); }}
              style={{ height: 36, background: '#fff7ed', border: '1.5px solid #fed7aa', color: '#c2410c', borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>📣 Broadcast</button>
          )}
          {perms.canLedgerExportExcel && (
            <button onClick={() => exportAllExcel(tabs)} title="Export all"
              style={{ height: 36, background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#15803d', borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>📊 Export All</button>
          )}
          {/* Counter Display Button */}
          <button onClick={openCounterDisplay} style={{
            height: 36, background: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#0f172a',
            borderRadius: 10, padding: '0 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>🖥️ Display</button>
          {perms.canLedgerCreateTab && (
            <button
              onClick={() => setShowNewForm(true)}
              style={{
                height: 36,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: '#fff', border: 'none', borderRadius: 10, padding: '0 18px',
                fontSize: 13, fontWeight: 800, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
                boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
              }}>
              + New Tab
            </button>
          )}
        </div>
      </div>


      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, padding: '16px 16px 0' }}>
        {[
          { label: 'Outstanding', value: `${currSym}${fmt(totalBalance)}`, color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe' },
          { label: 'Open Tabs', value: openCount, color: '#b45309', bg: '#fef3c7', border: '#fcd34d' },
          { label: 'Settled', value: tabs.filter(t => t.status === 'settled').length, color: '#15803d', bg: '#dcfce7', border: '#86efac' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: 14, padding: '16px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: s.color, letterSpacing: -0.5 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: s.color, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ padding: '12px 16px 0' }}>
        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: '#64748b' }}>🔍</span>
          <input style={{ ...inputStyle, height: 40, paddingLeft: 42, background: '#fff', border: '1.5px solid #cbd5e1', color: '#0f172a', fontSize: 13, fontWeight: 600 }}
            placeholder="Search by name or phone..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, overflowX: 'auto', paddingBottom: 2 }}>
          {['all', 'open', 'partially_paid', 'settled', 'overdue'].map(f => {
            const st = STATUS[f as keyof typeof STATUS];
            const active = statusFilter === f;
            return (
              <button key={f} onClick={() => setStatusFilter(f)} style={{
                height: 34,
                border: active ? `2px solid ${st?.border || '#4f46e5'}` : '1.5px solid #cbd5e1',
                background: active ? (st?.bg || '#eef2ff') : '#fff',
                color: active ? (st?.text || '#4338ca') : '#334155',
                borderRadius: 99, padding: '0 16px', fontSize: 12, fontWeight: 800,
                cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                boxShadow: active ? '0 2px 6px rgba(0,0,0,0.08)' : 'none',
              }}>
                {f === 'all' ? 'All' : st?.label || f}
              </button>
            );
          })}
        </div>

        {/* List */}
        {!filtered.length && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📒</div>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#475569' }}>No ledger tabs yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Create a new tab to start tracking</div>
          </div>
        )}
        {filtered.map(t => {
          const st = STATUS[t.status] || STATUS.open;
          return (
            <div key={t.id} onClick={() => loadTabDetail(t.id)}
              style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 16, padding: '16px 18px', marginBottom: 10, cursor: 'pointer', transition: 'all 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#c7d2fe'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg, #eef2ff, #f5f3ff)', border: '1.5px solid #c7d2fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 900, color: '#4338ca', flexShrink: 0 }}>
                      {t.customer_name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 900, fontSize: 15, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.customer_name}</div>
                      <div style={{ fontSize: 12, color: '#334155', fontWeight: 600, marginTop: 1, fontFamily: 'monospace' }}>{t.phone}{t.address ? ` · ${t.address}` : ''}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <span style={{ background: st.bg, color: st.text, border: `1.5px solid ${st.border}`, borderRadius: 20, padding: '2px 9px', fontSize: 10, fontWeight: 800 }}>
                      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: st.dot, marginRight: 5, verticalAlign: 'middle' }} />{st.label}
                    </span>
                    <span style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>Updated {new Date(t.updated_at.endsWith('Z') || t.updated_at.includes('+') ? t.updated_at : t.updated_at + 'Z').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right', paddingLeft: 12, flexShrink: 0 }}>
                  <div style={{ fontWeight: 900, fontSize: 20, color: t.balance > 0 ? '#dc2626' : '#15803d', letterSpacing: -0.5 }}>{currSym}{fmt(t.balance)}</div>
                  <div style={{ fontSize: 10, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>balance</div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 6, justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                    {perms.canLedgerEditTab && t.status !== 'settled' && t.balance > 0 && (
                      <button onClick={() => { setEditTab(t); setEditTabName(t.customer_name); setEditTabPhone(t.phone); setEditTabEmail(t.email || ''); setEditTabAddress(t.address || ''); }}
                        style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Edit</button>
                    )}
                    {perms.canLedgerDeleteTab && t.status !== 'settled' && t.balance > 0 && (
                      <button onClick={() => handleDeleteTab(t)}
                        style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>🗑</button>
                    )}
                    {(t.status === 'settled' || t.balance <= 0) && (
                      <span style={{ fontSize: 11, fontWeight: 800, color: '#16a34a', padding: '3px 8px', background: '#f0fdf4', borderRadius: 6, border: '1px solid #bbf7d0' }}>
                        ✓ Settled
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Ledger Status Dashboard */}
      {showStatusDashLedger && (() => {
        const overdue = tabs.filter(t => t.status === 'open' || t.status === 'partially_paid').filter(t => {
  const updated = new Date(t.updated_at.endsWith('Z') || t.updated_at.includes('+') ? t.updated_at : t.updated_at + 'Z');
  const days = Math.floor((Date.now() - updated.getTime()) / 86400000);
  return days > 30;
});
        const highBalance = tabs.filter(t => t.balance > 10000 && t.status !== 'settled');
        const settled = tabs.filter(t => t.status === 'settled');
        const partial = tabs.filter(t => t.status === 'partially_paid');
        const open = tabs.filter(t => t.status === 'open');
        return (
          <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowStatusDashLedger(false)}>
            <div style={{ ...sheetStyle, maxWidth: 500 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div><div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>📊 Ledger Status</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{tabs.length} total customers</div></div>
                <button onClick={() => setShowStatusDashLedger(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 20 }}>
                {[
                  { label: 'Open', count: open.length, bg: '#fefce8', border: '#fde68a', text: '#b45309' },
                  { label: 'Partial', count: partial.length, bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
                  { label: 'Settled', count: settled.length, bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
                  { label: 'Overdue 30d+', count: overdue.length, bg: '#fff1f2', border: '#fecdd3', text: '#be123c' },
                ].map(s => (
                  <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 12, padding: '12px 8px', textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 900, color: s.text }}>{s.count}</div>
                    <div style={{ fontSize: 9, color: s.text, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {overdue.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#be123c', textTransform: 'uppercase', letterSpacing: '0.06em' }}>🔴 Overdue 30d+ ({overdue.length})</div>
                    <button onClick={async () => {
                      setSendingBulkReminders(true); setBulkRemindersDone(0);
                      const failed: string[] = [];
                      for (const t of overdue) {
                        try {
                          await sendWhatsApp(t.phone, `⏰ *Payment Reminder*\n\nHi ${t.customer_name}! Your account balance of ${currSym}${fmt(t.balance)} has been pending for over 30 days.\n\nPlease clear your dues at your earliest convenience.\n\n— ${companyName} Ledger`, outletId);
                        } catch (e: any) {
                          failed.push(t.customer_name);
                        }
                        setBulkRemindersDone(d => d + 1);
                        await new Promise(r => setTimeout(r, 800));
                      }
                      setSendingBulkReminders(false);
                      if (failed.length) {
                        alert(`Reminders sent to ${overdue.length - failed.length}/${overdue.length}. Failed for: ${failed.join(', ')}`);
                      } else {
                        toast$(`Reminders sent to ${overdue.length} customers! 📲`);
                      }
                    }} disabled={sendingBulkReminders}
                      style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#be123c', borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: sendingBulkReminders ? 0.6 : 1 }}>
                      {sendingBulkReminders ? `Sending ${bulkRemindersDone}/${overdue.length}...` : '📲 Remind All'}
                    </button>
                  </div>
                  {overdue.map(t => (
                    <div key={t.id} onClick={() => { setShowStatusDashLedger(false); loadTabDetail(t.id); }} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 12, padding: '10px 14px', marginBottom: 6, cursor: 'pointer' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{t.customer_name}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{currSym}{fmt(t.balance)} · {t.phone}</div>
                      </div>
                      <button onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await sendWhatsApp(t.phone, `⏰ *Payment Reminder*\n\nHi ${t.customer_name}! Your balance of ${currSym}${fmt(t.balance)} is overdue. Please clear at your earliest.\n\n— ${companyName}`, outletId);
                          toast$(`Reminder sent to ${t.customer_name} 📲`);
                        } catch (e: any) {
                          alert(`Failed to send reminder to ${t.customer_name}: ${e.message}`);
                        }
                      }} style={{ background: '#fff', border: '1px solid #fecdd3', color: '#be123c', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>📲</button>
                    </div>
                  ))}
                </div>
              )}
              {highBalance.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.06em' }}> 🟡 High Balance {currSym}10k+ ({highBalance.length})</div>
                    <button onClick={async () => {
                      setSendingBulkReminders(true); setBulkRemindersDone(0);
                      const failed: string[] = [];
                      for (const t of highBalance) {
                        try {
                          await sendWhatsApp(t.phone, `💰 *High Balance Alert*\n\nHi ${t.customer_name}! Your account balance has reached ${currSym}${fmt(t.balance)}.\n\nPlease make a payment at your earliest convenience.\n\n— ${companyName} Ledger`, outletId);
                        } catch (e: any) {
                          failed.push(t.customer_name);
                        }
                        setBulkRemindersDone(d => d + 1);
                        await new Promise(r => setTimeout(r, 800));
                      }
                      setSendingBulkReminders(false);
                      if (failed.length) {
                        alert(`Reminders sent to ${highBalance.length - failed.length}/${highBalance.length}. Failed for: ${failed.join(', ')}`);
                      } else {
                        toast$(`Reminders sent to ${highBalance.length} customers! 📲`);
                      }
                    }} disabled={sendingBulkReminders}
                      style={{ background: '#fefce8', border: '1px solid #fde68a', color: '#b45309', borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: sendingBulkReminders ? 0.6 : 1 }}>
                      {sendingBulkReminders ? `Sending ${bulkRemindersDone}/${highBalance.length}...` : '📲 Remind All'}
                    </button>
                  </div>
                  {highBalance.map(t => (
                    <div key={t.id} onClick={() => { setShowStatusDashLedger(false); loadTabDetail(t.id); }} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fefce8', border: '1px solid #fde68a', borderRadius: 12, padding: '10px 14px', marginBottom: 6, cursor: 'pointer' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{t.customer_name}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{currSym}{fmt(t.balance)} · {t.phone}</div>
                      </div>
                      <button onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await sendWhatsApp(t.phone, `💰 *High Balance Alert*\n\nHi ${t.customer_name}! Your balance has reached ${currSym}${fmt(t.balance)}. Please make a payment.\n\n— ${companyName}`, outletId);
                          toast$(`Reminder sent to ${t.customer_name} 📲`);
                        } catch (e: any) {
                          alert(`Failed to send reminder to ${t.customer_name}: ${e.message}`);
                        }
                      }} style={{ background: '#fff', border: '1px solid #fde68a', color: '#b45309', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>📲</button>
                    </div>
                  ))}
                </div>
              )}
              {tabs.length === 0 && <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>No customers yet</div>}
            </div>
          </div>
        );
      })()}

      {/* Category Manager Modal */}
      {showCatManager && (
        <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowCatManager(false)}>
          <div style={sheetStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div><div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>🏷️ Charge Categories</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Customize your charge categories</div></div>
              <button onClick={() => setShowCatManager(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              {ledgerCategories.map(c => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 14px' }}>
                  <span style={{ fontSize: 20 }}>{c.icon}</span>
                  <span style={{ flex: 1, fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{c.name}</span>
                  <button onClick={async () => {
  await api(`/ledger/categories/${c.id}`, { method: 'DELETE' });
  loadCategories();
  toast$('Category removed', 'info');
}} style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>🗑 Delete</button>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Add Category</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input style={{ ...inputStyle, width: 60, textAlign: 'center', fontSize: 20 }} placeholder="📦" value={newCatIcon} onChange={e => setNewCatIcon(e.target.value)} maxLength={2} />
              <input style={{ ...inputStyle, flex: 1 }} placeholder="Category name..." value={newCatName} onChange={e => setNewCatName(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowCatManager(false)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Close</button>
              <button onClick={async () => {
                if (!newCatName.trim()) return;
                setAddingCat(true);
                try {
                  await api('/ledger/categories', { method: 'POST', body: JSON.stringify({ name: newCatName.trim(), icon: newCatIcon || '📦' }) });
                  setNewCatName(''); setNewCatIcon('📦');
                  loadCategories();
                  toast$('Category added!');
                } catch (e: any) { toast$(e.message, 'error'); }
                finally { setAddingCat(false); }
              }} disabled={addingCat || !newCatName.trim()}
                style={{ flex: 1, background: !newCatName.trim() ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: !newCatName.trim() ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {addingCat ? 'Adding...' : '+ Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}
{/* Broadcast Modal */}
      {showBroadcast && (
        <div style={modalStyle} onClick={e => e.target === e.currentTarget && setShowBroadcast(false)}>
          <div style={{ ...sheetStyle, maxWidth: 520 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>📣 Broadcast Message</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Send to multiple customers at once</div>
              </div>
              <button onClick={() => setShowBroadcast(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16, color: '#64748b' }}>×</button>
            </div>

            {/* Templates */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Quick Templates</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[
                  { icon: '🍽️', label: 'Food ready', msg: `Hi {name}! Your food is ready. Please come and collect. 🍽️` },
                  { icon: '📋', label: "Today's menu", msg: `Hi {name}! Here is today's special menu:\n\n🍛 \n🥗 \n🍜 \n\nCome visit us! 😊` },
                  { icon: '⏰', label: 'Payment reminder', msg: `Hi {name}! 🙏 Friendly reminder — your account balance is pending. Please clear when convenient.\n\n— ${companyName}` },
                  { icon: '🎉', label: 'Special offer', msg: `Hi {name}! 🎉 We have an exclusive offer just for you today! Visit us to know more.` },
                  { icon: '🏪', label: 'Shop update', msg: `Hi {name}! Just a quick update from our end. ` },
                  { icon: '🚪', label: 'We are open', msg: `Hi {name}! We are open today. Come visit us! 😊` },
                  { icon: '🎊', label: 'Festival wishes', msg: `Hi {name}! Wishing you and your family a very happy festive season! 🎊🙏` },
                  { icon: '📦', label: 'Order ready', msg: `Hi {name}! Your order is ready for pickup. 📦` },
                ].map(t => (
                  <button key={t.label} onClick={() => setBroadcastMsg(t.msg)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: broadcastMsg === t.msg ? '#fff7ed' : '#f8fafc', border: broadcastMsg === t.msg ? '1.5px solid #fed7aa' : '1.5px solid #e2e8f0', borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Message editor */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                Message <span style={{ fontWeight: 400, color: '#94a3b8', textTransform: 'none' }}>— use <code style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4 }}>{'{name}'}</code> for customer name</span>
              </div>
              <textarea value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)}
                placeholder="Type your message... Use {name} to personalize."
                rows={4}
                style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '12px 14px', fontSize: 13, outline: 'none', background: '#f8fafc', resize: 'vertical', fontFamily: 'inherit', color: '#1e293b', boxSizing: 'border-box' }} />
            </div>

            {/* Customer selector */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Recipients ({broadcastSelected.length} selected)
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setBroadcastSelected(tabs.filter(t => t.status !== 'settled').map(t => t.id))}
                    style={{ fontSize: 11, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Select open</button>
                  <button onClick={() => setBroadcastSelected(tabs.map(t => t.id))}
                    style={{ fontSize: 11, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>All</button>
                  <button onClick={() => setBroadcastSelected([])}
                    style={{ fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>None</button>
                </div>
              </div>
              <div style={{ maxHeight: 180, overflowY: 'auto', border: '1.5px solid #e2e8f0', borderRadius: 12, background: '#f8fafc' }}>
                {tabs.map(t => {
                  const st = STATUS[t.status] || STATUS.open;
                  const checked = broadcastSelected.includes(t.id);
                  return (
                    <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: checked ? '#f5f3ff' : 'transparent', transition: 'background 0.1s' }}>
                      <input type="checkbox" checked={checked}
                        onChange={() => setBroadcastSelected(prev => checked ? prev.filter(id => id !== t.id) : [...prev, t.id])}
                        style={{ width: 16, height: 16, accentColor: '#6366f1', flexShrink: 0 }} />
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, ${st.bg}, #fff)`, border: `1px solid ${st.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900, color: st.text, flexShrink: 0 }}>
                        {t.customer_name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{t.customer_name}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{t.phone}</div>
                      </div>
                      <span style={{ background: st.bg, color: st.text, border: `1px solid ${st.border}`, borderRadius: 10, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>{st.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Progress */}
            {broadcasting && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '10px 14px', marginBottom: 14, fontSize: 12, fontWeight: 700, color: '#15803d' }}>
                Sending... {broadcastDone} / {broadcastSelected.length}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowBroadcast(false)}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button
                disabled={broadcasting || !broadcastMsg.trim() || broadcastSelected.length === 0}
                onClick={async () => {
                  if (!broadcastMsg.trim() || broadcastSelected.length === 0) return;
                  setBroadcasting(true); setBroadcastDone(0);
                  const targets = tabs.filter(t => broadcastSelected.includes(t.id));
                  const failed: string[] = [];
                  for (const t of targets) {
                    const msg = broadcastMsg.replace(/\{name\}/g, t.customer_name);
                    try {
                      await sendWhatsApp(t.phone, msg, outletId);
                    } catch (e: any) {
                      failed.push(t.customer_name);
                    }
                    setBroadcastDone(d => d + 1);
                    await new Promise(r => setTimeout(r, 800)); // 800ms gap to avoid spam
                  }
                  setBroadcasting(false);
                  postLog('LEDGER_BROADCAST_SENT', `Broadcast sent to ${targets.length - failed.length}/${targets.length} customers`, outletId);
                  if (failed.length) {
                    alert(`Sent to ${targets.length - failed.length}/${targets.length}. Failed for: ${failed.join(', ')}`);
                  } else {
                    toast$(`📣 Sent to ${targets.length} customers!`);
                  }
                  setShowBroadcast(false); setBroadcastMsg(''); setBroadcastSelected([]);
                }}
                style={{ flex: 1, background: broadcasting || !broadcastMsg.trim() || broadcastSelected.length === 0 ? '#e2e8f0' : 'linear-gradient(135deg, #f97316, #ef4444)', color: broadcasting || !broadcastMsg.trim() || broadcastSelected.length === 0 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  {broadcasting ? `Sending ${broadcastDone}/${broadcastSelected.length}...` : `📣 Send to ${broadcastSelected.length} customers`}
              </button>
            </div>
          </div>
        </div>
    
      )}
      {/* New Tab Modal */}
      {showNewForm && (
        <div style={modalStyle} onClick={e => e.target === e.currentTarget && (setShowNewForm(false), resetNewForm())}>
          <div style={sheetStyle}>
            {!pendingOTP ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <div><div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>New Customer Tab</div><div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Add a new customer account</div></div>
                  <button onClick={() => { setShowNewForm(false); resetNewForm(); }} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Customer Name *</label>
                  <input style={inputStyle} placeholder="Full name" value={newName} onChange={e => setNewName(e.target.value)} autoFocus />
                </div>
                <div style={{ marginBottom: 14 }}>
                 <label style={labelStyle}>Phone Number *</label>
{phoneExists && <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, marginBottom: 6 }}>⚠️ This phone number already has an active tab</div>}
<PhoneInput value={newPhone} onChange={setNewPhone} />
                </div>
                <div style={{ marginBottom: 14 }}>
                 <label style={labelStyle}>Email <span style={{ fontWeight: 400, textTransform: 'none', color: '#94a3b8' }}>(for OTP + email notifications)</span></label>
{emailExists && <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700, marginBottom: 6 }}>⚠️ This email already has an active tab</div>}
<input type="email" style={{ ...inputStyle, borderColor: emailExists ? '#dc2626' : undefined }} placeholder="customer@email.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Address <span style={{ fontWeight: 400, textTransform: 'none', color: '#94a3b8' }}>(optional)</span></label>
                  <input style={inputStyle} placeholder="Room no., area..." value={newAddress} onChange={e => setNewAddress(e.target.value)} />
                </div>
                {newTabError && <div style={{ background: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3', borderRadius: 10, padding: '10px 14px', fontSize: 12, fontWeight: 600, marginBottom: 14 }}>{newTabError}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setShowNewForm(false); resetNewForm(); setNewTabError(''); }} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={handleCreateTab} disabled={creatingTab || !newName || !newPhone || phoneExists || emailExists} style={{ flex: 1, background: creatingTab || !newName || !newPhone ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: creatingTab || !newName || !newPhone ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    {creatingTab ? 'Creating...' : 'Create Tab →'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, margin: '0 auto 12px' }}>🔐</div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a' }}>Verify Customer</div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 6, lineHeight: 1.5 }}>
                    Sent via WhatsApp to <strong>{newPhone}</strong>
                    {newEmail && <> and to <strong>{newEmail}</strong></>}.
                    <br />Ask the customer for the 6-digit code.{newEmail && ' If checking email, remember to look in Spam/Promotions.'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '20px 0' }}>
                  {[0,1,2,3,4,5].map(i => (
                    <input key={i} id={`tab-otp-${i}`} type="text" inputMode="numeric" maxLength={1}
                      value={otpInput[i] || ''}
                      onChange={e => {
                        const char = e.target.value.replace(/\D/g, '').slice(-1);
                        const arr = otpInput.padEnd(6, ' ').split('');
                        arr[i] = char || ' ';
                        setOtpInput(arr.join('').trimEnd());
                        if (char) { const n = document.getElementById(`tab-otp-${i+1}`); if (n) (n as HTMLInputElement).focus(); }
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Backspace') {
                          const arr = otpInput.padEnd(6, ' ').split('');
                          arr[i] = ' '; setOtpInput(arr.join('').trimEnd());
                          if (i > 0) { const p = document.getElementById(`tab-otp-${i-1}`); if (p) (p as HTMLInputElement).focus(); }
                        }
                      }}
                      style={{ width: 44, height: 52, textAlign: 'center', fontSize: 22, fontWeight: 900, border: `2px solid ${otpInput[i] && otpInput[i] !== ' ' ? '#6366f1' : '#94a3b8'}`, borderRadius: 12, outline: 'none', background: otpInput[i] && otpInput[i] !== ' ' ? '#f5f3ff' : '#fff', color: '#1e1b4b', fontFamily: 'monospace', boxSizing: 'border-box' }} />
                  ))}
                </div>
                {error && <div style={{ background: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3', borderRadius: 10, padding: '10px 14px', fontSize: 12, fontWeight: 600, marginBottom: 14, textAlign: 'center' }}>{error}</div>}
                <button onClick={handleVerifyOTP} disabled={verifying || otpInput.replace(/\s/g,'').length !== 6} style={{ width: '100%', background: verifying || otpInput.length !== 6 ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: verifying || otpInput.length !== 6 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 800, cursor: 'pointer', marginBottom: 10 }}>
                  {verifying ? 'Verifying...' : 'Verify & Open Tab →'}
                </button>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button onClick={async () => {
                    setCreatingTab(true);
                    try {
                      const res = await api<any>('/subscriptions/members/send-precreate-otp', {
                        method: 'POST',
                        body: JSON.stringify({ phone: newPhone, name: newName }),
                      });
                      await sendWhatsApp(newPhone, `🔐 *New OTP*\n\nHi ${newName}! Your new verification code is: *${res.otp}*\n\nValid for 10 minutes.\n— ${companyName}`, outletId);
                      if (newEmail) {
                        await sendEmail(newEmail, 'Your Verification OTP', `<div style="font-family:sans-serif;max-width:400px;margin:auto;padding:32px;background:#f8f9fb;border-radius:16px"><h2 style="color:#1e1b4b">Verification OTP</h2><div style="font-size:36px;font-weight:900;letter-spacing:12px;color:#6366f1;background:#fff;padding:20px;border-radius:12px;text-align:center">${res.otp}</div><p style="color:#6b7280;margin-top:16px">Valid for 10 minutes. Do not share this with anyone.</p></div>`, outletId);
                      }
                      setOtpInput('');
                      toast$('New OTP sent 📲');
                    } catch (e: any) { setError(e.message); }
                    finally { setCreatingTab(false); }
                  }} disabled={creatingTab} style={{ background: 'none', border: 'none', fontSize: 12, color: '#6366f1', cursor: 'pointer', fontWeight: 700, marginRight: 16 }}>
                    {creatingTab ? 'Sending...' : '🔄 Resend OTP'}
                  </button>
                  <button onClick={() => { setPendingOTP(null); setError(''); }} style={{ background: 'none', border: 'none', fontSize: 12, color: '#64748b', cursor: 'pointer' }}>← Back</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
{/* Edit Tab Modal */}
      {editTab && (
        <div style={modalStyle} onClick={e => e.target === e.currentTarget && setEditTab(null)}>
          <div style={sheetStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>✏️ Edit Customer</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{editTab.customer_name}</div>
              </div>
              <button onClick={() => setEditTab(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, width: 32, height: 32, cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Customer Name *</label>
              <input style={inputStyle} value={editTabName} onChange={e => setEditTabName(e.target.value)} />
            </div>
            <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Phone *</label>
        <PhoneInput value={editTabPhone} onChange={setEditTabPhone} />
      </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} value={editTabEmail} onChange={e => setEditTabEmail(e.target.value)} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Address</label>
              <input style={inputStyle} value={editTabAddress} onChange={e => setEditTabAddress(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEditTab(null)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveTabEdit} disabled={savingTabEdit || !editTabName || !editTabPhone}
                style={{ flex: 1, background: !editTabName || !editTabPhone ? '#e2e8f0' : '#0f172a', color: !editTabName || !editTabPhone ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                {savingTabEdit ? 'Saving...' : 'Save Changes ✓'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Tab Phone OTP Modal */}
      {showTabPhoneOtp && (
        <div style={modalStyle}>
          <div style={{ ...sheetStyle, maxWidth: 360, textAlign: 'center', overflowY: 'auto' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>📱</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', marginBottom: 4 }}>Verify New Number</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>{pendingTabEditData?.name}</div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 20 }}>
        Sent via WhatsApp to <strong>{pendingTabEditData?.phone}</strong><br/>
        <span style={{ color: '#94a3b8' }}>Enter the code to confirm phone change. Didn't get it? Check WhatsApp is active on this number, then tap Resend below.</span>
      </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '20px 0' }}>
              {[0,1,2,3,4,5].map(i => (
                <input key={i} id={`tab-phone-otp-list-${i}`} type="text" inputMode="numeric" maxLength={1}
                  value={tabPhoneOtp[i] || ''}
                  onChange={e => {
                    const char = e.target.value.replace(/\D/g, '').slice(-1);
                    const arr = tabPhoneOtp.padEnd(6, ' ').split('');
                    arr[i] = char || ' ';
                    setTabPhoneOtp(arr.join('').trimEnd());
                    if (char) { const n = document.getElementById(`tab-phone-otp-list-${i+1}`); if (n) (n as HTMLInputElement).focus(); }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Backspace') {
                      const arr = tabPhoneOtp.padEnd(6, ' ').split('');
                      arr[i] = ' '; setTabPhoneOtp(arr.join('').trimEnd());
                      if (i > 0) { const p = document.getElementById(`tab-phone-otp-list-${i-1}`); if (p) (p as HTMLInputElement).focus(); }
                    }
                  }}
                  style={{ width: 44, height: 52, textAlign: 'center', fontSize: 22, fontWeight: 900,
                    border: `2px solid ${tabPhoneOtp[i] && tabPhoneOtp[i] !== ' ' ? '#6366f1' : '#94a3b8'}`,
                    borderRadius: 12, outline: 'none',
                    background: tabPhoneOtp[i] && tabPhoneOtp[i] !== ' ' ? '#f5f3ff' : '#fff',
                    color: '#1e1b4b', fontFamily: 'monospace', boxSizing: 'border-box' }} />
              ))}
            </div>
            <button onClick={async () => {
              if (!editTab || !pendingTabEditData) return;
              setVerifyingTabPhoneOtp(true);
              try {
                await api('/subscriptions/members/verify-precreate-otp', {
                  method: 'POST',
                  body: JSON.stringify({ phone: pendingTabEditData.phone, otp: tabPhoneOtp }),
                });
                setSavingTabEdit(true);
                await api(`/ledger/tabs/${editTab.id}?outlet_id=${outletId || ''}`, {
  method: 'PATCH',
  body: JSON.stringify({
    customer_name: pendingTabEditData.name,
    phone: pendingTabEditData.phone,
    email: pendingTabEditData.email || null,
    address: pendingTabEditData.address || null,
  }),
});
                toast$('Customer updated! ✅');
                setShowTabPhoneOtp(false);
                setEditTab(null);
                setPendingTabEditData(null);
                setTabPhoneOtp('');
                loadTabs(true);
              } catch (e: any) { toast$(e.message, 'error'); }
              finally { setVerifyingTabPhoneOtp(false); setSavingTabEdit(false); }
            }} disabled={verifyingTabPhoneOtp || savingTabEdit || tabPhoneOtp.replace(/\s/g,'').length !== 6}
              style={{ width: '100%', background: tabPhoneOtp.replace(/\s/g,'').length !== 6 ? '#e2e8f0' : '#0f172a', color: tabPhoneOtp.replace(/\s/g,'').length !== 6 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 12, padding: '13px', fontSize: 14, fontWeight: 800, cursor: 'pointer', marginBottom: 10 }}>
              {verifyingTabPhoneOtp || savingTabEdit ? 'Verifying...' : 'Verify & Save →'}
            </button>
            <button onClick={async () => {
              setSendingTabPhoneOtp(true);
              try {
                const res = await api<any>('/subscriptions/members/send-precreate-otp', {
                  method: 'POST', body: JSON.stringify({ phone: pendingTabEditData?.phone, name: pendingTabEditData?.name }),
                });
                await sendWhatsApp(pendingTabEditData?.phone, `🔐 *New OTP*\n\nHi ${pendingTabEditData?.name}! Your new verification code is: *${res.otp}*\n\nValid for 10 minutes.\n— ${companyName}`, outletId);
                setTabPhoneOtp('');
                toast$('New OTP sent 📲');
              } catch (e: any) { toast$(e.message, 'error'); }
              finally { setSendingTabPhoneOtp(false); }
            }} disabled={sendingTabPhoneOtp}
              style={{ background: 'none', border: 'none', fontSize: 12, color: '#6366f1', cursor: 'pointer', fontWeight: 700, marginBottom: 8 }}>
              {sendingTabPhoneOtp ? 'Sending...' : '🔄 Resend OTP'}
            </button>
            <button onClick={() => { setShowTabPhoneOtp(false); setPendingTabEditData(null); setTabPhoneOtp(''); }}
              style={{ background: 'none', border: 'none', fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>← Cancel</button>
          </div>
        </div>
      )}
      {renderNotConnectedModal()}
      {/* Toast */}
      {toast.msg && (
        <div style={{ position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', background: toast.type === 'error' ? '#ef4444' : toast.type === 'info' ? '#3b82f6' : '#0f172a', color: '#fff', borderRadius: 14, padding: '12px 24px', fontSize: 13, fontWeight: 700, zIndex: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', whiteSpace: 'nowrap' }}>{toast.msg}</div>
      )}
      {error && !showNewForm && (
        <div style={{ position: 'fixed', bottom: 28, right: 16, background: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3', borderRadius: 12, padding: '10px 16px', fontSize: 12, fontWeight: 700, zIndex: 200 }}>
          {error} <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 8, color: '#be123c' }}>×</button>
        </div>
      )}
      {listLoadError && (
        <div style={{ position: 'fixed', bottom: 28, left: 16, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 12, padding: '10px 16px', fontSize: 12, fontWeight: 700, zIndex: 200, maxWidth: 340 }}>
          ⚠️ {listLoadError} <button onClick={() => { dismissedListErrorRef.current = listLoadError; setListLoadError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 8, color: '#c2410c' }}>×</button>
        </div>
      )}
    </div>
  );
};

export default LedgerView;
